import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Users,
  Building,
  Settings,
  FileText,
  Shield,
  ShieldAlert,
  Activity,
  X,
  RefreshCw,
  Trash2,
  LogOut,
  LogIn,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  UserPlus,
  Loader2,
  ChevronRight,
  Mail,
  Pencil,
  Link2,
  Search,
  HardDrive,
  Landmark,
  Save,
  Hotel,
  Send,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  CalendarClock,
  UserCheck,
  Globe,
  Brain,
  Map,
  MessageCircle,
  Pill,
  DollarSign,
  CalendarDays,
  Download,
  Upload,
  PiggyBank,
  Building2,
  CreditCard,
  IdCard,
  MapPin,
  Heart,
  Camera,
  Headphones,
  ImageIcon,
  Wrench,
  Sparkles,
} from "lucide-react";
import ImmobiliAdminModule from "./ImmobiliAdminModule";
import FarmacieAdminModule from "./FarmacieAdminModule";
import CasadiriposoperteAdminModule from "./CasadiriposoperteAdminModule";
import ManutenzioneAdminModule from "./ManutenzioneAdminModule";
import NewsletterAdminModule from "./NewsletterAdminModule";
import RecruitingCandidatiAdminModule from "./RecruitingCandidatiAdminModule";
import LeadsAdminModule from "./LeadsAdminModule";
import PartnershipModule from "./cdrpt/PartnershipModule";
import RegistrazioniPortaleAdminModule from "./RegistrazioniPortaleAdminModule";
import PacchettiVetrinaAdminModule from "./PacchettiVetrinaAdminModule";
import StruttureAdminModule from "./StruttureAdminModule";
import AdesioniAdminModule from "./AdesioniAdminModule";
import ModulisticaAdminModule from "./ModulisticaAdminModule";
import RichiestePersonaleAdminModule from "./RichiestePersonaleAdminModule";
import AdminLimitedUsersModule from "./AdminLimitedUsersModule";
import LeadStruttureAdminModule from "./LeadStruttureAdminModule";
import RcoWebsiteRequestsAdminModule from "./RcoWebsiteRequestsAdminModule";
import CdrptWebsiteRequestsAdminModule from "./CdrptWebsiteRequestsAdminModule";
import DemoAdminModule from "./DemoAdminModule";
import DemoAccountAdminModule from "./DemoAccountAdminModule";
import PipelineAdminModule from "./PipelineAdminModule";
import AttivitaAdminModule from "./AttivitaAdminModule";
import CommercialeVisiteModule from "./CommercialeVisiteModule";
import WhatsAppAdminModule from "./WhatsAppAdminModule";
import WhatsAppOpsAdminModule from "./WhatsAppOpsAdminModule";
import CommercialKitModule from "./CommercialKitModule";
import EmailMarketingAdminModule from "./EmailMarketingAdminModule";
import MarketingSocialAdminModule from "./marketingSocial/MarketingSocialAdminModule";
import SocialMarketingBrandModule from "./socialMarketing/SocialMarketingBrandModule";
import SocialMarketingPostizModule from "./socialMarketing/SocialMarketingPostizModule";
import SocialMarketingMetaModule from "./socialMarketing/SocialMarketingMetaModule";
import SocialMarketingProcedureModule from "./socialMarketing/SocialMarketingProcedureModule";
import TrustpilotAdminModule from "./TrustpilotAdminModule";
import OnboardingInvitiAdminModule from "./OnboardingInvitiAdminModule";
import AffidyaFicAdminModule from "./AffidyaFicAdminModule";
import AdminPagamentoCard, { pagamentoListBadges } from "./AdminPagamentoCard";
import AssistantVoiceAdminTab, {
  AssistantVoiceConfigCard,
  voiceConfigFromApi,
  voiceConfigToPayload,
} from "./AssistantVoiceAdminTab";
import AiConfigCollapsibleCard from "./AiConfigCollapsibleCard";
import AdminLayout from "./AdminLayout";
import DashboardReportModule from "./DashboardReportModule";
import {
  DASHBOARD_MODULE_ID,
  buildAdminBreadcrumb,
  buildAdminSearch,
  findAdminModule,
  hasAdminModule,
  resolveAdminModule,
  resolveAdminTarget,
  visibleNavGroups,
} from "./adminNavConfig";
import { PushNotificationsAdminModule } from "../../components/PushNotifications";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { linkifyText } from "../../utils/linkifyText";

/** Auto-login da Super Admin → Utenti admin (nuova scheda con token in query). */
function consumeAdminImpersonateFromUrl() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search || "");
  const impToken = params.get("admin_impersonate_token");
  if (!impToken) {
    return sessionStorage.getItem("admin_impersonating") === "1";
  }

  const prevToken = localStorage.getItem("super_admin_token");
  const prevUser = localStorage.getItem("super_admin_user");
  if (prevToken && !sessionStorage.getItem("admin_impersonator_token")) {
    sessionStorage.setItem("admin_impersonator_token", prevToken);
    sessionStorage.setItem("admin_impersonator_user", prevUser || "{}");
  }

  localStorage.setItem("super_admin_token", impToken);
  localStorage.setItem(
    "super_admin_user",
    JSON.stringify({ scope: "crm_operatore" })
  );
  sessionStorage.setItem("admin_impersonating", "1");

  params.delete("admin_impersonate_token");
  const qs = params.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname || "/admin"}${qs ? `?${qs}` : ""}`
  );
  return true;
}

function endAdminImpersonation({ closeTab = false } = {}) {
  const prevToken = sessionStorage.getItem("admin_impersonator_token");
  const prevUser = sessionStorage.getItem("admin_impersonator_user");
  sessionStorage.removeItem("admin_impersonating");
  sessionStorage.removeItem("admin_impersonator_token");
  sessionStorage.removeItem("admin_impersonator_user");
  if (prevToken) {
    localStorage.setItem("super_admin_token", prevToken);
    localStorage.setItem("super_admin_user", prevUser || "{}");
    if (closeTab) {
      window.close();
    }
    window.location.href = "/admin";
    return;
  }
  localStorage.removeItem("super_admin_token");
  localStorage.removeItem("super_admin_user");
  if (closeTab) {
    window.close();
  }
  window.location.href = "/admin-login";
}

export default function AdminPanel() {
  // Deve essere il primo hook: scrive localStorage prima degli altri initializer.
  const [adminImpersonating] = useState(() => consumeAdminImpersonateFromUrl());

  // Modulo e vista attivi derivano dall'URL (?module=…&tab=…): refresh,
  // accesso diretto e avanti/indietro del browser restano coerenti.
  const [initialTarget] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("module");
      const landing = sessionStorage.getItem("admin_open_module");
      if (landing) {
        sessionStorage.removeItem("admin_open_module");
      }
      const requested = resolveAdminTarget((fromUrl || landing || "").trim(), params.get("tab"));
      const u = JSON.parse(localStorage.getItem("super_admin_user") || "{}");
      const mods = Array.isArray(u.modules) ? u.modules : [];
      // Deep link a un modulo non abilitato per l'operatore CRM: si apre la sua pagina iniziale.
      if (requested.module && (u.scope !== "crm_operatore" || hasAdminModule(mods, requested.module))) return requested;
      if (u.scope === "crm_operatore") {
        if (hasAdminModule(mods, DASHBOARD_MODULE_ID)) return { module: DASHBOARD_MODULE_ID, tab: null };
        if (mods.includes("leads-admin")) return { module: "leads-admin", tab: null };
        if (mods.includes("newsletters")) return { module: "newsletters", tab: null };
        return resolveAdminTarget(mods[0] || DASHBOARD_MODULE_ID);
      }
    } catch { /* ignore */ }
    return { module: "dashboard", tab: null };
  });
  const [activeModule, setActiveModule] = useState(initialTarget.module);
  const [activeTab, setActiveTab] = useState(initialTarget.tab);
  // "push" per le navigazioni dell'utente, "replace" per redirect automatici e popstate.
  const historyModeRef = useRef("replace");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const navigate = useNavigate();

  // Controlla stato 2FA
  const checkTwoFactorStatus = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const status = await response.json();
        setTwoFactorStatus(status);
      }
    } catch (error) {
      console.error('Errore controllo 2FA:', error);
    }
  };

  const [adminUser, setAdminUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("super_admin_user") || "{}");
    } catch {
      return {};
    }
  });
  const [menuCounts, setMenuCounts] = useState({});
  const isCrmOnly = adminUser.scope === "crm_operatore";
  const crmModules = useMemo(() => {
    const raw = Array.isArray(adminUser.modules)
      ? [...adminUser.modules]
      : ["commerciale-dashboard", "commerciale-lead-strutture", "commerciale-onboarding", "leads-admin", "strutture", "immobili"];
    if (raw.includes("commerciale-richieste-cdrpt") && !raw.includes("leads-admin")) {
      raw.push("leads-admin");
    }
    return raw;
  }, [adminUser.modules]);
  const adminPermissions = Array.isArray(adminUser.permissions)
    ? adminUser.permissions
    : null;

  // Verifica autorizzazione e aggiorna moduli/permessi dal backend
  useEffect(() => {
    const token = localStorage.getItem('super_admin_token');
    const user = JSON.parse(localStorage.getItem('super_admin_user') || '{}');
    
    if (!token || (user.scope !== 'super_admin' && user.scope !== 'crm_operatore')) {
      window.location.href = '/admin-login';
      return;
    }
    // Lo stato 2FA riguarda solo il super admin (per gli operatori CRM l'API risponde 403).
    if (user.scope === 'super_admin') checkTwoFactorStatus();
    if (user.scope === 'crm_operatore') {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((me) => {
          if (!me) return;
          const next = {
            email: me.email,
            scope: me.scope,
            modules: me.modules || null,
            permissions: me.permissions || null,
            profile: me.profile || null,
            name: me.name || '',
          };
          localStorage.setItem('super_admin_user', JSON.stringify(next));
          setAdminUser(next);
          const prevModules = Array.isArray(user.modules) ? user.modules : [];
          const allowed = Array.isArray(next.modules) ? next.modules : [];
          historyModeRef.current = "replace";
          setActiveModule((current) => {
            const resolved = resolveAdminModule(current);
            if (
              hasAdminModule(allowed, DASHBOARD_MODULE_ID) &&
              !hasAdminModule(prevModules, DASHBOARD_MODULE_ID)
            ) {
              return DASHBOARD_MODULE_ID;
            }
            if (hasAdminModule(allowed, resolved)) return resolved;
            if (hasAdminModule(allowed, DASHBOARD_MODULE_ID)) return DASHBOARD_MODULE_ID;
            return resolveAdminModule(allowed[0] || DASHBOARD_MODULE_ID);
          });
        })
        .catch(() => {});
    }
  }, []);

  // Sincronizza lo stato con l'URL (pushState per i click, replaceState per i redirect).
  const urlModuleRef = useRef(activeModule);
  useEffect(() => {
    // Parametri specifici del modulo (es. ?conversation= di WhatsApp) restano
    // solo finché si resta su quel modulo.
    const base = urlModuleRef.current === activeModule ? window.location.search : "";
    urlModuleRef.current = activeModule;
    const search = buildAdminSearch(base, activeModule, activeTab);
    const target = `${window.location.pathname || "/admin"}${search}${window.location.hash || ""}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;
    if (target !== current) {
      const state = { adminModule: activeModule, adminTab: activeTab };
      if (historyModeRef.current === "push") window.history.pushState(state, "", target);
      else window.history.replaceState(state, "", target);
    }
    historyModeRef.current = "replace";
  }, [activeModule, activeTab]);

  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const target = resolveAdminTarget(params.get("module") || "", params.get("tab"));
      historyModeRef.current = "replace";
      if (target.module) {
        setActiveModule(target.module);
        setActiveTab(target.tab);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const loadMenuCounts = useCallback(async () => {
    const token = localStorage.getItem("super_admin_token");
    if (!token) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/commerciale/stats/nuove-richieste`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const payload = await response.json();
      setMenuCounts(payload.counts && typeof payload.counts === "object" ? payload.counts : {});
    } catch {
      /* i badge restano invariati */
    }
  }, []);

  useEffect(() => {
    loadMenuCounts();
    const timer = setInterval(loadMenuCounts, 60000);
    const onChange = () => loadMenuCounts();
    window.addEventListener("affidya-commerciale-menu-counts", onChange);
    window.addEventListener("focus", onChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener("affidya-commerciale-menu-counts", onChange);
      window.removeEventListener("focus", onChange);
    };
  }, [loadMenuCounts, activeModule]);

  // Logout super admin
  const handleLogout = () => {
    if (adminImpersonating) {
      endAdminImpersonation();
      return;
    }
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin_user');
    toast.success('Logout effettuato con successo');
    navigate('/admin-login');
  };

  const fetchGoogleMapsUsage = useCallback(async (mese) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const q = mese ? `?mese=${encodeURIComponent(mese)}` : "";
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/google-maps/usage${q}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData((prev) => ({ ...prev, "google-maps/usage": result }));
    } catch (error) {
      console.error("Error fetching google-maps/usage:", error);
      toast.error(`Errore nel caricamento dei dati: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch dati dal backend
  const fetchData = async (endpoint) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setData(prev => ({ ...prev, [endpoint]: result }));
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      toast.error(`Errore nel caricamento dei dati: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Carica dati iniziali
  useEffect(() => {
    const token = localStorage.getItem('super_admin_token');
    const user = JSON.parse(localStorage.getItem('super_admin_user') || '{}');
    
    if (!token || (user.scope !== 'super_admin' && user.scope !== 'crm_operatore')) {
      window.location.href = '/admin-login';
      return;
    }

    if (user.scope === "crm_operatore" && !hasAdminModule(crmModules, activeModule)) {
      return;
    }

    switch (activeModule) {
      case "dashboard":
        fetchData("dashboard/stats");
        fetchData("dashboard/recent-activity");
        break;
      case "utenti":
        fetchData("users");
        break;
      case "strutture":
        fetchData("strutture");
        break;
      case "servizi":
        fetchData("servizi");
        break;
      case "risparmio-listino":
        fetchData("risparmio/listino");
        break;
      case "immobili":
        break;
      case "farmacie":
        break;
      case "casadiriposoperte":
        break;
      case "google-maps":
        fetchGoogleMapsUsage();
        break;
      case "richieste-posto":
        fetchData("richieste-posto-ospite");
        break;
      case "trova-ospiti":
        fetchData("trova-servizi/richieste?servizio=ospiti");
        fetchData("trova-servizi/suggerimenti?servizio=ospiti");
        break;
      case "trova-personale":
        fetchData("trova-servizi/richieste?servizio=personale");
        fetchData("trova-servizi/suggerimenti?servizio=personale");
        break;
      case "assistenza":
        fetchData("assistenza/config");
        fetchData("assistenza/richieste");
        break;
      case "config":
        fetchData("config");
        break;
      case "push-notifiche":
        fetchData("users");
        break;
      case "alloggiati-web":
        fetchData("alloggiati");
        break;
      case "sistema":
        fetchData("system-status");
        break;
      case "audit":
        fetchData("audit-logs/auth");
        fetchData("users");
        break;
      case "sicurezza":
        break;
      case "manutenzione":
      case "admin-utenti-limitati":
      case "newsletters":
      case "modulistica-admin":
      case "recruiting-candidati":
      case "richieste-personale-admin":
      case "leads-admin":
      case "adesioni-admin":
      case "commerciale-dashboard":
      case "commerciale-lead-strutture":
      case "commerciale-visite":
      case "commerciale-richieste-sito":
      case "commerciale-richieste-cdrpt":
      case "commerciale-candidature-cdrpt":
      case "commerciale-onboarding":
      case "commerciale-demo":
      case "commerciale-pipeline":
      case "commerciale-attivita":
      case "commerciale-whatsapp":
      case "commerciale-kit-cdrpt":
      case "commerciale-kit-affidya":
      case "commerciale-report":
      case "email-marketing":
      case "marketing-social":
      case "social-marketing-procedura":
      case "social-marketing-affidya":
      case "social-marketing-casadiriposoperte":
      case "social-marketing-rco":
      case "social-marketing-postiz":
      case "social-marketing-meta":
      case "commerciale-trustpilot":
      case "affidya-fic":
      case "whatsapp-operativo":
        break;
    }
  }, [activeModule, fetchGoogleMapsUsage, crmModules]);

  // Render modulo corrente
  const renderModule = () => {
    switch (activeModule) {
      case "dashboard":
        return (
          <DashboardModule
            data={data}
            loading={loading}
            onNavigate={handleModuleClick}
            adminUser={adminUser}
          />
        );
      case "cdrpt-partnership":
        return <PartnershipModule />;
      case "cdrpt-registrazioni":
        return <RegistrazioniPortaleAdminModule />;
      case "cdrpt-vetrina":
        return <PacchettiVetrinaAdminModule />;
      case "utenti":
        return <UtentiModule data={data} loading={loading} onRefresh={() => fetchData("users")} />;
      case "strutture":
        return (
          <StruttureAdminModule
            canMerge={
              !isCrmOnly ||
              (adminPermissions
                ? adminPermissions.includes("strutture.merge")
                : true)
            }
            canDelete={!isCrmOnly}
          />
        );
      case "servizi":
        return <ServiziModule data={data} loading={loading} onRefresh={() => fetchData("servizi")} />;
      case "risparmio-listino":
        return (
          <RisparmioListinoModule
            data={data}
            loading={loading}
            onRefresh={() => fetchData("risparmio/listino")}
          />
        );
      case "immobili":
        return (
          <ImmobiliAdminModule
            allowedTabs={
              !isCrmOnly
                ? null
                : adminPermissions
                  ? [
                      adminPermissions.includes("immobili.catalog") && "immobili",
                      adminPermissions.includes("immobili.richieste") && "richieste",
                      adminPermissions.includes("immobili.config") && "impostazioni",
                      adminPermissions.includes("immobili.access") && "accesso",
                      adminPermissions.includes("immobili.viewers") && "portale",
                    ].filter(Boolean)
                  : ["immobili", "richieste", "impostazioni", "accesso", "portale"]
            }
          />
        );
      case "farmacie":
        return <FarmacieAdminModule />;
      case "casadiriposoperte":
        return <CasadiriposoperteAdminModule />;
      case "google-maps":
        return (
          <GoogleMapsUsageModule
            data={data}
            loading={loading}
            onRefresh={fetchGoogleMapsUsage}
          />
        );
      case "richieste-posto":
        return (
          <RichiestePostoOspiteModule
            data={data}
            loading={loading}
            onRefresh={() => fetchData("richieste-posto-ospite")}
          />
        );
      case "trova-ospiti":
        return (
          <RichiesteTrovaServizioModule
            servizio="ospiti"
            titolo="Trova Ospiti — richieste accesso"
            descrizione="Richieste di accesso anticipato al servizio Trova Ospiti (Servizi in anteprima)."
            accent="teal"
            data={data}
            loading={loading}
            onRefresh={() => {
              fetchData("trova-servizi/richieste?servizio=ospiti");
              fetchData("trova-servizi/suggerimenti?servizio=ospiti");
            }}
          />
        );
      case "trova-personale":
        return (
          <RichiesteTrovaServizioModule
            servizio="personale"
            titolo="Trova Personale — richieste accesso"
            descrizione="Richieste di accesso anticipato al servizio Trova Personale (Servizi in anteprima)."
            accent="violet"
            data={data}
            loading={loading}
            onRefresh={() => {
              fetchData("trova-servizi/richieste?servizio=personale");
              fetchData("trova-servizi/suggerimenti?servizio=personale");
            }}
          />
        );
      case "assistenza":
        return (
          <RichiesteAssistenzaModule
            data={data}
            loading={loading}
            onRefresh={() => {
              fetchData("assistenza/config");
              fetchData("assistenza/richieste");
            }}
          />
        );
      case "config":
        return <ConfigModule data={data} loading={loading} />;
      case "sistema":
        return (
          <ServerBackupModule
            data={data}
            loading={loading}
            onRefresh={() => fetchData("system-status")}
          />
        );
      case "audit":
        return <AuditModule data={data} loading={loading} />;
      case "sicurezza":
        return <SecurityMonitorModule />;
      case "bank-details":
        return <BankDetailsModule />;
      case "affidya-fic":
        return <AffidyaFicAdminModule />;
      case "ai-config":
        return <AIConfigModule />;
      case "whatsapp-operativo":
        return <WhatsAppOpsAdminModule />;
      case "push-notifiche":
        return (
          <PushNotificationsAdminModule
            users={data.users}
            loadingUsers={loading}
          />
        );
      case "alloggiati-web":
        return (
          <AlloggiatiWebModule
            data={data}
            loading={loading}
            onRefresh={() => fetchData("alloggiati")}
          />
        );
      case "manutenzione":
        return <ManutenzioneAdminModule />;
      case "admin-utenti-limitati":
        return <AdminLimitedUsersModule />;
      case "newsletters":
        return <NewsletterAdminModule />;
      case "modulistica-admin":
        return <ModulisticaAdminModule />;
      case "recruiting-candidati":
        return <RecruitingCandidatiAdminModule />;
      case "richieste-personale-admin":
        return <RichiestePersonaleAdminModule />;
      case "leads-admin":
        return (
          <LeadsAdminModule
            canManageGettoni={
              !isCrmOnly || !adminPermissions || adminPermissions.includes("rete.gettoni")
            }
            canManagePratiche={
              !isCrmOnly || !adminPermissions || adminPermissions.includes("rete.pratiche")
            }
          />
        );
      case "adesioni-admin":
        return <AdesioniAdminModule />;
      case "commerciale-dashboard":
      case "commerciale-report":
        return (
          <DashboardReportModule
            adminUser={adminUser}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onNavigate={handleModuleClick}
          />
        );
      case "commerciale-lead-strutture":
        return <LeadStruttureAdminModule />;
      case "commerciale-visite":
        return <CommercialeVisiteModule />;
      case "commerciale-richieste-sito":
        return <RcoWebsiteRequestsAdminModule />;
      case "commerciale-richieste-cdrpt":
        return <LeadsAdminModule />;
      case "commerciale-candidature-cdrpt":
        return <CdrptWebsiteRequestsAdminModule variant="candidature" />;
      case "commerciale-onboarding":
        return <OnboardingInvitiAdminModule />;
      case "commerciale-demo":
        return <DemoAdminModule />;
      case "demo-account":
        return <DemoAccountAdminModule />;
      case "commerciale-pipeline":
        return <PipelineAdminModule />;
      case "commerciale-attivita":
        return <AttivitaAdminModule />;
      case "commerciale-whatsapp":
        return <WhatsAppAdminModule />;
      case "commerciale-kit-cdrpt":
        return <CommercialKitModule servizio="CDRPT" tab={activeTab} onTabChange={handleTabChange} />;
      case "commerciale-kit-affidya":
      case "commerciale-playbook":
        return <CommercialKitModule servizio="AFFIDYA" tab={activeTab} onTabChange={handleTabChange} />;
      case "email-marketing":
        return <EmailMarketingAdminModule />;
      case "marketing-social":
        return <MarketingSocialAdminModule adminUser={adminUser} permissions={adminPermissions} />;
      case "social-marketing-procedura":
        return <SocialMarketingProcedureModule />;
      case "social-marketing-affidya":
        return <SocialMarketingBrandModule key="affidya" brand="affidya" />;
      case "social-marketing-casadiriposoperte":
        return <SocialMarketingBrandModule key="casadiriposoperte" brand="casadiriposoperte" />;
      case "social-marketing-rco":
        return <SocialMarketingBrandModule key="rco" brand="rco" />;
      case "social-marketing-postiz":
        return <SocialMarketingPostizModule />;
      case "social-marketing-meta":
        return <SocialMarketingMetaModule />;
      case "commerciale-trustpilot":
        return adminUser.scope === "super_admin" ? <TrustpilotAdminModule /> : <div>Modulo non trovato</div>;
      default:
        return <div>Modulo non trovato</div>;
    }
  };

  const handleModuleClick = (moduleId, tab = null) => {
    const target = resolveAdminTarget(moduleId, tab);
    historyModeRef.current = "push";
    setActiveModule(target.module);
    setActiveTab(target.tab);
  };

  const handleTabChange = (tab) => {
    historyModeRef.current = "push";
    setActiveTab(tab);
  };

  // Stessa regola di visibilità di prima (moduli dell'operatore, superAdminOnly);
  // per gli operatori l'elenco moduli effettivo è crmModules.
  const visibleNavSections = visibleNavGroups(
    { scope: adminUser.scope, modules: isCrmOnly ? crmModules : null },
    menuCounts
  );

  const activeModuleMeta = findAdminModule(activeModule);
  const breadcrumb = buildAdminBreadcrumb(
    activeModule,
    activeModule === DASHBOARD_MODULE_ID || String(activeModule).startsWith("commerciale-kit-") ? activeTab : null,
  ).map(
    (crumb) =>
      crumb.moduleId && activeModule === DASHBOARD_MODULE_ID && activeTab
        ? { ...crumb, onClick: () => handleModuleClick(crumb.moduleId) }
        : crumb
  );

  return (
    <AdminLayout
      navSections={visibleNavSections}
      activeModule={activeModule}
      onModuleClick={handleModuleClick}
      adminUser={adminUser}
      pushToken={typeof window !== "undefined" ? localStorage.getItem("super_admin_token") : ""}
      onLogout={handleLogout}
      impersonating={adminImpersonating}
      onEndImpersonation={endAdminImpersonation}
      twoFactorStatus={adminImpersonating ? null : twoFactorStatus}
      onConfigure2fa={() => {
        toast.info("Effettua il logout e riaccedi per configurare 2FA");
        handleLogout();
      }}
      pageTitle={activeModuleMeta?.label || "Admin"}
      pageIcon={activeModuleMeta?.icon}
      breadcrumb={breadcrumb}
    >
      {loading ? (
        <div className="flex h-64 items-center justify-center" role="status" aria-label="Caricamento">
          <RefreshCw className="h-8 w-8 animate-spin text-admin-accent" />
        </div>
      ) : adminUser?.scope === "crm_operatore" && !hasAdminModule(crmModules, activeModule) ? null : (
        renderModule()
      )}
    </AdminLayout>
  );
}

function fmtBytes(n) {
  if (n == null || typeof n !== "number" || Number.isNaN(n)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function backupStatusBadgeClass(status) {
  switch (status) {
    case "ok":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "stale":
      return "bg-amber-100 text-amber-900 border-amber-200";
    case "uncertain":
      return "bg-amber-50 text-amber-900 border-amber-200";
    case "missing":
    case "inaccessible":
      return "bg-red-100 text-red-800 border-red-200";
    case "not_configured":
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

/** Super Admin: metriche host del processo backend e stato backup MongoDB (MONGODB_BACKUP_DIR). */
function ServerBackupModule({ data, loading, onRefresh }) {
  const snap = data["system-status"];

  if (!snap && !loading) {
    return (
      <div className="space-y-4">
        <p className="text-slate-600">Impossibile caricare lo stato del server. Riprova.</p>
        <Button type="button" onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>
    );
  }

  if (!snap) return null;

  const disk = snap.disk || {};
  const mem = snap.memory || {};
  const load = snap.load_average || {};
  const backup = snap.backup || {};

  const captured = snap.captured_at
    ? new Date(snap.captured_at).toLocaleString("it-IT")
    : "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Server e backup database</h2>
          <p className="text-sm text-slate-500 mt-1">
            Rilevazione sul server che esegue il backend (non il tuo PC). Host:{" "}
            <span className="font-medium text-slate-700">{snap.host || "—"}</span>
            {snap.platform ? (
              <>
                {" "}
                · <span className="font-medium text-slate-700">{snap.platform}</span>
              </>
            ) : null}
          </p>
        </div>
        <Button type="button" onClick={onRefresh} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <p className="text-xs text-slate-400">Campione: {captured}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-5 w-5 text-cyan-600" />
              Disco
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {disk.error ? (
              <p className="text-red-600">{disk.error}</p>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2">
                  <span className="text-slate-500">Percorso</span>
                  <code className="text-xs text-slate-800 text-right break-all">{disk.path}</code>
                  <span className="text-slate-500">Libero</span>
                  <span className="font-medium text-slate-900 text-right">{fmtBytes(disk.free_bytes)}</span>
                  <span className="text-slate-500">Usato / Totale</span>
                  <span className="font-medium text-slate-900 text-right">
                    {fmtBytes(disk.used_bytes)} / {fmtBytes(disk.total_bytes)}
                  </span>
                  <span className="text-slate-500">Utilizzo</span>
                  <span className="font-medium text-slate-900 text-right">
                    {disk.percent_used != null ? `${disk.percent_used}%` : "—"}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-violet-600" />
              Memoria e carico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {mem.linux_detail_available ? (
              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2">
                <span className="text-slate-500">RAM disponibile</span>
                <span className="font-medium text-slate-900 text-right">{fmtBytes(mem.available_bytes)}</span>
                <span className="text-slate-500">RAM usata / Totale</span>
                <span className="font-medium text-slate-900 text-right">
                  {fmtBytes(mem.used_bytes)} / {fmtBytes(mem.total_bytes)}
                </span>
                <span className="text-slate-500">Utilizzo RAM</span>
                <span className="font-medium text-slate-900 text-right">
                  {mem.percent_used != null ? `${mem.percent_used}%` : "—"}
                </span>
              </div>
            ) : (
              <p className="text-slate-500">
                Dettaglio RAM non disponibile (tipico se il backend non gira su Linux con /proc/meminfo).
              </p>
            )}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Load average</p>
              {load.available ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 py-2 px-1">
                    <div className="text-lg font-semibold text-slate-900">{load["1m"]}</div>
                    <div className="text-[10px] text-slate-500">1 min</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 py-2 px-1">
                    <div className="text-lg font-semibold text-slate-900">{load["5m"]}</div>
                    <div className="text-[10px] text-slate-500">5 min</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 py-2 px-1">
                    <div className="text-lg font-semibold text-slate-900">{load["15m"]}</div>
                    <div className="text-[10px] text-slate-500">15 min</div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">Non disponibile su questo sistema.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5 text-slate-600" />
              Backup MongoDB
            </span>
            <Badge variant="outline" className={backupStatusBadgeClass(backup.status)}>
              {backup.status === "ok" && "OK"}
              {backup.status === "stale" && "Da verificare (vecchio)"}
              {backup.status === "uncertain" && "Incoerente"}
              {backup.status === "missing" && "Mancante"}
              {backup.status === "inaccessible" && "Cartella non accessibile"}
              {backup.status === "not_configured" && "Non configurato"}
              {!["ok", "stale", "uncertain", "missing", "inaccessible", "not_configured"].includes(backup.status) &&
                (backup.status || "—")}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-slate-700">{backup.message}</p>
          {backup.directory && (
            <p className="text-xs text-slate-500 break-all">
              Cartella: <code>{backup.directory}</code>
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-3">
            <div>
              <span className="text-slate-500">Ultimo archivio</span>
              <p className="font-mono text-xs text-slate-900 mt-0.5 break-all">{backup.last_archive || "—"}</p>
            </div>
            <div>
              <span className="text-slate-500">Dimensione</span>
              <p className="font-medium text-slate-900 mt-0.5">{fmtBytes(backup.last_size_bytes)}</p>
            </div>
            <div>
              <span className="text-slate-500">Età stimata</span>
              <p className="font-medium text-slate-900 mt-0.5">
                {backup.last_archive_age_hours != null
                  ? `${backup.last_archive_age_hours} h fa`
                  : "—"}
              </p>
            </div>
            <div>
              <span className="text-slate-500">Archivi .gz in cartella</span>
              <p className="font-medium text-slate-900 mt-0.5">{backup.archives_count ?? "—"}</p>
            </div>
          </div>
          {backup.explicit_status_ok && (
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
              L&apos;ultimo dump è stato segnato come completato dallo script <code className="text-[11px]">mongodb-backup.sh</code>.
            </p>
          )}
          <p className="text-xs text-slate-400 pt-2">
            Imposta <code className="text-[11px]">MONGODB_BACKUP_DIR</code> nel <code className="text-[11px]">backend/.env</code>{" "}
            (stesso path del cron). Opzionale: <code className="text-[11px]">MONGODB_BACKUP_MAX_AGE_HOURS</code> (default 36),{" "}
            <code className="text-[11px]">SYSTEM_HEALTH_DISK_PATH</code> per il disco da mostrare (default /).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function RichiesteTrovaServizioModule({
  servizio,
  titolo,
  descrizione,
  accent = "teal",
  data,
  loading,
  onRefresh,
}) {
  const dataKey = `trova-servizi/richieste?servizio=${servizio}`;
  const suggKey = `trova-servizi/suggerimenti?servizio=${servizio}`;
  const payload = data[dataKey] || {};
  const items = payload.richieste || [];
  const suggerimenti = (data[suggKey] || {}).suggerimenti || [];
  const [savingId, setSavingId] = useState(null);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [togglingUserId, setTogglingUserId] = useState(null);
  const spinnerClass = accent === "violet" ? "text-violet-600" : "text-teal-600";
  const accentBadge =
    accent === "violet"
      ? "bg-violet-50 text-violet-800 border-violet-200"
      : "bg-teal-50 text-teal-800 border-teal-200";

  const loadAccessUsers = useCallback(async () => {
    setAccessLoading(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/trova-servizi/${servizio}/access/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setAccessUsers(result.users || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento accessi");
    } finally {
      setAccessLoading(false);
    }
  }, [servizio]);

  useEffect(() => {
    loadAccessUsers();
  }, [loadAccessUsers]);

  const toggleAccess = async (userId, enabled) => {
    setTogglingUserId(userId);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/trova-servizi/${servizio}/access/users/${userId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ enabled }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      setAccessUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, enabled } : u))
      );
      toast.success(enabled ? "Accesso beta abilitato" : "Accesso beta disabilitato");
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setTogglingUserId(null);
    }
  };

  const updateStato = async (id, stato) => {
    setSavingId(id);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/trova-servizi/richieste/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stato }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      toast.success("Stato aggiornato");
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setSavingId(null);
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return String(iso);
    }
  };

  const enabledUsers = accessUsers.filter((u) => u.enabled);
  const q = accessSearch.trim().toLowerCase();
  const filteredAccess = accessUsers.filter((u) => {
    if (!q) return true;
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.facility_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{titolo}</h2>
          <p className="text-sm text-slate-500">{descrizione}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onRefresh();
            loadAccessUsers();
          }}
          disabled={loading || accessLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading || accessLoading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex flex-wrap items-center gap-2">
            <Users className="w-4 h-4" />
            Account che vedono questa sezione beta
            <Badge variant="outline" className={accentBadge}>
              {enabledUsers.length} abilitati / {accessUsers.length}
            </Badge>
          </CardTitle>
          <p className="text-sm text-slate-500 font-normal">
            Solo gli account abilitati vedono la voce nel menu «Servizi in anteprima» e possono aprire la pagina.
            Di default nessuno ha accesso: attiva lo switch per i singoli utenti.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {enabledUsers.length > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Account con accesso attivo
              </p>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {enabledUsers.map((u) => (
                  <li key={u.id} className="text-sm text-slate-800 flex flex-wrap gap-x-2">
                    <span className="font-medium">{u.name || u.email}</span>
                    <span className="text-slate-500">{u.email}</span>
                    {u.facility_name ? (
                      <span className="text-slate-400">· {u.facility_name}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Nessun account abilitato: la sezione non è visibile a nessun utente Affidya.
            </p>
          )}

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Cerca account per nome, email o struttura..."
              value={accessSearch}
              onChange={(e) => setAccessSearch(e.target.value)}
            />
          </div>

          {accessLoading && !accessUsers.length ? (
            <div className="flex justify-center py-10">
              <Loader2 className={`h-7 w-7 animate-spin ${spinnerClass}`} />
            </div>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {filteredAccess.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{u.name || u.email}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {u.email}
                      {u.facility_name ? ` · ${u.facility_name}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {togglingUserId === u.id ? (
                      <Loader2 className={`w-4 h-4 animate-spin ${spinnerClass}`} />
                    ) : null}
                    <Switch
                      checked={Boolean(u.enabled)}
                      disabled={togglingUserId === u.id}
                      onCheckedChange={(v) => toggleAccess(u.id, v)}
                    />
                  </div>
                </div>
              ))}
              {!filteredAccess.length ? (
                <p className="text-sm text-slate-500 text-center py-6">Nessun account trovato.</p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1">Richieste di accesso anticipato</h3>
        <p className="text-sm text-slate-500 mb-3">
          Form inviati dalle strutture dalla pagina beta (interesse al servizio).
        </p>
        <Card>
          <CardContent className="p-0">
            {loading && !items.length ? (
              <div className="flex justify-center py-16">
                <Loader2 className={`h-8 w-8 animate-spin ${spinnerClass}`} />
              </div>
            ) : items.length === 0 ? (
              <p className="text-center text-slate-500 py-12 text-sm">Nessuna richiesta registrata.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-600">
                      <th className="p-3 whitespace-nowrap">Data</th>
                      <th className="p-3 min-w-[140px]">Struttura</th>
                      <th className="p-3 min-w-[160px]">Località</th>
                      <th className="p-3">Richiesta attuale</th>
                      <th className="p-3 min-w-[120px]">Richiedente</th>
                      <th className="p-3 min-w-[130px]">Stato</th>
                      <th className="p-3 min-w-[120px]">Agg. stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 align-top hover:bg-slate-50/80">
                        <td className="p-3 text-slate-600 whitespace-nowrap">{formatWhen(r.created_at)}</td>
                        <td className="p-3">
                          <p className="font-medium text-slate-900">{r.struttura_nome || "—"}</p>
                          <p className="text-[11px] text-slate-400 font-mono break-all">{r.organizationId || ""}</p>
                          {r.email_inviata === false ? (
                            <Badge variant="outline" className="mt-1 text-[10px] text-amber-700 border-amber-200">
                              Email non inviata
                            </Badge>
                          ) : null}
                          {r.consenso_contatto ? (
                            <Badge variant="outline" className="mt-1 text-[10px] text-emerald-700 border-emerald-200">
                              Consenso OK
                            </Badge>
                          ) : null}
                        </td>
                        <td className="p-3 text-xs text-slate-600">
                          <p className="font-medium text-slate-800">{r.comune || "—"}</p>
                          <p>{r.provincia ? `Prov. ${r.provincia}` : ""}</p>
                        </td>
                        <td className="p-3">
                          <p className="text-xs text-slate-700 whitespace-pre-wrap max-w-[320px]">
                            {r.richiesta_attuale || "—"}
                          </p>
                          {r.suggerimento ? (
                            <p className="text-[11px] text-slate-500 mt-2 whitespace-pre-wrap max-w-[320px]">
                              Suggerimento: {r.suggerimento}
                            </p>
                          ) : null}
                        </td>
                        <td className="p-3 text-xs text-slate-600">
                          <p>{r.created_by_name || "—"}</p>
                          <p className="text-[11px] break-all">{r.created_by_email || r.created_by || ""}</p>
                        </td>
                        <td className="p-3">
                          <Select
                            value={r.stato || "in attesa"}
                            onValueChange={(v) => updateStato(r.id, v)}
                            disabled={savingId === r.id}
                          >
                            <SelectTrigger className="h-9 w-[150px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="in attesa">In attesa</SelectItem>
                              <SelectItem value="in lavorazione">In lavorazione</SelectItem>
                              <SelectItem value="completata">Completata</SelectItem>
                              <SelectItem value="rifiutata">Rifiutata</SelectItem>
                            </SelectContent>
                          </Select>
                          {savingId === r.id ? (
                            <Loader2 className={`w-4 h-4 animate-spin mt-2 ${spinnerClass}`} />
                          ) : null}
                        </td>
                        <td className="p-3 text-[11px] text-slate-400">
                          {r.stato_updated_at ? <span>{formatWhen(r.stato_updated_at)}</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1 flex flex-wrap items-center gap-2">
          Suggerimenti utenti
          <Badge variant="outline" className={accentBadge}>
            {suggerimenti.length}
          </Badge>
        </h3>
        <p className="text-sm text-slate-500 mb-3">
          Inviato da «Aiutaci a migliorare» nel pannello beta. Notifica email a{" "}
          <code className="text-xs bg-slate-100 px-1 rounded">assistenza@affidya.it</code>
          {" "}(o <code className="text-xs bg-slate-100 px-1 rounded">TROVA_ACCESSO_EMAIL_TO</code>).
        </p>
        <Card>
          <CardContent className="p-0">
            {loading && !suggerimenti.length ? (
              <div className="flex justify-center py-12">
                <Loader2 className={`h-7 w-7 animate-spin ${spinnerClass}`} />
              </div>
            ) : suggerimenti.length === 0 ? (
              <p className="text-center text-slate-500 py-10 text-sm">Nessun suggerimento registrato.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-600">
                      <th className="p-3 whitespace-nowrap">Data</th>
                      <th className="p-3 min-w-[140px]">Struttura</th>
                      <th className="p-3">Suggerimento</th>
                      <th className="p-3 min-w-[140px]">Autore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggerimenti.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 align-top hover:bg-slate-50/80">
                        <td className="p-3 text-slate-600 whitespace-nowrap">{formatWhen(s.created_at)}</td>
                        <td className="p-3">
                          <p className="font-medium text-slate-900">{s.struttura_nome || "—"}</p>
                          <p className="text-[11px] text-slate-400 font-mono break-all">{s.organizationId || ""}</p>
                          {s.email_inviata === false ? (
                            <Badge variant="outline" className="mt-1 text-[10px] text-amber-700 border-amber-200">
                              Email non inviata
                            </Badge>
                          ) : null}
                        </td>
                        <td className="p-3">
                          <p className="text-xs text-slate-700 whitespace-pre-wrap max-w-[420px]">
                            {s.suggerimento || "—"}
                          </p>
                        </td>
                        <td className="p-3 text-xs text-slate-600">
                          <p>{s.created_by_name || "—"}</p>
                          <p className="text-[11px] break-all">{s.created_by_email || ""}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RichiestePostoOspiteModule({ data, loading, onRefresh }) {
  const payload = data["richieste-posto-ospite"] || {};
  const items = payload.richieste || [];
  const [savingId, setSavingId] = useState(null);

  const updateStato = async (id, stato) => {
    setSavingId(id);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/richieste-posto-ospite/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stato }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      toast.success("Stato aggiornato");
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setSavingId(null);
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return String(iso);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Richieste posto ospite</h2>
          <p className="text-sm text-slate-500">
            Richieste inviate dalle strutture da Camere e posti letto (email a info@casadiriposoperte.it).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && !items.length ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 py-12 text-sm">Nessuna richiesta registrata.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-600">
                    <th className="p-3 whitespace-nowrap">Data richiesta</th>
                    <th className="p-3 min-w-[140px]">Struttura</th>
                    <th className="p-3">Dettaglio richiesta</th>
                    <th className="p-3 min-w-[120px]">Richiedente</th>
                    <th className="p-3 min-w-[130px]">Stato</th>
                    <th className="p-3 min-w-[120px]">Agg. stato</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top hover:bg-slate-50/80">
                      <td className="p-3 text-slate-600 whitespace-nowrap">{formatWhen(r.created_at)}</td>
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{r.struttura_nome || "—"}</p>
                        <p className="text-[11px] text-slate-400 font-mono break-all">{r.organizationId || ""}</p>
                        {r.hidden_by_user ? (
                          <Badge variant="outline" className="mt-1 text-[10px] border-red-200 text-red-700 bg-red-50">
                            Cancellata dall&apos;utente
                            {r.hidden_at ? ` · ${formatWhen(r.hidden_at)}` : ""}
                          </Badge>
                        ) : null}
                        {r.email_inviata === false ? (
                          <Badge variant="outline" className="mt-1 text-[10px] text-amber-700 border-amber-200">
                            Email non inviata (Resend?)
                          </Badge>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-slate-800">{r.tipologia}</p>
                        <p className="text-xs text-slate-500">
                          Ingresso previsto: {r.data_ingresso_prevista || "—"}
                          {r.sesso ? ` · Sesso: ${r.sesso}` : ""}
                          {r.camera ? ` · Camera: ${r.camera}` : ""}
                        </p>
                        {r.note ? (
                          <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap max-w-[280px]">{r.note}</p>
                        ) : null}
                        {r.lead_compatibile_id ? (
                          <p className="text-[11px] text-teal-700 mt-1">Lead ID: {r.lead_compatibile_id}</p>
                        ) : null}
                        {r.lead_dettaglio && Object.keys(r.lead_dettaglio).length > 0 ? (
                          <pre className="mt-2 text-[10px] bg-slate-100 rounded p-2 overflow-x-auto max-w-[320px] text-slate-700">
                            {JSON.stringify(r.lead_dettaglio, null, 2)}
                          </pre>
                        ) : null}
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        <p>{r.created_by_name || "—"}</p>
                        <p className="text-[11px] break-all">{r.created_by_email || r.created_by || ""}</p>
                      </td>
                      <td className="p-3">
                        <Select
                          value={r.stato || "in attesa"}
                          onValueChange={(v) => updateStato(r.id, v)}
                          disabled={savingId === r.id}
                        >
                          <SelectTrigger className="h-9 w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in attesa">In attesa</SelectItem>
                            <SelectItem value="in lavorazione">In lavorazione</SelectItem>
                            <SelectItem value="completata">Completata</SelectItem>
                          </SelectContent>
                        </Select>
                        {savingId === r.id ? <Loader2 className="w-4 h-4 animate-spin mt-2 text-teal-600" /> : null}
                      </td>
                      <td className="p-3 text-[11px] text-slate-400">
                        {r.stato_updated_at ? <span>Agg. stato: {formatWhen(r.stato_updated_at)}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const ASSISTENZA_CATEGORIE_LABEL = {
  tecnico: "Problema tecnico",
  fatturazione: "Fatturazione e pagamenti",
  funzionalita: "Richiesta nuova funzionalità",
  alloggiati: "Alloggiati Web",
  altro: "Altro",
};

function formatAssistenzaDuration(seconds) {
  if (seconds == null || seconds < 0) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}g ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return "< 1m";
}

function AssistenzaAuthImage({ path, alt = "Allegato", className = "" }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!path) return undefined;
    let objectUrl;
    let cancelled = false;
    const token = localStorage.getItem("super_admin_token");
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className}`}>
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

function RichiesteAssistenzaModule({ data, loading, onRefresh }) {
  const configPayload = data["assistenza/config"] || {};
  const richiestePayload = data["assistenza/richieste"] || {};
  const items = richiestePayload.richieste || [];
  const [savingId, setSavingId] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [emailTo, setEmailTo] = useState(configPayload.email_to || "");
  const [expandedId, setExpandedId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyFiles, setReplyFiles] = useState({});
  const [replyingId, setReplyingId] = useState(null);

  useEffect(() => {
    setEmailTo(configPayload.email_to || "");
  }, [configPayload.email_to]);

  const updateStato = async (id, stato) => {
    setSavingId(id);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assistenza/richieste/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stato }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      toast.success("Stato aggiornato");
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setSavingId(null);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assistenza/config`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email_to: emailTo }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      toast.success("Configurazione salvata");
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setSavingConfig(false);
    }
  };

  const sendRisposta = async (id) => {
    const messaggio = (replyDrafts[id] || "").trim();
    const files = replyFiles[id] || [];
    if (!messaggio && files.length === 0) {
      toast.error("Inserisci un messaggio o almeno una foto");
      return;
    }
    setReplyingId(id);
    try {
      const token = localStorage.getItem("super_admin_token");
      const form = new FormData();
      form.append("messaggio", messaggio);
      files.forEach((file) => form.append("files", file));
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assistenza/richieste/${id}/risposte`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || response.statusText);
      }
      toast.success("Risposta inviata");
      setReplyDrafts((prev) => ({ ...prev, [id]: "" }));
      setReplyFiles((prev) => ({ ...prev, [id]: [] }));
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Errore durante l'invio della risposta");
    } finally {
      setReplyingId(null);
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return String(iso);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Assistenza</h2>
          <p className="text-sm text-slate-500">
            Richieste inviate dal widget in sidebar, dalla pagina Assistenza o dall&apos;assistente virtuale.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configurazione notifiche</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="max-w-md">
            <Label htmlFor="assistenza-email-to">Email destinatario richieste</Label>
            <Input
              id="assistenza-email-to"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="assistenza@esempio.it"
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">
              Ogni nuova richiesta invia una notifica a questo indirizzo (via Resend).
            </p>
          </div>
          <Button size="sm" onClick={saveConfig} disabled={savingConfig}>
            {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva configurazione
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading && !items.length ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 py-12 text-sm">Nessuna richiesta registrata.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-600">
                    <th className="p-3 whitespace-nowrap">Data</th>
                    <th className="p-3 min-w-[140px]">Utente</th>
                    <th className="p-3 min-w-[120px]">Struttura</th>
                    <th className="p-3">Richiesta</th>
                    <th className="p-3 min-w-[130px]">Stato</th>
                    <th className="p-3 min-w-[160px]">Tempi</th>
                    <th className="p-3 min-w-[100px]">Risposte</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const isExpanded = expandedId === r.id;
                    const draftFiles = replyFiles[r.id] || [];
                    return (
                      <Fragment key={r.id}>
                        <tr className="border-b border-slate-100 align-top hover:bg-slate-50/80">
                          <td className="p-3 text-slate-600 whitespace-nowrap">{formatWhen(r.created_at)}</td>
                          <td className="p-3 text-xs text-slate-600">
                            <p className="font-medium text-slate-900">{r.user_name || "—"}</p>
                            <p className="text-[11px] break-all">{r.user_email || ""}</p>
                            {r.telefono ? <p className="text-[11px] mt-1">Tel: {r.telefono}</p> : null}
                          </td>
                          <td className="p-3 text-xs text-slate-600">
                            <p>{r.struttura_nome || "—"}</p>
                            {r.struttura_id ? (
                              <p className="text-[11px] text-slate-400 font-mono break-all">{r.struttura_id}</p>
                            ) : null}
                          </td>
                          <td className="p-3">
                            <p className="font-medium text-slate-800">
                              {ASSISTENZA_CATEGORIE_LABEL[r.categoria] || r.categoria}
                            </p>
                            {r.oggetto ? <p className="text-xs text-slate-600">{r.oggetto}</p> : null}
                            <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap max-w-[320px]">{r.messaggio}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {r.origine === "assistente_virtuale" ? (
                                <Badge variant="outline" className="text-[10px]">Assistente virtuale</Badge>
                              ) : null}
                              {r.email_inviata === false ? (
                                <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200">
                                  Email non inviata
                                </Badge>
                              ) : null}
                              {r.hidden_by_user ? (
                                <Badge variant="outline" className="text-[10px] border-red-200 text-red-700 bg-red-50">
                                  Eliminata
                                  {r.hidden_at ? ` · ${formatWhen(r.hidden_at)}` : ""}
                                </Badge>
                              ) : null}
                              {r.unread_for_user ? (
                                <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700 bg-sky-50">
                                  Non letto dall&apos;utente
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="p-3">
                            <Select
                              value={r.stato || "nuova"}
                              onValueChange={(v) => updateStato(r.id, v)}
                              disabled={savingId === r.id}
                            >
                              <SelectTrigger className="h-9 w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nuova">Nuova</SelectItem>
                                <SelectItem value="in lavorazione">In lavorazione</SelectItem>
                                <SelectItem value="lavorata">Lavorata</SelectItem>
                              </SelectContent>
                            </Select>
                            {savingId === r.id ? <Loader2 className="w-4 h-4 animate-spin mt-2 text-sky-600" /> : null}
                            {r.stato_updated_at ? (
                              <p className="text-[11px] text-slate-400 mt-2">
                                Agg. stato: {formatWhen(r.stato_updated_at)}
                              </p>
                            ) : null}
                          </td>
                          <td className="p-3 text-[11px] text-slate-600">
                            {r.in_lavorazione_at ? (
                              <p>
                                <span className="text-slate-400">In lavorazione:</span>{" "}
                                {formatWhen(r.in_lavorazione_at)}
                              </p>
                            ) : null}
                            {r.lavorata_at ? (
                              <p className={r.in_lavorazione_at ? "mt-1" : ""}>
                                <span className="text-slate-400">Lavorata:</span>{" "}
                                {formatWhen(r.lavorata_at)}
                              </p>
                            ) : null}
                            {r.durata_attesa_sec != null ? (
                              <p className="mt-2 text-sky-700">
                                Attesa: {formatAssistenzaDuration(r.durata_attesa_sec)}
                              </p>
                            ) : null}
                            {r.durata_lavorazione_sec != null ? (
                              <p className="mt-1 text-amber-700">
                                Lavorazione: {formatAssistenzaDuration(r.durata_lavorazione_sec)}
                              </p>
                            ) : null}
                            {r.durata_totale_sec != null ? (
                              <p className="mt-1 font-medium text-emerald-700">
                                Totale: {formatAssistenzaDuration(r.durata_totale_sec)}
                              </p>
                            ) : null}
                            {!r.in_lavorazione_at && !r.lavorata_at ? (
                              <span className="text-slate-400">—</span>
                            ) : null}
                          </td>
                          <td className="p-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            >
                              <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                              {(r.risposte_count || 0) > 0 ? r.risposte_count : "Rispondi"}
                              {isExpanded ? (
                                <ChevronUp className="h-3.5 w-3.5 ml-1.5" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
                              )}
                            </Button>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="border-b border-slate-100 bg-sky-50/40">
                            <td colSpan={7} className="p-4">
                              <div className="space-y-4 max-w-3xl">
                                {(r.risposte || []).length > 0 ? (
                                  <div className="space-y-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Conversazione
                                    </p>
                                    {(r.risposte || []).map((risposta) => (
                                      <div
                                        key={risposta.id}
                                        className="rounded-lg border border-sky-100 bg-white p-3"
                                      >
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                          <p className="text-xs font-medium text-sky-800">
                                            {risposta.admin_name || "Assistenza Affidya"}
                                          </p>
                                          <p className="text-[11px] text-slate-400">
                                            {formatWhen(risposta.created_at)}
                                          </p>
                                        </div>
                                        {risposta.messaggio ? (
                                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                            {linkifyText(
                                              risposta.messaggio,
                                              "text-sky-700 underline underline-offset-2 hover:text-sky-900 break-all"
                                            )}
                                          </p>
                                        ) : null}
                                        {(risposta.allegati || []).length > 0 ? (
                                          <div className="mt-2 flex flex-wrap gap-2">
                                            {risposta.allegati.map((a) => (
                                              <a
                                                key={a.id}
                                                href={`${process.env.REACT_APP_BACKEND_URL}${a.admin_url}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={async (e) => {
                                                  e.preventDefault();
                                                  const token = localStorage.getItem("super_admin_token");
                                                  const res = await fetch(
                                                    `${process.env.REACT_APP_BACKEND_URL}${a.admin_url}`,
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  if (!res.ok) {
                                                    toast.error("Impossibile aprire la foto");
                                                    return;
                                                  }
                                                  const blob = await res.blob();
                                                  const url = URL.createObjectURL(blob);
                                                  window.open(url, "_blank", "noopener,noreferrer");
                                                }}
                                                className="block"
                                              >
                                                <AssistenzaAuthImage
                                                  path={a.admin_url}
                                                  alt={a.nome || "Foto"}
                                                  className="h-20 w-20 rounded-md object-cover border border-slate-200"
                                                />
                                              </a>
                                            ))}
                                          </div>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-500">Nessuna risposta ancora.</p>
                                )}

                                <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Nuova risposta
                                  </p>
                                  <textarea
                                    value={replyDrafts[r.id] || ""}
                                    onChange={(e) =>
                                      setReplyDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
                                    }
                                    placeholder="Scrivi la risposta all'utente..."
                                    className="w-full min-h-[90px] rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  />
                                  <div>
                                    <Label className="text-xs">Foto allegate (max 5)</Label>
                                    <Input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp,image/gif"
                                      multiple
                                      className="mt-1"
                                      onChange={(e) => {
                                        const selected = Array.from(e.target.files || []).slice(0, 5);
                                        setReplyFiles((prev) => ({ ...prev, [r.id]: selected }));
                                      }}
                                    />
                                    {draftFiles.length > 0 ? (
                                      <p className="text-[11px] text-slate-500 mt-1">
                                        {draftFiles.length} foto selezionate:{" "}
                                        {draftFiles.map((f) => f.name).join(", ")}
                                      </p>
                                    ) : null}
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => sendRisposta(r.id)}
                                    disabled={replyingId === r.id}
                                  >
                                    {replyingId === r.id ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4 mr-2" />
                                    )}
                                    Invia risposta
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Dashboard Module
function DashboardModule({ data, onNavigate, adminUser }) {
  const stats = data["dashboard/stats"] || {};
  const activity = data["dashboard/recent-activity"] || {};
  const oggi = new Date();
  const giorni = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
  const mesi = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
  ];
  const dataFormattata = `${giorni[oggi.getDay()]} ${oggi.getDate()} ${mesi[oggi.getMonth()]}`;
  const ora = oggi.getHours();
  const saluto = ora >= 5 && ora < 12 ? "Buongiorno" : ora >= 12 && ora < 18 ? "Buon pomeriggio" : "Buonasera";
  const displayName =
    adminUser?.name ||
    (adminUser?.scope === "crm_operatore" ? "Operatore CRM" : "Super Admin");

  const kpiCards = [
    {
      id: "utenti",
      title: "Utenti",
      value: stats.users?.total || 0,
      subtitle: "Account piattaforma",
      metric: `${stats.users?.active || 0} attivi`,
      gradient: "bg-gradient-to-br from-violet-50 to-purple-100",
      border: "border border-violet-300",
      iconBg: "bg-purple-500",
      titleClass: "text-purple-900",
      subClass: "text-purple-700/70",
      icon: Users,
    },
    {
      id: "strutture",
      title: "Strutture",
      value: stats.strutture?.total || 0,
      subtitle: "Organizzazioni registrate",
      metric: "Sedi collegate",
      gradient: "bg-gradient-to-br from-teal-50 to-emerald-100",
      border: "border border-teal-300",
      iconBg: "bg-teal-600",
      titleClass: "text-teal-900",
      subClass: "text-teal-700/70",
      icon: Building,
    },
    {
      id: "fascicoli",
      title: "Fascicoli",
      value: stats.fascicoli?.total || 0,
      subtitle: "Documentazione assistenziale",
      metric: `${stats.pai?.total || 0} PAI`,
      gradient: "bg-gradient-to-br from-slate-50 to-slate-100",
      border: "border border-slate-300",
      iconBg: "bg-slate-700",
      titleClass: "text-slate-900",
      subClass: "text-slate-600/80",
      icon: FileText,
    },
    {
      id: "documenti",
      title: "Documenti",
      value: stats.documents?.total || 0,
      subtitle: "File caricati",
      metric: `${stats.activity?.recent_logins || 0} accessi / 7gg`,
      gradient: "bg-gradient-to-br from-sky-50 to-blue-100",
      border: "border border-sky-300",
      iconBg: "bg-sky-600",
      titleClass: "text-sky-900",
      subClass: "text-sky-700/70",
      icon: FileText,
    },
  ];

  const quickActions = [
    { id: "utenti", line1: "Gestisci utenti", line2: "account e ruoli", icon: UserPlus, bg: "bg-[#22c55e]" },
    { id: "strutture", line1: "Strutture", line2: "anagrafica sedi", icon: Building, bg: "bg-[#a855f7]" },
    { id: "assistenza", line1: "Assistenza", line2: "ticket in coda", icon: Headphones, bg: "bg-[#3b82f6]" },
    { id: "manutenzione", line1: "Manutenzione", line2: "modalità sistema", icon: Wrench, bg: "bg-[#f97316]" },
    { id: "sicurezza", line1: "Sicurezza", line2: "monitoraggio", icon: ShieldAlert, bg: "bg-[#ef4444]" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-[28px]">
          {saluto}, <span className="text-slate-800">{displayName}</span>
        </h2>
        <p className="mt-1 text-sm text-slate-500">Panoramica operativa della piattaforma Affidya</p>
        <div className="mt-2.5 flex items-center gap-2 text-slate-700">
          <CalendarDays className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-medium">{dataFormattata}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          const clickable = kpi.id === "utenti" || kpi.id === "strutture";
          const inner = (
            <Card
              className={`card-base flex h-full min-h-[172px] flex-col ${kpi.gradient} ${kpi.border} shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md`}
            >
              <CardContent className="flex min-h-0 flex-col gap-1.5 p-4">
                <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${kpi.iconBg}`}>
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <div className="min-w-0 space-y-0.5 pt-0.5">
                  <h3 className={`text-base font-bold leading-tight ${kpi.titleClass}`}>{kpi.title}</h3>
                  <p className={`line-clamp-2 text-[11px] leading-snug ${kpi.subClass}`}>{kpi.subtitle}</p>
                </div>
                <div className="mt-1 min-w-0">
                  <div className="flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-hidden rounded-lg bg-white/80 px-2 py-1 shadow-sm ring-1 ring-slate-100">
                    <span className="text-lg font-bold tabular-nums text-slate-900">{kpi.value}</span>
                    <span className="min-w-0 truncate text-[10px] leading-tight text-slate-600">{kpi.metric}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          if (!clickable || !onNavigate) return <div key={kpi.title}>{inner}</div>;
          return (
            <button
              key={kpi.title}
              type="button"
              className="block h-full w-full cursor-pointer text-left"
              onClick={() => onNavigate(kpi.id)}
            >
              {inner}
            </button>
          );
        })}
      </div>

      <section
        className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-[0_2px_12px_rgba(15,23,42,0.06)] sm:px-5 sm:py-4"
        aria-label="Azioni rapide"
      >
        <div className="flex gap-3 overflow-x-auto pb-0.5 sm:gap-4 lg:grid lg:grid-cols-5 lg:overflow-visible">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                onClick={() => onNavigate?.(action.id)}
                className="group flex min-w-[148px] flex-shrink-0 items-center gap-2.5 rounded-xl px-1 py-1 text-left transition-colors hover:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 lg:min-w-0"
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${action.bg} transition-transform group-hover:scale-[1.04] sm:h-12 sm:w-12`}
                >
                  <Icon className="h-5 w-5 text-white sm:h-[22px] sm:w-[22px]" strokeWidth={2.25} />
                </div>
                <div className="min-w-0 pr-1">
                  <p className="text-[12px] font-bold leading-[1.25] text-slate-900 sm:text-[13px]">{action.line1}</p>
                  <p className="mt-0.5 text-[12px] font-semibold leading-[1.25] text-slate-700 sm:text-[13px]">
                    {action.line2}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <Card className="card-base border border-slate-200/70 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5 text-emerald-600" />
            Attività recenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activity.activities?.length > 0 ? (
              activity.activities.slice(0, 12).map((item, index) => (
                <div key={index} className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {typeof item.details === "string"
                          ? item.details
                          : `${item.action} - ${item.section}`}
                      </p>
                      <p className="text-xs text-slate-500">
                        {item.user_name} — {item.timestamp ? new Date(item.timestamp).toLocaleString("it-IT") : ""}
                      </p>
                    </div>
                  </div>
                  {item.section ? <Badge variant="outline">{item.section}</Badge> : null}
                </div>
              ))
            ) : (
              <p className="py-8 text-center text-slate-500">Nessuna attività recente</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== REGISTRAZIONE MANUALE ====================

const TIPI_STRUTTURA = [
  "RSA", "Casa di Riposo", "Comunità Alloggio", "Centro Diurno",
  "Residenza Protetta", "Hospice", "Altro",
];

const TIER_OPTIONS = [
  { id: "tier_1", label: "1 Struttura" },
  { id: "tier_2", label: "2 Strutture" },
  { id: "tier_3", label: "3 Strutture" },
  { id: "tier_4", label: "4 Strutture" },
  { id: "tier_5", label: "5 Strutture" },
  { id: "tier_6", label: "6 Strutture" },
  { id: "tier_7", label: "7 Strutture" },
  { id: "tier_8", label: "8 Strutture" },
  { id: "tier_9", label: "9 Strutture" },
  { id: "tier_10", label: "10 Strutture" },
  { id: "tier_unlimited", label: "10+ Strutture" },
];

const EMPTY_REG_FORM = {
  organizationName: "", tipoStruttura: "", postiTotali: "",
  telefonoStruttura: "", comune: "", via: "", numeroCivico: "",
  provincia: "", tariffaDoppiaWc: "", tariffaSingolaWc: "",
  sitoWeb: "", partitaIva: "", pec: "", sdi: "", zohoRecordId: "",
  nomeReferente: "", telefonoReferente: "",
  adminEmail: "", password: "", confirmPassword: "",
  plan: "monthly", plan_tier: "tier_1",
};

function ManualRegistrationDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_REG_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [zohoLoaded, setZohoLoaded] = useState(false);
  const [loadingZoho, setLoadingZoho] = useState(false);
  const [zohoSearchQuery, setZohoSearchQuery] = useState("");
  const [zohoSearchLoading, setZohoSearchLoading] = useState(false);
  const [zohoSearchResults, setZohoSearchResults] = useState([]);
  const zohoDebounceRef = useRef(null);

  const reset = () => {
    setStep(1);
    setForm({ ...EMPTY_REG_FORM });
    setResult(null);
    setSubmitting(false);
    setZohoLoaded(false);
    setLoadingZoho(false);
    setZohoSearchQuery("");
    setZohoSearchLoading(false);
    setZohoSearchResults([]);
    if (zohoDebounceRef.current) {
      clearTimeout(zohoDebounceRef.current);
      zohoDebounceRef.current = null;
    }
  };

  useEffect(() => () => {
    if (zohoDebounceRef.current) clearTimeout(zohoDebounceRef.current);
  }, []);

  const handleClose = (openState) => {
    if (!openState) {
      reset();
      onOpenChange(false);
    }
  };

  const set = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const mergeZohoIntoForm = useCallback((zohoData, zohoIdExplicit) => {
    setForm((prev) => {
      const tipoRaw = zohoData.tipoStruttura;
      const tipoStruttura =
        tipoRaw && TIPI_STRUTTURA.includes(String(tipoRaw).trim())
          ? String(tipoRaw).trim()
          : tipoRaw
            ? "Altro"
            : prev.tipoStruttura;
      return {
        ...prev,
        zohoRecordId: zohoIdExplicit != null ? String(zohoIdExplicit) : prev.zohoRecordId,
        organizationName: zohoData.organizationName || prev.organizationName,
        tipoStruttura,
        postiTotali: zohoData.postiTotali !== undefined && zohoData.postiTotali !== ""
          ? String(zohoData.postiTotali)
          : prev.postiTotali,
        telefonoStruttura: zohoData.telefonoStruttura || prev.telefonoStruttura,
        comune: zohoData.comune || prev.comune,
        via: zohoData.via || prev.via,
        numeroCivico: zohoData.numeroCivico || prev.numeroCivico,
        provincia: zohoData.provincia || prev.provincia,
        tariffaDoppiaWc:
          zohoData.tariffaDoppiaWc !== undefined && zohoData.tariffaDoppiaWc !== ""
            ? String(zohoData.tariffaDoppiaWc)
            : prev.tariffaDoppiaWc,
        tariffaSingolaWc:
          zohoData.tariffaSingolaWc !== undefined && zohoData.tariffaSingolaWc !== ""
            ? String(zohoData.tariffaSingolaWc)
            : prev.tariffaSingolaWc,
        sitoWeb: zohoData.sitoWeb || prev.sitoWeb,
        partitaIva: zohoData.partitaIva || prev.partitaIva,
        pec: zohoData.pec || prev.pec,
        sdi: zohoData.sdi || prev.sdi,
        nomeReferente: zohoData.nomeReferente || prev.nomeReferente,
        telefonoReferente: zohoData.telefonoReferente || prev.telefonoReferente,
      };
    });
    setZohoLoaded(true);
  }, []);

  const fetchAdminZohoPrefill = useCallback(async (zohoId) => {
    const id = (zohoId || "").trim();
    if (!id || id.length < 10) return;
    setLoadingZoho(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/zoho-struttura/${encodeURIComponent(id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg).join(", ") : typeof d === "string" ? d : "Errore caricamento Zoho";
        throw new Error(msg);
      }
      if (data.success && data.data) {
        mergeZohoIntoForm(data.data, data.zohoId || id);
        toast.success("Dati struttura caricati da Zoho. Completa i campi mancanti.");
      }
    } catch (e) {
      toast.error(e.message || "Impossibile recuperare i dati da Zoho");
      setZohoLoaded(false);
    } finally {
      setLoadingZoho(false);
    }
  }, [mergeZohoIntoForm]);

  const handleZohoIdInputChange = (value) => {
    setForm((prev) => ({ ...prev, zohoRecordId: value }));
    setZohoLoaded(false);
    if (zohoDebounceRef.current) clearTimeout(zohoDebounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length >= 10) {
      zohoDebounceRef.current = setTimeout(() => {
        fetchAdminZohoPrefill(trimmed);
      }, 800);
    }
  };

  const runZohoSearchByName = async () => {
    const q = zohoSearchQuery.trim();
    if (q.length < 2) {
      toast.error("Inserisci almeno 2 caratteri per la ricerca");
      return;
    }
    setZohoSearchLoading(true);
    setZohoSearchResults([]);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/zoho-strutture/search?q=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg).join(", ") : typeof d === "string" ? d : "Ricerca non riuscita";
        throw new Error(msg);
      }
      setZohoSearchResults(data.results || []);
      if (!(data.results || []).length) {
        toast.info("Nessuna struttura trovata su Zoho con questo nome");
      }
    } catch (e) {
      toast.error(e.message || "Errore ricerca Zoho");
    } finally {
      setZohoSearchLoading(false);
    }
  };

  const selectZohoFromSearch = (row) => {
    if (!row?.id) return;
    setForm((prev) => ({ ...prev, zohoRecordId: row.id }));
    setZohoSearchResults([]);
    setZohoSearchQuery(row.name || "");
    fetchAdminZohoPrefill(row.id);
  };

  const tariffaDoppiaOk = form.tariffaDoppiaWc !== "" && !isNaN(Number(form.tariffaDoppiaWc)) && Number(form.tariffaDoppiaWc) >= 0;
  const tariffaSingolaOk = form.tariffaSingolaWc !== "" && !isNaN(Number(form.tariffaSingolaWc)) && Number(form.tariffaSingolaWc) >= 0;
  const canGoStep2 = form.organizationName && form.tipoStruttura && form.postiTotali
    && form.telefonoStruttura && form.comune && form.via && form.numeroCivico
    && form.provincia && tariffaDoppiaOk && tariffaSingolaOk;

  const canSubmit = canGoStep2 && form.nomeReferente && form.telefonoReferente
    && form.adminEmail && form.password && form.password === form.confirmPassword
    && form.password.length >= 10;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const payload = { ...form };
      delete payload.confirmPassword;
      payload.postiTotali = Number(payload.postiTotali);
      payload.tariffaDoppiaWc = Number(payload.tariffaDoppiaWc);
      payload.tariffaSingolaWc = Number(payload.tariffaSingolaWc);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/register-manual`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore nella registrazione");

      setResult(data);
      toast.success(data.message);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputField = (label, field, opts = {}) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600">{label}{opts.required !== false && " *"}</Label>
      <Input
        value={form[field]}
        onChange={set(field)}
        type={opts.type || "text"}
        placeholder={opts.placeholder || ""}
        className="h-9 text-sm"
      />
    </div>
  );

  if (result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Registrazione completata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm">
              <div><span className="font-medium">Email:</span> {result.email}</div>
              <div><span className="font-medium">Organizzazione:</span> {form.organizationName}</div>
              <div><span className="font-medium">Password:</span> {form.password}</div>
              <div><span className="font-medium">Org ID:</span> <code className="text-xs">{result.organizationId}</code></div>
              {result.zohoId && <div><span className="font-medium">Zoho ID:</span> <code className="text-xs">{result.zohoId}</code></div>}
              {result.zohoError && (
                <div className="text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Zoho: {result.zohoError}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500">
              L'utente puo accedere immediatamente con le credenziali sopra indicate.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => handleClose(false)} size="sm">Chiudi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-500" />
            Registrazione manuale
            <Badge variant="outline" className="ml-2 text-xs">
              Step {step}/2
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div
              className={`p-4 rounded-xl border space-y-3 ${
                zohoLoaded ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Link2 className="h-4 w-4 shrink-0 text-amber-700" />
                Collegamento Zoho (opzionale)
              </div>
              <p className={`text-xs ${zohoLoaded ? "text-green-800" : "text-amber-800"}`}>
                {zohoLoaded
                  ? "Dati struttura caricati da Zoho. Verifica e completa i campi sotto."
                  : "Cerca per nome oppure incolla l'ID record Zoho (Accounts): i campi struttura verranno precompilati come in registrazione utente."}
              </p>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Cerca struttura su Zoho per nome</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={zohoSearchQuery}
                    onChange={(e) => setZohoSearchQuery(e.target.value)}
                    placeholder="Es. Residenza San Giuseppe"
                    className="h-9 text-sm bg-white flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runZohoSearchByName();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-9 shrink-0"
                    disabled={zohoSearchLoading || zohoSearchQuery.trim().length < 2}
                    onClick={runZohoSearchByName}
                  >
                    {zohoSearchLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1" />
                        Cerca
                      </>
                    )}
                  </Button>
                </div>
                {zohoSearchResults.length > 0 && (
                  <ul className="mt-2 max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white text-sm shadow-sm">
                    {zohoSearchResults.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectZohoFromSearch(r)}
                        >
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-500">
                            {[r.comune, r.phone].filter(Boolean).join(" · ")}
                            {r.id && (
                              <>
                                {" · "}
                                <span className="font-mono">ID {r.id}</span>
                              </>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="manual-reg-zoho-id" className="text-xs font-medium text-slate-600">
                  Zoho Record ID (Accounts)
                </Label>
                <div className="relative">
                  <Input
                    id="manual-reg-zoho-id"
                    value={form.zohoRecordId}
                    onChange={(e) => handleZohoIdInputChange(e.target.value)}
                    placeholder="ID dopo selezione dalla lista oppure incolla manualmente"
                    className={`h-9 text-sm bg-white pr-10 ${zohoLoaded ? "border-green-400" : ""}`}
                  />
                  {loadingZoho && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                  )}
                </div>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Dati Struttura</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {inputField("Nome Struttura", "organizationName")}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Tipo Struttura *</Label>
                <Select value={form.tipoStruttura} onValueChange={set("tipoStruttura")}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPI_STRUTTURA.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inputField("Posti Totali", "postiTotali", { type: "number" })}
              {inputField("Telefono Struttura", "telefonoStruttura")}
              {inputField("Via", "via")}
              {inputField("Numero Civico", "numeroCivico")}
              {inputField("Comune", "comune")}
              {inputField("Provincia", "provincia")}
              {inputField("Tariffa Doppia WC", "tariffaDoppiaWc", { type: "number" })}
              {inputField("Tariffa Singola WC", "tariffaSingolaWc", { type: "number" })}
            </div>
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1 pt-2">Campi facoltativi</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {inputField("Sito Web", "sitoWeb", { required: false })}
              {inputField("Partita IVA", "partitaIva", { required: false })}
              {inputField("PEC", "pec", { required: false })}
              {inputField("SDI", "sdi", { required: false })}
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => setStep(2)} disabled={!canGoStep2} size="sm">
                Avanti <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Referente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {inputField("Nome Referente", "nomeReferente")}
              {inputField("Telefono Referente", "telefonoReferente")}
            </div>

            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1 pt-2">Credenziali di accesso</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {inputField("Email amministratore", "adminEmail", { type: "email" })}
              <div />
              {inputField("Password", "password", { type: "password", placeholder: "Min. 10 caratteri" })}
              {inputField("Conferma Password", "confirmPassword", { type: "password" })}
            </div>
            {form.password && form.confirmPassword && form.password !== form.confirmPassword && (
              <p className="text-xs text-red-500">Le password non coincidono</p>
            )}
            {form.password && form.password.length > 0 && form.password.length < 10 && (
              <p className="text-xs text-red-500">La password deve essere di almeno 10 caratteri</p>
            )}

            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1 pt-2">Piano</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Tier *</Label>
                <Select value={form.plan_tier} onValueChange={set("plan_tier")}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Frequenza *</Label>
                <Select value={form.plan} onValueChange={set("plan")}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensile</SelectItem>
                    <SelectItem value="yearly">Annuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              L'utenza verra creata con stato <strong>ACTIVE</strong> senza passare da Stripe. Stripe ID impostato su "manual_admin".
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                Indietro
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creazione...</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" /> Crea Utenza</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Export / import tenant per replica su istanza locale
async function downloadTenantExport(userId, email) {
  const token = localStorage.getItem("super_admin_token");
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/export`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : detail?.message || "Export non riuscito";
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match
    ? match[1]
    : `affidya-tenant-${(email || userId || "export").split("@")[0]}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function TenantImportDialog({ open, onOpenChange, onSuccess }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const runImport = async (replace = false) => {
    if (!selectedFile) {
      toast.error("Seleziona un file export (.json)");
      return;
    }
    setImporting(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const form = new FormData();
      form.append("file", selectedFile);
      form.append("replace", replace ? "true" : "false");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/import`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        },
      );
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && !replace) {
        const conflicts = data.detail?.conflicts || {};
        const existingEmails = (conflicts.existing_users || [])
          .map((u) => u.email)
          .filter(Boolean)
          .join(", ");
        const orgLabel = conflicts.org_name || conflicts.org_id || "organizzazione";
        const confirmMsg =
          `Tenant già presente (${orgLabel}${existingEmails ? ` — utenti: ${existingEmails}` : ""}).\n\n` +
          "Sovrascrivere organizzazione, utenti, OSS e dati collegati?";
        if (window.confirm(confirmMsg)) {
          await runImport(true);
        }
        return;
      }
      if (!res.ok) {
        const detail = data.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : detail?.message || "Import non riuscito";
        throw new Error(msg);
      }
      toast.success(
        data.replaced
          ? `Tenant importato (sostituito): ${data.total_documents} documenti`
          : `Tenant importato: ${data.total_documents} documenti`,
      );
      handleClose(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.message || "Errore durante l'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-purple-500" />
            Importa utente / tenant
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            Carica un file <code className="text-xs bg-slate-100 px-1 rounded">.json</code> generato
            con Export da un&apos;altra istanza Affidya. Vengono importati organizzazione, utenti,
            OSS, turni, fascicoli e dati collegati (senza file upload).
          </p>
          <div>
            <Label htmlFor="tenant-import-file">File export</Label>
            <Input
              id="tenant-import-file"
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="mt-1.5"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            {selectedFile && (
              <p className="text-xs text-slate-500 mt-1">{selectedFile.name}</p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={importing}>
            Annulla
          </Button>
          <Button
            type="button"
            onClick={() => runImport(false)}
            disabled={importing || !selectedFile}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Importa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Utenti Module – colonna Strutture solo numero; click riga → dialog con dettaglio utente e elenco strutture
function UtentiModule({ data, loading, onRefresh }) {
  const utentiData = data["users"] || { users: [] };
  const [detailUserId, setDetailUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showManualReg, setShowManualReg] = useState(false);
  const [showTenantImport, setShowTenantImport] = useState(false);
  const [exportingUserId, setExportingUserId] = useState(null);
  const [showOnlyBonifici, setShowOnlyBonifici] = useState(false);
  const [showOnlyPagamentiCritici, setShowOnlyPagamentiCritici] = useState(false);
  const [activeFilter, setActiveFilter] = useState("active");

  const isUserActive = (user) => user.is_active !== false;
  const hasPaymentIssue = (user) => !!(user.payment_issue || user.stripe_past_due || user.stripe_canceled);

  const filteredUsers = (utentiData.users || []).filter((u) => {
    if (showOnlyBonifici && !u.pending_bank_transfer) return false;
    if (showOnlyPagamentiCritici && !hasPaymentIssue(u)) return false;
    if (activeFilter === "active") return isUserActive(u);
    return !isUserActive(u);
  });

  const activeCount = (utentiData.users || []).filter(isUserActive).length;
  const inactiveCount = (utentiData.users || []).filter((u) => !isUserActive(u)).length;

  const fetchUserDetail = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setUserDetail(null);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setUserDetail(await res.json());
      else setUserDetail({ error: "Dettaglio non disponibile" });
    } catch (e) {
      setUserDetail({ error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (detailUserId) fetchUserDetail(detailUserId);
  }, [detailUserId]);

  const openUserDetail = (id) => setDetailUserId(id);
  const closeUserDetail = () => {
    setDetailUserId(null);
    setUserDetail(null);
  };

  const handleExportUser = async (user, event) => {
    event?.stopPropagation?.();
    if (!user?.id) return;
    if (!user.organizationId && !(user.strutture || []).length) {
      toast.error("Utente senza organizzazione: export non disponibile");
      return;
    }
    setExportingUserId(user.id);
    try {
      await downloadTenantExport(user.id, user.email);
      toast.success(`Export scaricato per ${user.email}`);
    } catch (e) {
      toast.error(e.message || "Errore export");
    } finally {
      setExportingUserId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Gestione Utenti</h2>
        <div className="flex space-x-2">
          <Button onClick={() => setShowTenantImport(true)} variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Importa utente
          </Button>
          <Button onClick={() => setShowManualReg(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
            <UserPlus className="h-4 w-4 mr-2" />
            Registrazione manuale
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-500">Clicca su una riga per aprire il dettaglio utente e le strutture collegate.</p>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-600">Stato account</span>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setActiveFilter("active")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeFilter === "active"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Attivi ({activeCount})
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter("inactive")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeFilter === "inactive"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Non attivi ({inactiveCount})
          </button>
        </div>
      </div>

      <ManualRegistrationDialog
        open={showManualReg}
        onOpenChange={setShowManualReg}
        onSuccess={onRefresh}
      />
      <TenantImportDialog
        open={showTenantImport}
        onOpenChange={setShowTenantImport}
        onSuccess={onRefresh}
      />

      {/* Filtro bonifici in attesa */}
      {utentiData.users.some(u => u.pending_bank_transfer) && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <Landmark className="w-4 h-4 text-amber-700 shrink-0" />
          <span className="text-sm text-amber-800 font-medium">
            {utentiData.users.filter(u => u.pending_bank_transfer).length} utente/i in attesa di verifica bonifico
          </span>
          <button
            onClick={() => setShowOnlyBonifici(v => !v)}
            className={`ml-auto text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
              showOnlyBonifici
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-white text-amber-700 border-amber-400 hover:bg-amber-100"
            }`}
          >
            {showOnlyBonifici ? "Mostra tutti" : "Mostra solo bonifici"}
          </button>
        </div>
      )}

      {utentiData.users.some(hasPaymentIssue) && (
        <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0" />
          <span className="text-sm text-rose-800 font-medium">
            {utentiData.users.filter(hasPaymentIssue).length} utente/i con abbonamento cancellato o pagamento Stripe in errore
          </span>
          <button
            onClick={() => setShowOnlyPagamentiCritici(v => !v)}
            className={`ml-auto text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
              showOnlyPagamentiCritici
                ? "bg-rose-600 text-white border-rose-600"
                : "bg-white text-rose-700 border-rose-400 hover:bg-rose-100"
            }`}
          >
            {showOnlyPagamentiCritici ? "Mostra tutti" : "Mostra solo criticità"}
          </button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ruolo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Abbonamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Personale</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Struttura / Organizzazione</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Strutture inserite</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-sm text-slate-500">
                      Nessun utente {activeFilter === "active" ? "attivo" : "non attivo"}
                      {showOnlyBonifici ? " con bonifico in attesa" : ""}
                      {showOnlyPagamentiCritici ? " con criticità di pagamento" : ""}.
                    </td>
                  </tr>
                ) : filteredUsers.map((user) => {
                  const count = (user.strutture || []).length;
                  const struttureLabel = count === 0 ? "—" : count === 1 ? "1 struttura" : `${count} strutture`;
                  return (
                    <tr
                      key={user.id}
                      onClick={() => openUserDetail(user.id)}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{user.nome}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={user.role === "super_admin" ? "destructive" : "secondary"}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {user.account_status === "trial_active" ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Trial 14gg</Badge>
                        ) : user.account_status === "trial_expired" ? (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800">Trial scaduto</Badge>
                        ) : (
                          user.subscription_label || "—"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {user.recruiting_plan === "pro" ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">Pro</Badge>
                        ) : user.recruiting_plan === "base" ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">Base</Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{user.facility_name || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {count > 0 ? (
                          <Badge variant="outline" className="font-normal">
                            {struttureLabel}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">{struttureLabel}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-start">
                          <Badge variant={isUserActive(user) ? "default" : "secondary"}>
                            {isUserActive(user) ? "Attivo" : "Disattivo"}
                          </Badge>
                          {pagamentoListBadges(user).map((badge) => (
                            <Badge key={badge.key} className={`${badge.className} text-xs font-medium`}>
                              {badge.label}
                            </Badge>
                          ))}
                          {user.terms_reacceptance_required && user.role !== "super_admin" && (
                            <Badge className="bg-red-100 text-red-800 border border-red-300 text-xs font-medium">
                              Termini non accettati
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8"
                          disabled={exportingUserId === user.id}
                          onClick={(e) => handleExportUser(user, e)}
                          title="Scarica export per replica su istanza locale"
                        >
                          {exportingUserId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-1" />
                              Export
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detailUserId} onOpenChange={(open) => !open && closeUserDetail()}>
        <DialogContent className="flex h-[min(92vh,880px)] max-h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl">
          {detailLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <DialogHeader>
                <DialogTitle>Dettaglio utente</DialogTitle>
                <DialogDescription>Caricamento dati utente</DialogDescription>
              </DialogHeader>
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}
          {!detailLoading && userDetail && (
            <UserDetailContent
              user={userDetail}
              onClose={closeUserDetail}
              onExport={handleExportUser}
              exporting={exportingUserId === detailUserId}
              onEmailUpdated={async () => {
                onRefresh();
                await fetchUserDetail(detailUserId);
              }}
              onSubscriptionUpdated={async () => {
                onRefresh();
                await fetchUserDetail(detailUserId);
              }}
              onActiveChange={async (isActive) => {
                try {
                  const token = localStorage.getItem("super_admin_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${detailUserId}/active`,
                    {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ is_active: isActive }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.detail || "Errore aggiornamento");
                  toast.success(data.message || (isActive ? "Utente attivato" : "Utente disattivato"));
                  onRefresh();
                  fetchUserDetail(detailUserId);
                } catch (e) {
                  toast.error(e.message || "Errore nel cambiamento stato");
                }
              }}
              onFamilyChange={async (enabled) => {
                try {
                  const token = localStorage.getItem("super_admin_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${detailUserId}/family`,
                    {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ enabled }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.detail || "Errore aggiornamento");
                  toast.success(data.message || (enabled ? "Family abilitato" : "Family disabilitato"));
                  onRefresh();
                  fetchUserDetail(detailUserId);
                } catch (e) {
                  toast.error(e.message || "Errore Family");
                }
              }}
              onAssistantVoiceChange={async (enabled) => {
                try {
                  const token = localStorage.getItem("super_admin_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${detailUserId}/assistant-voice`,
                    {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ enabled }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.detail || "Errore aggiornamento");
                  toast.success(data.message || (enabled ? "Chat vocale abilitata" : "Chat vocale disabilitata"));
                  onRefresh();
                  fetchUserDetail(detailUserId);
                } catch (e) {
                  toast.error(e.message || "Errore chat vocale");
                }
              }}
              onWhatsappOpsChange={async (enabled) => {
                try {
                  const token = localStorage.getItem("super_admin_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${detailUserId}/whatsapp-ops`,
                    {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ enabled }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.detail || "Errore aggiornamento");
                  toast.success(
                    data.message || (enabled ? "WhatsApp operativo abilitato" : "WhatsApp operativo disabilitato")
                  );
                  onRefresh();
                  fetchUserDetail(detailUserId);
                } catch (e) {
                  toast.error(e.message || "Errore WhatsApp operativo");
                }
              }}
              onLoginOverrideChange={async (enabled) => {
                try {
                  const token = localStorage.getItem("super_admin_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${detailUserId}/login-access-override`,
                    {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ enabled }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.detail || "Errore aggiornamento");
                  toast.success(data.message || (enabled ? "Accesso forzato attivo" : "Accesso forzato disattivato"));
                  onRefresh();
                  fetchUserDetail(detailUserId);
                } catch (e) {
                  toast.error(e.message || "Errore accesso forzato");
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatAdminDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function termsContextLabel(context) {
  switch (context) {
    case "registration_paid":
      return "Registrazione (pagamento)";
    case "registration_trial":
      return "Registrazione trial";
    case "reacceptance":
      return "Riaccettazione";
    default:
      return context || "—";
  }
}

function userDetailInitials(nome, email) {
  const n = (nome || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  return (email || "?").slice(0, 2).toUpperCase();
}

function AdminDetailField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-0.5 break-words text-sm font-medium text-slate-900">{children ?? "—"}</div>
    </div>
  );
}

function AdminDetailSection({ icon: Icon, iconWrapClass, title, children, className = "" }) {
  return (
    <section
      className={`rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_4px_rgba(15,23,42,0.04)] sm:p-5 ${className}`}
    >
      <div className="mb-4 flex items-center gap-2.5">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-xl ${iconWrapClass || "bg-slate-100 text-slate-600"}`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function TermsAcceptanceAdminSection({ acceptance, history = [], currentVersion, reacceptanceRequired }) {
  const field = (label, value) => (
    <AdminDetailField label={label}>{value ?? "—"}</AdminDetailField>
  );

  return (
    <AdminDetailSection icon={FileText} iconWrapClass="bg-slate-100 text-slate-600" title="Accettazione contrattuale">
      {reacceptanceRequired && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Accettazione mancante o non aggiornata rispetto alla versione corrente ({currentVersion || "—"}).
        </div>
      )}

      {!acceptance ? (
        <p className="text-sm text-slate-500">Nessuna accettazione registrata.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {field("Versione contratto", acceptance.contract_version)}
          {field("Accettato il", formatAdminDateTime(acceptance.accepted_at))}
          {field("Termini", acceptance.accepted_terms ? "Sì" : "No")}
          {field("Clausole 1341-1342", acceptance.accepted_clauses_1341_1342 ? "Sì" : "No")}
          {field("Contesto", termsContextLabel(acceptance.context))}
          {field("IP", acceptance.ip_address)}
          <AdminDetailField label="User-Agent" className="sm:col-span-2">
            {acceptance.user_agent || "—"}
          </AdminDetailField>
          {field("Struttura ID", acceptance.struttura_id)}
          {acceptance.contract_url && (
            <AdminDetailField label="Contratto" className="sm:col-span-2">
              <a href={acceptance.contract_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">
                {acceptance.contract_url}
              </a>
            </AdminDetailField>
          )}
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
            Storico accettazioni ({history.length})
          </p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Versione</th>
                  <th className="px-3 py-2 text-left">Contesto</th>
                  <th className="px-3 py-2 text-left">IP</th>
                  <th className="px-3 py-2 text-left">User ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDateTime(row.accepted_at)}</td>
                    <td className="px-3 py-2">{row.contract_version || "—"}</td>
                    <td className="px-3 py-2">{termsContextLabel(row.context)}</td>
                    <td className="px-3 py-2">{row.ip_address || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.user_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminDetailSection>
  );
}

function UserDetailContent({ user, onClose, onActiveChange, onLoginOverrideChange, onFamilyChange, onAssistantVoiceChange, onWhatsappOpsChange, onEmailUpdated, onSubscriptionUpdated, onExport, exporting }) {
  const [updatingActive, setUpdatingActive] = useState(false);
  const [updatingLoginOverride, setUpdatingLoginOverride] = useState(false);
  const [updatingFamily, setUpdatingFamily] = useState(false);
  const [updatingVoice, setUpdatingVoice] = useState(false);
  const [updatingWhatsappOps, setUpdatingWhatsappOps] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [planTierDraft, setPlanTierDraft] = useState(user.subscription_tier || "tier_1");
  const [planTierSaving, setPlanTierSaving] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [pagamento, setPagamento] = useState(user.pagamento || null);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const isActive = user.is_active !== false;
  const loginAccessOverride = user.login_access_override === true;
  const familyEnabled = user.family_enabled === true;
  const assistantVoiceEnabled = user.assistant_voice_enabled === true;
  const whatsappOpsEnabled = user.whatsapp_ops_enabled === true;
  const isBonifico = user.org_payment_method === "bank_transfer";
  const userStatusRaw = user.status ?? null;
  const orgStatusRaw = user.facility_status ?? null;
  const loginBlockedByStatus = userStatusRaw === "INACTIVE" && !loginAccessOverride;
  const loginBlockedByIsActive = user.is_active === false && !loginAccessOverride;
  const loginBlockedByOrgCanceled = orgStatusRaw === "CANCELED" && !loginAccessOverride;
  const statusMismatch = isActive && userStatusRaw === "INACTIVE" && !loginAccessOverride;

  const orgStatusLabel = (() => {
    if (user.account_status === "trial_active") return "Trial attivo";
    if (user.account_status === "trial_expired") return "Trial scaduto";
    if (user.account_status === "active_paid") return "Pagante";
    if (user.account_status === "suspended") return "Sospeso";
    switch (orgStatusRaw) {
      case "ACTIVE":
        return "Attivo";
      case "PAST_DUE":
        return "Pagamento scaduto (Stripe)";
      case "CANCELED":
        return "Abbonamento cancellato";
      case "PENDING_PAYMENT":
        return "Pagamento in attesa";
      case "PENDING_BANK_TRANSFER":
        return "Bonifico in attesa";
      default:
        return orgStatusRaw || "—";
    }
  })();

  const handleSendRenewalReminder = async () => {
    if (!window.confirm(
      `Inviare l'email di reminder rinnovo a ${user.email}?\n\n` +
      `L'utente riceverà le coordinate bancarie e l'importo per il rinnovo del proprio abbonamento.`
    )) return;
    setSendingReminder(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/send-renewal-reminder`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Errore invio email");
      toast.success(data.message || "Email reminder inviata con successo");
    } catch (e) {
      toast.error(e.message || "Errore durante l'invio del reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSyncStripe = async () => {
    if (!user.id) return;
    setSyncingStripe(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/sync-stripe`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Errore lettura Stripe");
      setPagamento(data.pagamento || null);
      toast.success("Stato Stripe aggiornato");
    } catch (e) {
      toast.error(e.message || "Errore Stripe");
    } finally {
      setSyncingStripe(false);
    }
  };

  useEffect(() => {
    setPlanTierDraft(user.subscription_tier || "tier_1");
    setPagamento(user.pagamento || null);
  }, [user.id, user.subscription_tier, user.pagamento]);

  const handleImpersonate = async () => {
    if (!user.id) return;
    setImpersonating(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/impersonate`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Errore impersonazione");
      }
      const data = await res.json();
      const impersonateUrl = new URL(window.location.origin);
      impersonateUrl.pathname = "/";
      impersonateUrl.searchParams.set("impersonate_token", data.token);
      impersonateUrl.searchParams.set("impersonate_refresh", data.refresh_token);
      window.open(impersonateUrl.toString(), "_blank");
      toast.success(`Login come ${user.nome || user.email} aperto in nuova scheda`);
    } catch (e) {
      toast.error(e.message || "Errore durante l'impersonazione");
    } finally {
      setImpersonating(false);
    }
  };

  if (user.error) {
    return <p className="text-red-600 py-4">{user.error}</p>;
  }
  const strutture = user.strutture || [];
  const stats = user.stats || {};
  const initials = userDetailInitials(user.nome, user.email);

  const handleLoginOverrideToggle = async (checked) => {
    if (!onLoginOverrideChange) return;
    const msg = checked
      ? "Abilitare l'accesso forzato?\n\nL'utente potrà fare login anche con status INACTIVE e abbonamento org CANCELED. Verranno impostati is_active e status ACTIVE."
      : "Disabilitare l'accesso forzato?\n\nL'utente tornerà soggetto ai blocchi normali (status utente e abbonamento org).";
    if (!window.confirm(msg)) return;
    setUpdatingLoginOverride(true);
    try {
      await onLoginOverrideChange(checked);
    } finally {
      setUpdatingLoginOverride(false);
    }
  };

  const handleActiveToggle = async (checked) => {
    if (!onActiveChange) return;
    setUpdatingActive(true);
    try {
      await onActiveChange(checked);
    } finally {
      setUpdatingActive(false);
    }
  };

  const handleFamilyToggle = async (checked) => {
    if (!onFamilyChange) return;
    setUpdatingFamily(true);
    try {
      await onFamilyChange(checked);
    } finally {
      setUpdatingFamily(false);
    }
  };

  const handleAssistantVoiceToggle = async (checked) => {
    if (!onAssistantVoiceChange) return;
    setUpdatingVoice(true);
    try {
      await onAssistantVoiceChange(checked);
    } finally {
      setUpdatingVoice(false);
    }
  };

  const handleWhatsappOpsToggle = async (checked) => {
    if (!onWhatsappOpsChange) return;
    setUpdatingWhatsappOps(true);
    try {
      await onWhatsappOpsChange(checked);
    } finally {
      setUpdatingWhatsappOps(false);
    }
  };

  const openEmailDialog = () => {
    setNewEmail("");
    setConfirmEmail("");
    setEmailDialogOpen(true);
  };

  const hasOrganization = !!user.organizationId;
  const planTierUnchanged =
    (planTierDraft || "tier_1") === (user.subscription_tier || "tier_1");

  const handleSavePlanTier = async () => {
    if (!user.id || planTierUnchanged || !hasOrganization) return;
    if (
      !window.confirm(
        `Aggiornare il piano abbonamento dell'organizzazione collegata a questo utente?\n\n` +
          `Nuovo piano: ${TIER_OPTIONS.find((t) => t.id === planTierDraft)?.label || planTierDraft}\n\n` +
          `Stripe non viene modificato; il limite strutture nell'app si aggiorna subito.`,
      )
    ) {
      return;
    }
    setPlanTierSaving(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/plan-tier`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ plan_tier: planTierDraft }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        const msg = Array.isArray(d)
          ? d.map((x) => x.msg).filter(Boolean).join(", ")
          : typeof d === "string"
            ? d
            : d?.msg;
        throw new Error(msg || "Errore aggiornamento piano");
      }
      toast.success(data.message || "Piano aggiornato");
      if (onSubscriptionUpdated) await onSubscriptionUpdated();
    } catch (e) {
      toast.error(e.message || "Errore durante l'aggiornamento del piano");
    } finally {
      setPlanTierSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    const a = newEmail.trim().toLowerCase();
    const b = confirmEmail.trim().toLowerCase();
    if (!a || !b) {
      toast.error("Compila entrambi i campi email");
      return;
    }
    if (a !== b) {
      toast.error("Le due email non coincidono");
      return;
    }
    if (a === (user.email || "").trim().toLowerCase()) {
      toast.error("La nuova email è uguale a quella attuale");
      return;
    }
    if (
      !window.confirm(
        `Confermi il cambio email di accesso?\n\nDa: ${user.email}\nA: ${a}\n\nL'utente dovrà usare la nuova email al login.`,
      )
    ) {
      return;
    }
    setEmailSaving(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/email`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: a }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg).filter(Boolean).join(", ") : typeof d === "string" ? d : d?.msg;
        throw new Error(msg || "Errore aggiornamento email");
      }
      toast.success(data.message || "Email aggiornata");
      setEmailDialogOpen(false);
      if (onEmailUpdated) await onEmailUpdated();
    } catch (e) {
      toast.error(e.message || "Errore durante il cambio email");
    } finally {
      setEmailSaving(false);
    }
  };

  const statCard = (label, value, subtitle) => (
    <div key={label} className="rounded-xl border border-slate-200/80 bg-white px-3 py-3 text-center shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
      <div className="text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      {subtitle && <div className="mt-1 text-[11px] leading-snug text-slate-400">{subtitle}</div>}
    </div>
  );

  const recruitingLabel = user.recruiting_plan
    ? `Piano ${user.recruiting_plan.charAt(0).toUpperCase() + user.recruiting_plan.slice(1)}`
    : "—";
  const userStatusBadgeClass =
    userStatusRaw === "ACTIVE"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : userStatusRaw === "INACTIVE"
        ? "border-slate-200 bg-slate-100 text-slate-600"
        : "border-slate-200 bg-slate-50 text-slate-700";
  const orgStatusBadgeClass =
    user.account_status === "active_paid" || orgStatusRaw === "ACTIVE"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : user.account_status === "trial_active"
        ? "border-sky-200 bg-sky-50 text-sky-800"
        : orgStatusRaw === "PAST_DUE" || user.account_status === "trial_expired"
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : orgStatusRaw === "CANCELED" || user.account_status === "suspended"
            ? "border-rose-200 bg-rose-50 text-rose-800"
            : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-500" />
              Cambia email di accesso
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Email attuale: <span className="font-mono font-medium text-slate-900">{user.email}</span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="admin-new-email">Nuova email</Label>
              <Input
                id="admin-new-email"
                type="email"
                autoComplete="off"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="nuova@email.it"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="admin-confirm-email">Ripeti nuova email</Label>
              <Input
                id="admin-confirm-email"
                type="email"
                autoComplete="off"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="nuova@email.it"
              />
            </div>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              Se questa utenza è l&apos;amministratore dell&apos;organizzazione, anche il campo admin email
              in fatturazione/abbonamento viene allineato automaticamente.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={emailSaving}>
              Annulla
            </Button>
            <Button type="button" onClick={handleSaveEmail} disabled={emailSaving} className="bg-purple-600 hover:bg-purple-700">
              {emailSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva nuova email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogHeader className="shrink-0 border-b border-slate-200/80 bg-white px-6 pb-4 pt-6 pr-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3.5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-sm font-bold tracking-wide text-indigo-700">
              {initials}
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-xl font-semibold tracking-tight text-slate-900">
                {user.nome || "Dettaglio utente"}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-500">
                {[user.email, user.facility_name, user.facility_comune].filter(Boolean).join(" · ")}
              </DialogDescription>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {user.role ? (
                  <Badge variant="outline" className="capitalize font-medium">
                    {user.role}
                  </Badge>
                ) : null}
                <Badge className={`border ${userStatusBadgeClass}`}>
                  {isActive ? "Accesso attivo" : "Accesso disattivo"}
                </Badge>
                <Badge className={`border ${orgStatusBadgeClass}`}>{orgStatusLabel}</Badge>
                {loginAccessOverride ? (
                  <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800">Accesso forzato</Badge>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                onClick={(e) => onExport(user, e)}
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export tenant
              </Button>
            )}
            {isBonifico && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendRenewalReminder}
                disabled={sendingReminder}
                className="border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
              >
                {sendingReminder ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Landmark className="mr-2 h-4 w-4" />
                )}
                Reminder rinnovo
              </Button>
            )}
            {(isActive || loginAccessOverride) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleImpersonate}
                disabled={impersonating}
                className="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800"
              >
                {impersonating ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="mr-2 h-4 w-4" />
                )}
                Login come utente
                <ExternalLink className="ml-1.5 h-3 w-3 opacity-60" />
              </Button>
            )}
          </div>
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/90 px-6 py-5">
        <div className="space-y-5">
          <div className="grid items-start gap-5 lg:grid-cols-2">
            <div className="space-y-5">
              <AdminDetailSection icon={IdCard} iconWrapClass="bg-indigo-50 text-indigo-600" title="Dati utente">
                <div className="grid gap-4 sm:grid-cols-2">
                  <AdminDetailField label="Nome">{user.nome || "—"}</AdminDetailField>
                  <AdminDetailField label="Ruolo">
                    <span className="capitalize">{user.role || "—"}</span>
                  </AdminDetailField>
                  <AdminDetailField label="Email" className="sm:col-span-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="break-all">{user.email || "—"}</span>
                      <Button type="button" variant="outline" size="sm" className="h-7 shrink-0 text-xs" onClick={openEmailDialog}>
                        <Pencil className="mr-1 h-3 w-3" />
                        Cambia email
                      </Button>
                    </div>
                  </AdminDetailField>
                </div>
              </AdminDetailSection>

              <AdminDetailSection icon={Shield} iconWrapClass="bg-emerald-50 text-emerald-700" title="Stato accesso">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">
                        {isActive ? "Accesso attivo" : "Accesso disattivo"}
                      </p>
                      <p className="text-xs text-slate-500">Flag is_active sul database utente</p>
                    </div>
                    <Switch
                      checked={isActive}
                      onCheckedChange={handleActiveToggle}
                      disabled={updatingActive || loginAccessOverride}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">Accesso forzato</p>
                        {loginAccessOverride && (
                          <Badge className="border border-emerald-200 bg-emerald-100 text-xs text-emerald-800">
                            login consentito
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Consente il login anche con status INACTIVE o abbonamento CANCELED.
                      </p>
                    </div>
                    <Switch
                      checked={loginAccessOverride}
                      onCheckedChange={handleLoginOverrideToggle}
                      disabled={updatingLoginOverride}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">Family</p>
                        {familyEnabled ? (
                          <Badge className="border border-violet-200 bg-violet-100 text-xs text-violet-800">
                            abilitato
                          </Badge>
                        ) : (
                          <Badge className="border border-slate-200 bg-white text-xs text-slate-500">
                            disabilitato
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Mostra il tab Family nei fascicoli e la voce Gestione → AFFIDYA Family.
                      </p>
                    </div>
                    <Switch
                      checked={familyEnabled}
                      onCheckedChange={handleFamilyToggle}
                      disabled={updatingFamily}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">Assistente vocale</p>
                        {assistantVoiceEnabled ? (
                          <Badge className="border border-teal-200 bg-teal-100 text-xs text-teal-800">
                            abilitato
                          </Badge>
                        ) : (
                          <Badge className="border border-slate-200 bg-white text-xs text-slate-500">
                            disabilitato
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Chat vocale EVA, nel pannello laterale (serve anche la funzione attiva in Intelligenza artificiale → Chat vocale).
                      </p>
                    </div>
                    <Switch
                      checked={assistantVoiceEnabled}
                      onCheckedChange={handleAssistantVoiceToggle}
                      disabled={updatingVoice}
                      data-testid="admin-user-assistant-voice"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">WhatsApp operativo</p>
                        {whatsappOpsEnabled ? (
                          <Badge className="border border-emerald-200 bg-emerald-100 text-xs text-emerald-800">
                            abilitato
                          </Badge>
                        ) : (
                          <Badge className="border border-slate-200 bg-white text-xs text-slate-500">
                            disabilitato
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Mostra Impostazioni → WhatsApp operativo e permette messaggi/vocali/file
                        sul numero WhatsApp Affidya (con conferma e isolamento struttura).
                      </p>
                    </div>
                    <Switch
                      checked={whatsappOpsEnabled}
                      onCheckedChange={handleWhatsappOpsToggle}
                      disabled={updatingWhatsappOps}
                      data-testid="admin-user-whatsapp-ops"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                    <div className="flex min-w-[140px] flex-1 flex-col gap-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status DB</span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge className={`border ${userStatusBadgeClass}`}>{userStatusRaw || "non impostato"}</Badge>
                        {loginBlockedByStatus && (
                          <span className="text-xs font-medium text-red-700">blocca il login</span>
                        )}
                      </div>
                    </div>
                    <div className="flex min-w-[140px] flex-1 flex-col gap-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Stato org</span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge className={`border ${orgStatusBadgeClass}`}>{orgStatusLabel}</Badge>
                        {orgStatusRaw && orgStatusRaw !== "ACTIVE" && (
                          <span className="font-mono text-[11px] text-slate-400">({orgStatusRaw})</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {loginAccessOverride && (
                    <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                      <strong>Accesso forzato attivo:</strong> l&apos;utente può accedere nonostante
                      {userStatusRaw === "INACTIVE" ? " status INACTIVE" : ""}
                      {userStatusRaw === "INACTIVE" && orgStatusRaw === "CANCELED" ? " e" : ""}
                      {orgStatusRaw === "CANCELED" ? " abbonamento CANCELED" : ""}
                      {userStatusRaw !== "INACTIVE" && orgStatusRaw !== "CANCELED"
                        ? " eventuali blocchi su status o abbonamento"
                        : ""}
                      .
                    </p>
                  )}
                  {statusMismatch && (
                    <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                      <strong>Incoerenza:</strong> is_active è attivo ma status è INACTIVE — il login
                      viene bloccato. Abilita &quot;Accesso forzato&quot; oppure disattiva e riattiva l&apos;utente.
                    </p>
                  )}
                  {!statusMismatch && loginBlockedByIsActive && (
                    <p className="text-xs text-slate-500">
                      is_active disattivato: l&apos;utente non può fare login e verrà sloggato se connesso.
                    </p>
                  )}
                  {!loginBlockedByIsActive && !loginBlockedByStatus && orgStatusRaw === "PAST_DUE" && (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      Pagamento ricorrente Stripe non riuscito: l&apos;organizzazione è in PAST_DUE.
                      Se Stripe cancella l&apos;abbonamento, gli utenti passano a status INACTIVE e il login si blocca.
                    </p>
                  )}
                  {loginBlockedByOrgCanceled && (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      Abbonamento organizzazione cancellato (CANCELED): il login è bloccato.
                      Abilita &quot;Accesso forzato&quot; per consentire l&apos;accesso temporaneo.
                    </p>
                  )}
                  {orgStatusRaw === "CANCELED" && loginAccessOverride && (
                    <p className="text-xs text-slate-500">
                      Org ancora CANCELED in fatturazione; l&apos;override consente solo il login di questo utente.
                    </p>
                  )}
                </div>
              </AdminDetailSection>
            </div>

            <div className="space-y-5">
              <AdminDetailSection icon={Building2} iconWrapClass="bg-teal-50 text-teal-700" title="Organizzazione">
                <div className="grid gap-4 sm:grid-cols-2">
                  <AdminDetailField label="Organizzazione">{user.facility_name || "—"}</AdminDetailField>
                  <AdminDetailField label="Comune">
                    <span className="inline-flex items-center gap-1.5">
                      {user.facility_comune ? <MapPin className="h-3.5 w-3.5 text-slate-400" /> : null}
                      {user.facility_comune || "—"}
                    </span>
                  </AdminDetailField>
                  <AdminDetailField label="Personale (abbonamento)" className="sm:col-span-2">
                    {recruitingLabel}
                  </AdminDetailField>
                </div>
                <div className="mt-4">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Strutture collegate ({strutture.length})
                  </p>
                  {strutture.length ? (
                    <ul className="flex flex-wrap gap-2">
                      {strutture.map((s, i) => (
                        <li
                          key={s.id || i}
                          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900"
                        >
                          <Building className="h-3.5 w-3.5 text-emerald-600" />
                          {s.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">Nessuna struttura collegata</p>
                  )}
                </div>
              </AdminDetailSection>

              <AdminDetailSection icon={CreditCard} iconWrapClass="bg-sky-50 text-sky-700" title="Abbonamento">
                <div className="space-y-3">
                  {hasOrganization ? (
                    <>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Select
                          value={planTierDraft}
                          onValueChange={setPlanTierDraft}
                          disabled={planTierSaving}
                        >
                          <SelectTrigger className="h-10 w-full text-sm sm:max-w-[240px]">
                            <SelectValue placeholder="Piano" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIER_OPTIONS.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="sm"
                          className="h-10 shrink-0 bg-indigo-600 hover:bg-indigo-700"
                          disabled={planTierSaving || planTierUnchanged}
                          onClick={handleSavePlanTier}
                        >
                          {planTierSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva piano"}
                        </Button>
                      </div>
                      <p className="text-sm text-slate-600">
                        Attuale:{" "}
                        <span className="font-semibold text-slate-900">{user.subscription_label || "—"}</span>
                        {user.org_max_strutture != null && (
                          <>
                            {" "}
                            · Limite strutture: {user.org_max_strutture}
                            {strutture.length > 0 && <> ({strutture.length} inserite)</>}
                          </>
                        )}
                      </p>
                      {(user.account_status === "trial_active" || user.account_status === "trial_expired") && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Stato trial</p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <Badge
                              className={
                                user.account_status === "trial_active"
                                  ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                                  : "border-orange-200 bg-orange-100 text-orange-800"
                              }
                            >
                              {user.account_status === "trial_active" ? "Trial attivo" : "Trial scaduto"}
                            </Badge>
                            {user.trial_expires_at && (
                              <span className="text-xs text-slate-500">
                                Scadenza:{" "}
                                {new Date(user.trial_expires_at).toLocaleDateString("it-IT", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            )}
                          </div>
                          {user.trial_started_at && (
                            <p className="mt-1 text-xs text-slate-500">
                              Inizio:{" "}
                              {new Date(user.trial_started_at).toLocaleDateString("it-IT", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </p>
                          )}
                        </div>
                      )}
                      <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        Modifica il piano dell&apos;organizzazione (tutti gli utenti della stessa org
                        condividono limite e fatturazione in app). Stripe non viene aggiornato automaticamente.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-900">
                      {user.subscription_label || "—"}{" "}
                      <span className="text-slate-500">(nessuna organizzazione collegata)</span>
                    </p>
                  )}
                </div>
              </AdminDetailSection>
            </div>
          </div>

          {pagamento ? (
            <AdminPagamentoCard
              pagamento={pagamento}
              refreshing={syncingStripe}
              onRefresh={handleSyncStripe}
            />
          ) : isBonifico ? (
            <AdminDetailSection icon={Landmark} iconWrapClass="bg-amber-50 text-amber-700" title="Pagamento">
              <p className="text-sm text-slate-700">
                Bonifico bancario
                {user.bank_transfer_expires_at && (
                  <span className="text-slate-500">
                    {" "}
                    · scade il{" "}
                    {new Date(user.bank_transfer_expires_at).toLocaleDateString("it-IT", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                )}
              </p>
            </AdminDetailSection>
          ) : null}

          <TermsAcceptanceAdminSection
            acceptance={user.terms_acceptance}
            history={user.terms_acceptance_history || []}
            currentVersion={user.current_contract_version}
            reacceptanceRequired={user.terms_reacceptance_required}
          />

          <AdminDetailSection icon={Activity} iconWrapClass="bg-violet-50 text-violet-700" title="Statistiche organizzazione">
            <p className="mb-4 text-xs text-slate-500">
              Dati inseriti e numeri relativi all&apos;organizzazione dell&apos;utente (nessun dato sensibile).
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {statCard("Fascicoli totali", stats.fascicoli ?? 0, "Ospiti in organizzazione")}
              {statCard("Creati da utente", stats.fascicoli_creati_da_utente ?? 0, "Fascicoli inseriti da questo utente")}
              {statCard("Utenti", stats.utenti_organizzazione ?? 0, "In organizzazione")}
              {statCard("OSS registrati", stats.oss ?? 0, "Account OSS")}
              {statCard("Camere", stats.camere ?? 0, "Configurate")}
              {statCard("Posti liberi", stats.posti_liberi ?? 0, "Dalle camere")}
              {statCard("Diario consegne", stats.diario_consegne ?? 0, "Note inserite")}
              {statCard("Diario OSS", stats.diario_oss ?? 0, "Note inserite")}
              {statCard("Tabelle dietetiche", stats.tabelle_dietetiche ?? 0, "Caricate")}
              {statCard("Documenti struttura", stats.documenti_struttura ?? 0, "Regolamento, carta servizi...")}
              {statCard("Ospiti diurni", stats.ospiti_diurni ?? 0, "Di questo utente")}
            </div>
          </AdminDetailSection>
        </div>
      </div>
    </div>
  );
}

// Google Maps API — utilizzo e costi stimati
function GoogleMapsUsageModule({ data, loading, onRefresh }) {
  const report = data["google-maps/usage"] || {};
  const [mese, setMese] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const handleMonthChange = (value) => {
    setMese(value);
    onRefresh?.(value);
  };

  const maxDaily = Math.max(1, ...(report.daily || []).map((d) => d.total || 0));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Google Maps Platform — Utilizzo API</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Monitoraggio chiamate Google Maps: Geocoding (Centro Diurno, matching richieste famiglie, farmacia)
            e Routes (Centro Diurno). Il raggio in km del matching è un calcolo locale gratuito. Stima costi sul
            listino Google (soglie gratuite: 10.000 Geocoding, 5.000 Routes Pro/mese).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={mese} onChange={(e) => handleMonthChange(e.target.value)} className="w-auto" />
          <Button type="button" variant="outline" size="sm" onClick={() => onRefresh?.(mese)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant={report.api_key_configured ? "default" : "destructive"}>
          {report.api_key_configured ? "API key configurata" : "GOOGLE_MAPS_API_KEY mancante"}
        </Badge>
        <Badge variant="secondary">Mese: {report.month || mese}</Badge>
        <Badge variant="outline" className="font-mono">
          Stima totale: ${report.total_estimated_cost_usd ?? "0.00"} USD
        </Badge>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {(report.skus || []).map((sku) => (
          <Card key={sku.sku}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{sku.label}</CardTitle>
              <p className="text-xs text-slate-500">
                {sku.category} · ${sku.price_per_1k_usd}/1.000 · gratis {sku.free_monthly?.toLocaleString()}/mese
              </p>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Chiamate riuscite</span>
                <span className="font-semibold">{sku.success_calls ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Fallite</span>
                <span>{sku.failed_calls ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Soglia gratis residua</span>
                <span className="text-emerald-700 font-medium">{sku.free_remaining?.toLocaleString() ?? "—"}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-slate-600">Costo stimato oltre soglia</span>
                <span className="font-bold">${sku.estimated_cost_usd ?? "0.0000"}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {report.pricing_note && (
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">{report.pricing_note}</p>
      )}

      {(report.free_operations || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Operazioni gratuite (nessun costo Google)</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-2 text-sm">
            {report.free_operations.map((op) => (
              <div key={op.id} className="border border-slate-100 rounded-lg px-3 py-2">
                <div className="font-medium text-slate-800">{op.label}</div>
                <div className="text-xs text-slate-500">{op.note}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(report.function_catalog || []).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-slate-900">Uso e costo per funzione</h3>
          <div className="grid lg:grid-cols-3 gap-4">
            {report.function_catalog.map((fn) => (
              <Card key={fn.source}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{fn.label}</CardTitle>
                  <p className="text-xs text-slate-500">{fn.description}</p>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-md bg-slate-50 px-2 py-1.5">
                      <div className="text-slate-500">Geocoding (OK)</div>
                      <div className="font-semibold">{fn.geocoding_success ?? 0}/{fn.geocoding ?? 0}</div>
                    </div>
                    <div className="rounded-md bg-slate-50 px-2 py-1.5">
                      <div className="text-slate-500">Routes Pro (OK)</div>
                      <div className="font-semibold">
                        {fn.routes_compute_pro_success ?? 0}/{fn.routes_compute_pro ?? 0}
                      </div>
                    </div>
                    <div className="rounded-md bg-slate-50 px-2 py-1.5">
                      <div className="text-slate-500">Listino (senza soglia)</div>
                      <div className="font-semibold">${Number(fn.list_price_usd || 0).toFixed(4)}</div>
                    </div>
                    <div className="rounded-md bg-emerald-50 px-2 py-1.5">
                      <div className="text-slate-500">Costo stimato mese</div>
                      <div className="font-semibold text-emerald-800">
                        ${Number(fn.estimated_cost_usd || 0).toFixed(4)}
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {(fn.operations || []).map((op) => (
                      <li key={op.id} className="border border-slate-100 rounded-md px-2 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-slate-800">{op.label}</span>
                          <Badge variant={op.billable ? "secondary" : "outline"} className="text-[10px]">
                            {op.billable ? "A pagamento" : "Gratis"}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{op.when}</p>
                        <p className="text-[11px] text-slate-700 mt-0.5">{op.cost}</p>
                      </li>
                    ))}
                  </ul>
                  {fn.cost_note && <p className="text-[10px] text-slate-400">{fn.cost_note}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Utilizzo per funzione</CardTitle>
          <p className="text-xs text-slate-500">
            Dove è stata usata l&apos;API questo mese. Il costo stimato è la quota della soglia gratuita
            condivisa; il listino è N × prezzo/1.000 senza sconto.
          </p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-slate-500">Funzione</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Geocoding</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Routes Pro</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">OK</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Errori</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Listino</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Costo stimato</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Totale</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_source || []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Nessuna chiamata registrata in questo mese
                  </td>
                </tr>
              ) : (
                report.by_source.map((row) => (
                  <tr key={row.source} className="border-b border-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium">{row.label || row.source}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{row.source}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{row.geocoding ?? 0}</td>
                    <td className="px-4 py-2 text-right">{row.routes_compute_pro ?? 0}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{row.success ?? 0}</td>
                    <td className="px-4 py-2 text-right text-red-600">{row.failed ?? 0}</td>
                    <td className="px-4 py-2 text-right">${Number(row.list_price_usd || 0).toFixed(4)}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      ${Number(row.estimated_cost_usd || 0).toFixed(4)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{row.total ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Utilizzo per organizzazione</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-slate-500">Organizzazione</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Geocoding</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Routes Pro</th>
                <th className="text-right px-4 py-2 text-xs text-slate-500">Totale</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_organization || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    Nessuna chiamata registrata in questo mese
                  </td>
                </tr>
              ) : (
                report.by_organization.map((row) => (
                  <tr key={row.organization_id} className="border-b border-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium">{row.organization_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{row.organization_id}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{row.geocoding ?? 0}</td>
                    <td className="px-4 py-2 text-right">{row.routes_compute_pro ?? 0}</td>
                    <td className="px-4 py-2 text-right font-semibold">{row.total ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Andamento giornaliero</CardTitle>
        </CardHeader>
        <CardContent>
          {(report.daily || []).length === 0 ? (
            <p className="text-sm text-slate-500">Nessun dato</p>
          ) : (
            <div className="space-y-2">
              {report.daily.map((d) => (
                <div key={d.date} className="flex items-center gap-3 text-xs">
                  <span className="w-24 text-slate-500 shrink-0">{d.date.slice(5)}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded"
                      style={{ width: `${Math.max(4, (d.total / maxDaily) * 100)}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-medium">{d.total}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ultime chiamate</CardTitle>
        </CardHeader>
        <CardContent className="p-0 max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Ora</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Funzione</th>
                <th className="text-left px-3 py-2">Dettaglio</th>
                <th className="text-left px-3 py-2">Esito</th>
              </tr>
            </thead>
            <tbody>
              {(report.recent_calls || []).map((c) => (
                <tr key={c._id || c.ts} className="border-b border-slate-50">
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">
                    {c.ts ? new Date(c.ts).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-3 py-1.5">{c.sku}</td>
                  <td className="px-3 py-1.5">
                    {c.source === "lead_matching"
                      ? "Matching famiglie"
                      : c.source === "centro_diurno"
                        ? "Centro Diurno"
                        : c.source === "farmacia"
                          ? "Farmacia"
                          : c.source || "—"}
                  </td>
                  <td className="px-3 py-1.5 truncate max-w-[220px]" title={c.metadata?.address_preview || ""}>
                    {c.metadata?.purpose ? `${c.metadata.purpose}: ` : ""}
                    {c.metadata?.address_preview || c.organization_id || "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {c.success ? (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">OK</Badge>
                    ) : (
                      <Badge variant="destructive">{c.error || "Errore"}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// Servizi Module — panoramica abbonamenti e moduli per organizzazione (dati reali da MongoDB)
function ServiziModule({ data, loading, onRefresh }) {
  const serviziData = data.servizi || {};
  const rows = serviziData.organizations || [];
  const legacyCount = serviziData.legacy_servizi_config_count ?? 0;
  const [togglingOrg, setTogglingOrg] = useState(null);

  const toggleCentroDiurno = async (organizationId, enabled) => {
    setTogglingOrg(organizationId);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/servizi`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          organization_id: organizationId,
          servizi: {
            centro_diurno: { enabled, name: "Centro Diurno" },
          },
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json.detail || "Errore aggiornamento modulo");
        return;
      }
      toast.success(enabled ? "Centro Diurno attivato" : "Centro Diurno disattivato");
      onRefresh?.();
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setTogglingOrg(null);
    }
  };

  const billingIntervalLabel = (v) => {
    if (v === "monthly") return "Mensile";
    if (v === "yearly") return "Annuale";
    return v && v !== "—" ? String(v) : "—";
  };

  const recruitingLabel = (row) => {
    const parts = [];
    if (row.recruiting_users_pro > 0) parts.push(`${row.recruiting_users_pro} Pro`);
    if (row.recruiting_users_base > 0) parts.push(`${row.recruiting_users_base} Base`);
    const none = row.recruiting_users_none ?? 0;
    if (none > 0) parts.push(`${none} senza piano`);
    return parts.length ? parts.join(" · ") : "Nessun utente in org";
  };

  const deblisteringLabel = (d) => {
    if (!d?.configured) {
      return d?.ospiti_attivi > 0 ? `Non in config · ${d.ospiti_attivi} ospiti (chiave legacy?)` : "—";
    }
    const prezzo =
      d.prezzo_blisteraggio != null && d.prezzo_blisteraggio !== ""
        ? `€${Number(d.prezzo_blisteraggio)}`
        : "—";
    const n = d.ospiti_attivi ?? 0;
    return `Sì (${prezzo}) · ${n} ospiti attivi`;
  };

  const legacyTogglesSummary = (toggles) => {
    if (!toggles || typeof toggles !== "object") return null;
    const on = Object.entries(toggles)
      .filter(([, v]) => v && v.enabled)
      .map(([k]) => k);
    if (!on.length) return "Nessun modulo legacy attivo";
    return on.join(", ");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Servizi e abbonamenti per organizzazione</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Riepilogo di piano Affidya (tier e fatturazione), piano Personale (base/pro per utente),
            deblistering, ordini &quot;Risparmio insieme&quot; (conteggio per struttura collegata).
            Le adesioni Zoho ai gruppi acquisto non sono elencate qui: restano su Zoho.
          </p>
          {legacyCount > 0 && (
            <p className="text-xs text-amber-800 mt-2">
              In database sono presenti {legacyCount} record in{" "}
              <code className="text-[11px]">servizi_config</code> (toggle moduli legacy): se valorizzati,
              compaiono nella colonna ultima.
            </p>
          )}
        </div>
        <Button type="button" onClick={onRefresh} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Organizzazione
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Comune
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Piano Affidya
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fatturazione
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Personale
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Deblistering
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ordini gruppo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Centro Diurno
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Legacy
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                      Nessuna organizzazione trovata.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.organization_id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">
                        {row.organization_name}
                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">{row.organization_id}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{row.comune}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant="secondary" className="font-normal">
                          {row.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <span className="font-medium">{row.plan_tier_label}</span>
                        <div className="text-xs text-slate-500">max {row.max_strutture} strutture</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {billingIntervalLabel(row.billing_interval)}
                        <div className="text-xs text-slate-500">{row.billing_backend_note}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-[200px]">{recruitingLabel(row)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-[220px]">
                        {deblisteringLabel(row.deblistering)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                        {row.risparmio_ordini_count ?? 0}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Switch
                          checked={Boolean(row.centro_diurno_enabled)}
                          disabled={togglingOrg === row.organization_id}
                          onCheckedChange={(checked) => toggleCentroDiurno(row.organization_id, checked)}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[180px]">
                        {legacyTogglesSummary(row.legacy_servizi_toggles) || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RisparmioListinoModule({ data, loading, onRefresh }) {
  const listino = data["risparmio/listino"] || {};
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editCategory, setEditCategory] = useState("");
  const [editTiers, setEditTiers] = useState([]);
  const [selectedImageFilename, setSelectedImageFilename] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("super_admin_token");
  const API = process.env.REACT_APP_BACKEND_URL;

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return iso;
    }
  };

  const downloadFile = async (path, fallbackName) => {
    try {
      const res = await fetch(`${API}/api/admin/risparmio/listino/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download non riuscito");
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const filename = match?.[1] || fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Seleziona un file .xlsx o .pdf");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      const res = await fetch(`${API}/api/admin/risparmio/listino/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || "Upload non riuscito");
      }
      toast.success(json.message || "Listino aggiornato");
      if (json.parse_method === "ai") {
        toast.info("Import completato tramite OpenAI — verifica i costi in Intelligenza Artificiale");
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const products = listino.products || listino.products_preview || [];
  const uploadedImages = listino.available_images || [];

  // Immagini statiche già presenti in public/images/products (servite dal frontend)
  const STATIC_PRODUCT_IMAGES = [
    { filename: "/images/products/pannoloni.jpeg", label: "Pannoloni" },
    { filename: "/images/products/manopole.jpeg", label: "Manopole" },
    { filename: "/images/products/traverse.jpeg", label: "Traverse" },
    { filename: "/images/products/bavagli.jpeg", label: "Bavagli" },
    { filename: "/images/products/guanti-nitrile.jpeg", label: "Guanti nitrile" },
    { filename: "/images/products/candeggina.jpeg", label: "Candeggina" },
    { filename: "/images/products/saniotec.jpeg", label: "Saniotec" },
    { filename: "/images/products/tovaglioli.jpeg", label: "Tovaglioli" },
    { filename: "/images/products/carta-igienica.jpeg", label: "Carta igienica" },
    { filename: "/images/products/detersivo-lemon.jpeg", label: "Detersivo lemon" },
    { filename: "/images/products/bicchieri.jpeg", label: "Bicchieri" },
    { filename: "/images/products/siringhe.jpeg", label: "Siringhe" },
    { filename: "/images/products/rotoloni.jpeg", label: "Rotoloni" },
    { filename: "/images/products/default.jpeg", label: "Generica" },
  ];

  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // Path statici (/images/products/...) sono serviti direttamente dal frontend
    if (url.startsWith("/images/")) return url;
    return `${API}${url}`;
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setEditCategory(product.category || "");
    setEditTiers(
      (product.tiers || []).map((t) => ({
        sku: t.sku,
        pack_qty: t.pack_qty,
        pack_unit: t.pack_unit,
        unit_price: String(t.unit_price ?? ""),
        notes: t.notes || "",
      }))
    );
    // image_filename può essere un path statico (/images/products/...) o un filename del backend
    setSelectedImageFilename(product.image_filename || null);
  };

  const closeEditProduct = () => {
    setEditingProduct(null);
    setEditCategory("");
    setEditTiers([]);
    setSelectedImageFilename(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleTierPriceChange = (sku, value) => {
    setEditTiers((prev) =>
      prev.map((t) => (t.sku === sku ? { ...t, unit_price: value } : t))
    );
  };

  const handleUploadProductImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/api/admin/risparmio/listino/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || "Upload immagine non riuscito");
      }
      // Salva il filename backend (non il path statico)
      setSelectedImageFilename(json.filename);
      toast.success("Immagine caricata");
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || "Errore upload immagine");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      const tiers = editTiers.map((t) => ({
        sku: t.sku,
        unit_price: parseFloat(String(t.unit_price).replace(",", ".")),
        notes: t.notes || undefined,
      }));
      if (tiers.some((t) => Number.isNaN(t.unit_price))) {
        throw new Error("Inserisci prezzi validi per tutte le varianti");
      }
      const body = {
        product_key: editingProduct.product_key || editingProduct.name,
        category: editCategory.trim() || undefined,
        tiers,
      };
      if (selectedImageFilename) {
        body.image_filename = selectedImageFilename;
      } else if (editingProduct.image_filename) {
        body.clear_image = true;
      }
      const res = await fetch(`${API}/api/admin/risparmio/listino/products`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || "Salvataggio non riuscito");
      }
      toast.success("Prodotto aggiornato");
      closeEditProduct();
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || "Errore durante il salvataggio");
    } finally {
      setSavingProduct(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Listino Risparmio insieme</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Gestisci il catalogo prodotti e i prezzi mostrati in{" "}
            <strong>Business → Risparmio insieme</strong>. Carica Excel (.xlsx), CSV o PDF.
            Colonne supportate: <strong>Categoria</strong>, <strong>Prodotto</strong>,{" "}
            <strong>Confezione</strong>, <strong>Prezzo (€)</strong> (anche su fogli oltre al primo).
            In alternativa: sku, name, category, pack_qty, unit_price. Puoi anche modificare singoli
            prodotti (prezzi, categoria e foto) dal catalogo sotto. Se il formato non è riconosciuto,
            con AI attiva in <strong>Intelligenza Artificiale → Listino Risparmio insieme</strong> viene
            usato OpenAI come fallback.
          </p>
        </div>
        <Button type="button" onClick={onRefresh} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Stato listino</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>
              <span className="text-slate-500">Prodotti:</span>{" "}
              <strong>{listino.row_count ?? "—"}</strong>
            </p>
            <p>
              <span className="text-slate-500">Varianti/SKU:</span>{" "}
              <strong>{listino.variant_count ?? "—"}</strong>
            </p>
            <p>
              <span className="text-slate-500">Ultimo aggiornamento:</span>{" "}
              {formatDate(listino.last_updated)}
            </p>
            {listino.uploaded_by && (
              <p>
                <span className="text-slate-500">Caricato da:</span> {listino.uploaded_by}
              </p>
            )}
            {listino.source_filename && (
              <p className="truncate" title={listino.source_filename}>
                <span className="text-slate-500">File sorgente:</span> {listino.source_filename}
                {listino.source_type ? ` (${listino.source_type.toUpperCase()})` : ""}
              </p>
            )}
            {listino.parse_method === "ai" && (
              <p className="text-violet-700 text-xs mt-1">
                Ultimo import elaborato con OpenAI (costi in Admin → Intelligenza Artificiale)
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Fornitore</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="font-medium">{listino.supplier?.supplier_name || "—"}</p>
            <p className="text-slate-500">{listino.supplier?.supplier_email || ""}</p>
            <p className="text-slate-500">{listino.supplier?.shipping_note || ""}</p>
            <p className="text-slate-500">Prezzi {listino.supplier?.vat === "EXCL" ? "IVA esclusa" : ""}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Categorie</CardTitle>
          </CardHeader>
          <CardContent>
            {(listino.categories || []).length ? (
              <div className="flex flex-wrap gap-1.5">
                {listino.categories.map((cat) => (
                  <Badge key={cat} variant="secondary" className="text-xs">
                    {cat}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">—</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Aggiorna listino</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="risparmio-listino-file">File listino (.xlsx, .csv o .pdf)</Label>
              <Input
                id="risparmio-listino-file"
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/pdf"
                className="mt-1.5"
                disabled={uploading}
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              {selectedFile && (
                <p className="text-xs text-slate-500 mt-1">{selectedFile.name}</p>
              )}
            </div>
            <Button type="button" onClick={handleUpload} disabled={uploading || !selectedFile}>
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Carica e applica
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!listino.csv_exists}
              onClick={() => downloadFile("download", "price_list.csv")}
            >
              <Download className="h-4 w-4 mr-2" />
              Scarica CSV corrente
            </Button>
            {listino.has_source_file && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadFile("source", listino.source_filename || "listino")}
              >
                <Download className="h-4 w-4 mr-2" />
                Scarica file sorgente
              </Button>
            )}
          </div>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            L&apos;upload sostituisce l&apos;intero listino. Viene creato un backup automatico del CSV
            precedente. Per i PDF senza tabella testuale usa preferibilmente Excel.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Catalogo prodotti ({products.length})</CardTitle>
          <p className="text-xs text-slate-500">Clicca Modifica per prezzi e foto</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[min(70vh,900px)] overflow-y-auto">
            <table className="w-full min-w-[860px]">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase w-16">Foto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Prodotto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Categoria</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Prezzo base</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sconto max</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Varianti</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-28">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading && !products.length ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                      Caricamento...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      Nessun prodotto nel listino.
                    </td>
                  </tr>
                ) : (
                  products.map((p) => (
                    <tr key={p.product_key || p.name} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        {p.image_url ? (
                          <img
                            src={resolveImageUrl(p.image_url)}
                            alt=""
                            className="h-10 w-10 rounded object-cover border border-slate-200 bg-slate-50"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center">
                            <ImageIcon className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{p.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{p.category}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        € {Number(p.base_price || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {Number(p.max_discount || 0).toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{p.tiers?.length || 0}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openEditProduct(p)}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Modifica
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editingProduct} onOpenChange={(open) => !open && closeEditProduct()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica prodotto</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-6">
              <div>
                <p className="text-sm font-medium text-slate-900">{editingProduct.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Aggiorna categoria, prezzi per confezione e immagine del catalogo.
                </p>
              </div>

              <div>
                <Label htmlFor="risparmio-edit-category">Categoria</Label>
                <Input
                  id="risparmio-edit-category"
                  className="mt-1.5"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                />
              </div>

              <div>
                <Label className="mb-2 block">Prezzi listino (per variante/confezione)</Label>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-slate-500">SKU</th>
                        <th className="px-3 py-2 text-left text-xs text-slate-500">Confezione</th>
                        <th className="px-3 py-2 text-left text-xs text-slate-500">Prezzo unitario (€)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {editTiers.map((t) => (
                        <tr key={t.sku}>
                          <td className="px-3 py-2 text-slate-600 font-mono text-xs">{t.sku}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {t.pack_qty} {t.pack_unit}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="text"
                              inputMode="decimal"
                              className="h-8 max-w-[120px]"
                              value={t.unit_price}
                              onChange={(e) => handleTierPriceChange(t.sku, e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Immagine prodotto</Label>
                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    {selectedImageFilename ? (
                      <img
                        src={resolveImageUrl(
                          selectedImageFilename.startsWith("/images/")
                            ? selectedImageFilename
                            : uploadedImages.find((img) => img.filename === selectedImageFilename)?.url
                              || `/api/risparmio/product-images/${selectedImageFilename}`
                        )}
                        alt=""
                        className="h-24 w-24 rounded-lg object-cover border border-slate-200"
                      />
                    ) : (
                      <div className="h-24 w-24 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3 min-w-0">
                    <p className="text-xs text-slate-500">
                      Seleziona un&apos;immagine dal catalogo o caricane una nuova (JPG, PNG, WEBP, GIF — max 5 MB).
                    </p>

                    {/* Immagini statiche del frontend */}
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1.5">Catalogo immagini</p>
                      <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto p-1 bg-slate-50 rounded border">
                        {STATIC_PRODUCT_IMAGES.map((img) => (
                          <button
                            key={img.filename}
                            type="button"
                            title={img.label}
                            onClick={() => setSelectedImageFilename(img.filename)}
                            className={`relative rounded-md border-2 overflow-hidden h-14 w-14 shrink-0 ${
                              selectedImageFilename === img.filename
                                ? "border-blue-500 ring-2 ring-blue-200"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <img
                              src={img.filename}
                              alt={img.label}
                              className="h-full w-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Immagini caricate dinamicamente */}
                    {uploadedImages.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1.5">Immagini caricate</p>
                        <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto p-1 bg-slate-50 rounded border">
                          {uploadedImages.map((img) => (
                            <button
                              key={img.filename}
                              type="button"
                              title={img.used_by?.length ? `Usata da: ${img.used_by.join(", ")}` : img.filename}
                              onClick={() => setSelectedImageFilename(img.filename)}
                              className={`relative rounded-md border-2 overflow-hidden h-14 w-14 shrink-0 ${
                                selectedImageFilename === img.filename
                                  ? "border-blue-500 ring-2 ring-blue-200"
                                  : "border-slate-200 hover:border-slate-300"
                              }`}
                            >
                              <img
                                src={resolveImageUrl(img.url)}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Upload nuova */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        ref={imageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="max-w-xs"
                        disabled={uploadingImage}
                        onChange={(e) => handleUploadProductImage(e.target.files?.[0] || null)}
                      />
                      {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                      {selectedImageFilename && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedImageFilename(null)}
                        >
                          Rimuovi foto
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeEditProduct} disabled={savingProduct}>
              Annulla
            </Button>
            <Button type="button" onClick={handleSaveProduct} disabled={savingProduct || uploadingImage}>
              {savingProduct ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Strutture Module – tutte le strutture (anche multi) con utenti visibili e dialog dettaglio
function StruttureModule({ data, loading, onRefresh }) {
  const struttureData = data["strutture"] || { strutture: [] };
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name || id}"? Questa azione non può essere annullata.`)) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/strutture/${id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json.detail || "Impossibile eliminare la struttura");
        return;
      }
      toast.success(json.message || "Eliminata");
      if (detailId === id) closeDetail();
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || "Errore durante l'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const fetchDetail = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setDetail(null);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/strutture/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setDetail(await res.json());
      else setDetail({ error: "Dettaglio non disponibile" });
    } catch (e) {
      setDetail({ error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (detailId) fetchDetail(detailId);
  }, [detailId]);

  const openDetail = (id) => {
    setDetailId(id);
  };
  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Gestione Strutture</h2>
        {onRefresh && (
          <Button onClick={onRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500">
        Tutte le sedi (anche multi-struttura). Clicca su una riga per aprire i dettagli.
      </p>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Struttura</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Organizzazione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data registrazione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utenti</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Primary</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Statistiche</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {!struttureData.strutture?.length ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Nessuna struttura trovata
                    </td>
                  </tr>
                ) : (
                struttureData.strutture.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => openDetail(s.id)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{s.name}</div>
                      {(s.comune || s.tipoStruttura) && (
                        <div className="text-xs text-slate-500">
                          {[s.tipoStruttura, s.comune].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.organizationName || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {s.dataRegistrazione
                        ? new Date(s.dataRegistrazione).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {s.users?.length ? (
                        <div className="space-y-0.5 max-w-[220px]">
                          {s.users.slice(0, 3).map((u) => (
                            <div key={u.id} className="text-slate-700 truncate" title={`${u.nome} (${u.email})`}>
                              {u.nome} <span className="text-slate-400">({u.email})</span>
                            </div>
                          ))}
                          {s.users.length > 3 && (
                            <div className="text-xs text-slate-500">+{s.users.length - 3} altri</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.is_primary ? (
                        <Badge variant="default">Primaria</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <span className="font-medium">{s.stats?.users ?? 0}</span> utenti ·{" "}
                      <span className="font-medium">{s.stats?.fascicoli ?? 0}</span> fascicoli
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {(s.stats?.users ?? 0) === 0 ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={deletingId === s.id}
                          onClick={() => handleDelete(s.id, s.name)}
                          title="Elimina struttura (nessun utente collegato)"
                        >
                          {deletingId === s.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detailId} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-green-500" />
              Dettaglio struttura
            </DialogTitle>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}
          {!detailLoading && detail && (
            <StrutturaDetailContent
              detail={detail}
              onClose={closeDetail}
              onDeleteRequest={handleDelete}
              deletingId={deletingId}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StrutturaDetailContent({ detail, onClose, onDeleteRequest, deletingId }) {
  if (detail.error) {
    return <p className="text-red-600 py-4">{detail.error}</p>;
  }
  // Per organizzazioni senza sedi in db.strutture (es. 1 struttura) il dettaglio è l'org stessa
  const org = detail.organization || (detail.organizationName ? detail : {});
  const users = detail.users || [];
  const stats = detail.stats || {};
  const structureName = detail.name || detail.organizationName;
  const canDelete = (stats.users ?? users.length) === 0;

  const field = (label, value) => (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900">{value ?? "—"}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 border-b pb-1">Dati struttura</h3>
        <div className="space-y-0">
          {field("Nome", structureName)}
          {field("Data registrazione", detail.dataRegistrazione || detail.createdAt || detail.created_at
            ? new Date(detail.dataRegistrazione || detail.createdAt || detail.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })
            : "—")}
          {field("Tipo", detail.tipoStruttura)}
          {field("Comune", detail.comune)}
          {field("Provincia", detail.provincia)}
          {field("Via", [detail.via, detail.numeroCivico].filter(Boolean).join(" "))}
          {field("Telefono", detail.telefonoStruttura)}
          {field("Posti totali", detail.postiTotali)}
          {field("Tariffa doppia WC", detail.tariffaDoppiaWc)}
          {field("Tariffa singola WC", detail.tariffaSingolaWc)}
          {field("Sito web", detail.sitoWeb)}
          {field("PEC", detail.pec)}
          {field("P.IVA", detail.partitaIva)}
          {field("SDI", detail.sdi)}
          {field("Zoho Record ID", detail.zohoRecordId)}
          {field("Primaria", detail.is_primary ? "Sì" : "No")}
        </div>
      </div>

      {org && (org.id || org.organizationName) && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2 border-b pb-1">Organizzazione</h3>
          <div className="space-y-0">
            {field("Nome", org.organizationName || org.nome)}
            {field("Comune", org.comune)}
            {field("Referente", org.nomeReferente)}
            {field("Telefono referente", org.telefonoReferente)}
          </div>
        </div>
      )}

      <TermsAcceptanceAdminSection
        acceptance={detail.terms_acceptance}
        history={detail.terms_acceptance_history || []}
        currentVersion={detail.current_contract_version}
        reacceptanceRequired={
          !detail.terms_acceptance ||
          (detail.current_contract_version &&
            detail.terms_acceptance?.contract_version !== detail.current_contract_version)
        }
      />

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 border-b pb-1">
          Utenti collegati ({users.length}) · Fascicoli: {stats.fascicoli ?? 0}
        </h3>
        {users.length ? (
          <ul className="space-y-2 text-sm">
            {users.map((u) => (
              <li key={u.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50">
                <span className="font-medium text-slate-900">{u.nome}</span>
                <span className="text-slate-500">{u.email}</span>
                <Badge variant={u.is_active !== false ? "default" : "secondary"}>{u.role}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500 text-sm">Nessun utente collegato</p>
        )}
      </div>

      {canDelete && onDeleteRequest && (
        <div className="pt-4 border-t flex justify-end">
          <Button
            variant="destructive"
            size="sm"
            disabled={deletingId === detail.id}
            onClick={() => onDeleteRequest(detail.id, structureName)}
          >
            {deletingId === detail.id ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Elimina struttura
          </Button>
        </div>
      )}
    </div>
  );
}

// Audit Log Module – Accessi (login/logout) + Operazioni (tutte) per monitorare l'utilizzo
const AUDIT_SECTIONS = ["anagrafica", "amministrativa", "sociale", "sanitaria", "pai", "valutazioni", "documenti", "diario", "auth", "export", "download", "users"];
const AUDIT_ACTIONS = ["create", "update", "delete", "view", "export", "archive", "restore", "login", "logout", "login_failed", "download", "upload"];

const THREAT_LABELS = {
  brute_force: "Brute force",
  port_scan: "Scansione porte",
  sql_injection: "SQL injection",
  xss: "XSS",
  directory_traversal: "Directory traversal",
  exploit_scanner: "Exploit scanner",
};

const SEVERITY_BADGE = {
  critical: "destructive",
  high: "destructive",
  medium: "secondary",
  low: "outline",
};

const STATUS_CONFIG = {
  secure: { label: "Sistema sicuro", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  warning: { label: "Attività sospette", color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  critical: { label: "Minacce attive", color: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert },
};

const LOG_SOURCE_LABELS = {
  apache_access: "Apache access",
  apache_error: "Apache error",
  syslog: "Syslog",
  auth: "Auth.log",
  mongodb: "MongoDB",
  fastapi: "FastAPI",
  ssh: "SSH",
  fail2ban: "Fail2ban",
  firewall: "Firewall",
  php_error: "PHP error",
  admin_panel: "Pannello admin",
};

function SecurityMonitorModule() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [togglingMonitor, setTogglingMonitor] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/security/dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );
      clearTimeout(timeoutId);
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setDashboard(json);
    } catch (e) {
      const msg = e.name === "AbortError"
        ? "Timeout: il server impiega troppo tempo. Riprova o usa «Raccogli log» separatamente."
        : e.message;
      toast.error(`Errore caricamento sicurezza: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleMonitoring = async (enabled) => {
    setTogglingMonitor(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/security/config`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      toast.success(enabled ? "Monitoraggio sicurezza attivato" : "Monitoraggio sicurezza disattivato");
      await fetchDashboard();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    } finally {
      setTogglingMonitor(false);
    }
  };

  const triggerIngest = async () => {
    setIngesting(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/security/ingest`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error("Ingest fallito");
      toast.success("Raccolta log completata (elaborazione a blocchi se i log sono grandi)");
      await fetchDashboard();
    } catch (e) {
      toast.error(e.name === "AbortError" ? "Raccolta log interrotta per timeout" : "Errore raccolta log");
    } finally {
      setIngesting(false);
    }
  };

  const acknowledgeThreat = async (threatId) => {
    try {
      const token = localStorage.getItem("super_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/security/threats/${threatId}/acknowledge`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success("Minaccia segnata come gestita");
        fetchDashboard();
      }
    } catch (e) {
      toast.error("Errore aggiornamento minaccia");
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading && !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        <p className="text-sm text-slate-500">Caricamento dashboard sicurezza…</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="space-y-4">
        <p className="text-slate-600">Impossibile caricare il monitor sicurezza.</p>
        <Button onClick={fetchDashboard} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Riprova
        </Button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[dashboard.overall_status] || STATUS_CONFIG.warning;
  const StatusIcon = statusCfg.icon;
  const summary = dashboard.summary || {};
  const monitoringEnabled = dashboard.monitoring_enabled !== false;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Monitor sicurezza</h2>
          <p className="text-sm text-slate-500 mt-1">
            Raccolta log Apache, sistema, MongoDB, SSH, fail2ban e analisi minacce
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
            <Label htmlFor="security-monitor-toggle" className="text-sm text-slate-700 cursor-pointer">
              Monitoraggio {monitoringEnabled ? "attivo" : "disattivato"}
            </Label>
            <Switch
              id="security-monitor-toggle"
              checked={monitoringEnabled}
              disabled={togglingMonitor || loading}
              onCheckedChange={toggleMonitoring}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={triggerIngest}
            disabled={!monitoringEnabled || ingesting || loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${ingesting ? "animate-spin" : ""}`} />
            Raccogli log
          </Button>
          <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {!monitoringEnabled && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            Il monitoraggio è <strong>disattivato</strong>: nessuna raccolta log, analisi HTTP o job schedulato.
            I dati già presenti restano visibili. Attiva l’interruttore per riprendere il monitoraggio.
            {dashboard.monitoring_updated_by && (
              <span className="block mt-1 text-amber-800/80">
                Ultima modifica: {dashboard.monitoring_updated_by}
                {dashboard.monitoring_updated_at
                  ? ` · ${new Date(dashboard.monitoring_updated_at).toLocaleString("it-IT")}`
                  : ""}
              </span>
            )}
          </CardContent>
        </Card>
      )}

      <Card className={`border-2 ${monitoringEnabled ? statusCfg.color : "bg-slate-100 text-slate-600 border-slate-200"}`}>
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <StatusIcon className="h-10 w-10 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-lg font-semibold">
              {monitoringEnabled ? statusCfg.label : "Monitoraggio in pausa"}
            </p>
            <p className="text-sm opacity-80">
              Campione: {dashboard.captured_at ? new Date(dashboard.captured_at).toLocaleString("it-IT") : "—"}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Badge variant="outline">{summary.open_threats ?? 0} minacce aperte</Badge>
            <Badge variant="outline">{summary.threats_24h ?? 0} ultime 24h</Badge>
            <Badge variant="outline">{summary.failed_logins_24h ?? 0} login falliti 24h</Badge>
            <Badge variant="outline">{summary.suspicious_ips_count ?? 0} IP sospetti</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" />
              Minacce rilevate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Dettaglio</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(dashboard.recent_threats || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        Nessuna minaccia rilevata negli ultimi 7 giorni
                      </td>
                    </tr>
                  ) : (
                    dashboard.recent_threats.map((t) => (
                      <tr key={t._id}>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                          {t.detected_at ? new Date(t.detected_at).toLocaleString("it-IT") : "—"}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant={SEVERITY_BADGE[t.severity] || "secondary"}>
                            {THREAT_LABELS[t.threat_type] || t.threat_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{t.source_ip || "—"}</td>
                        <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={t.details}>{t.details}</td>
                        <td className="px-4 py-2">
                          {t.status === "open" && (
                            <Button variant="ghost" size="sm" onClick={() => acknowledgeThreat(t._id)}>
                              Gestita
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-5 w-5 text-orange-600" />
              IP sospetti
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Eventi</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Tipi</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ultimo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(dashboard.suspicious_ips || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Nessun IP sospetto</td>
                    </tr>
                  ) : (
                    dashboard.suspicious_ips.map((ip) => (
                      <tr key={ip.ip}>
                        <td className="px-4 py-2 font-mono text-xs">{ip.ip}</td>
                        <td className="px-4 py-2">{ip.threat_count ?? 0}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(ip.threat_types || []).map((tt) => (
                              <Badge key={tt} variant="outline" className="text-[10px]">
                                {THREAT_LABELS[tt] || tt}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                          {ip.last_seen ? new Date(ip.last_seen).toLocaleString("it-IT") : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LogIn className="h-5 w-5 text-violet-600" />
              Accessi pannello admin
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Evento</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(dashboard.admin_access || []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-slate-500">Nessun accesso registrato</td>
                    </tr>
                  ) : (
                    dashboard.admin_access.map((log, i) => (
                      <tr key={log._id || i}>
                        <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString("it-IT") : "—"}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{log.parsed?.ip || "—"}</td>
                        <td className="px-4 py-2 text-slate-600 truncate max-w-md">{log.raw_message}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              Sorgenti log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {Object.entries(dashboard.log_sources || {}).map(([key, info]) => (
                <div key={key} className="py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-slate-700 font-medium">{LOG_SOURCE_LABELS[key] || key}</span>
                    {info.readable ? (
                      <Badge className="bg-emerald-100 text-emerald-800">OK</Badge>
                    ) : (
                      <Badge variant="secondary">Non disponibile</Badge>
                    )}
                  </div>
                  {(info.paths && info.paths.length > 0 ? info.paths : [{ path: info.path, readable: info.readable }]).map(
                    (fileInfo, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 pl-2 text-xs">
                        <code className="text-slate-500 break-all flex-1" title={fileInfo.path}>
                          {fileInfo.path || "—"}
                        </code>
                        {info.paths && (
                          fileInfo.readable ? (
                            <span className="text-emerald-600 shrink-0">leggibile</span>
                          ) : (
                            <span className="text-slate-400 shrink-0">mancante</span>
                          )
                        )}
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Vulnerabilità note
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(dashboard.known_vulnerabilities || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna vulnerabilità segnalata (pip audit / checklist)</p>
            ) : (
              <ul className="space-y-3">
                {dashboard.known_vulnerabilities.map((v, i) => (
                  <li key={i} className="text-sm border-l-2 border-amber-300 pl-3">
                    <span className="font-medium">{v.package}</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">{v.severity}</Badge>
                    <p className="text-slate-600 mt-0.5">{v.description}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-5 w-5 text-red-600" />
              Processi anomali
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(dashboard.anomalous_processes || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nessun processo anomalo rilevato</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 text-xs text-slate-500">PID</th>
                      <th className="text-left py-1 text-xs text-slate-500">User</th>
                      <th className="text-left py-1 text-xs text-slate-500">CPU</th>
                      <th className="text-left py-1 text-xs text-slate-500">RAM</th>
                      <th className="text-left py-1 text-xs text-slate-500">Cmd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.anomalous_processes.map((p) => (
                      <tr key={p.pid} className="border-b border-slate-50">
                        <td className="py-1">{p.pid}</td>
                        <td className="py-1">{p.user}</td>
                        <td className="py-1">{p.cpu_percent}%</td>
                        <td className="py-1">{p.mem_percent}%</td>
                        <td className="py-1 font-mono text-xs">{p.command}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Log recenti (24h)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sorgente</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Messaggio</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(dashboard.recent_logs || []).slice(0, 50).map((log, i) => (
                  <tr key={log._id || i}>
                    <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString("it-IT") : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="outline">{LOG_SOURCE_LABELS[log.source] || log.source}</Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{log.parsed?.ip || "—"}</td>
                    <td className="px-4 py-2 text-slate-600 truncate max-w-lg" title={log.raw_message}>
                      {log.raw_message?.slice(0, 120)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditModule({ data, loading }) {
  const auditData = data["audit-logs/auth"] || { logs: [] };
  const usersData = data["users"] || { users: [] };
  const usersList = usersData.users || [];
  const [viewMode, setViewMode] = useState("operations"); // "operations" | "auth"
  const [operationsLogs, setOperationsLogs] = useState([]);
  const [operationsTotal, setOperationsTotal] = useState(0);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [filterSection, setFilterSection] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [operationsSkip, setOperationsSkip] = useState(0);
  const limit = 100;

  const fetchOperations = useCallback(async (skip = 0) => {
    setOperationsLoading(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      if (filterSection) params.set("section", filterSection);
      if (filterAction) params.set("action", filterAction);
      if (filterUserId) params.set("user_id", filterUserId);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-logs/operations?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json().catch(() => ({}));
      setOperationsLogs(json.logs || []);
      setOperationsTotal(json.total ?? 0);
      setOperationsSkip(skip);
    } catch (e) {
      setOperationsLogs([]);
      setOperationsTotal(0);
    } finally {
      setOperationsLoading(false);
    }
  }, [filterSection, filterAction, filterUserId]);

  useEffect(() => {
    if (viewMode === "operations") fetchOperations(0);
  }, [viewMode, fetchOperations]);

  const handleFilterApply = () => {
    fetchOperations(0);
  };

  const truncateId = (id) => (id && id.length > 12 ? `${id.slice(0, 8)}…` : id || "—");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-xl font-semibold">Log operazioni utenti</h2>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "operations" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("operations")}
          >
            Operazioni (tutte)
          </Button>
          <Button
            variant={viewMode === "auth" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("auth")}
          >
            Solo accessi (login/logout)
          </Button>
        </div>
      </div>

      {viewMode === "operations" && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <span className="text-sm font-medium text-slate-600">Filtri:</span>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white min-w-[180px]"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
          >
            <option value="">Tutti gli utenti</option>
            {usersList.map((u) => (
              <option key={u.id} value={u.id}>{u.nome || u.email} ({u.email})</option>
            ))}
          </select>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
          >
            <option value="">Tutte le sezioni</option>
            {AUDIT_SECTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
          >
            <option value="">Tutte le azioni</option>
            {AUDIT_ACTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <Button variant="secondary" size="sm" onClick={handleFilterApply}>
            Applica
          </Button>
          <span className="text-sm text-slate-500">
            {operationsTotal} record totali
          </span>
        </div>
      )}

      {viewMode === "auth" && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b flex items-center justify-between">
              <span className="text-sm text-slate-600">Log di autenticazione (login/logout e tentativi falliti)</span>
              <Badge variant="outline">{auditData.logs?.length ?? 0} log</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data/Ora</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Azione</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dettagli</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">IP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {(auditData.logs || []).map((log) => (
                    <tr key={log._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString("it-IT") : "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{log.userName ?? "—"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={log.action === "login" ? "default" : log.action === "login_failed" ? "destructive" : "secondary"}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {log.section}
                        {log.extra?.success === false && (
                          <p className="text-red-600 text-xs">Errore: {log.extra?.failure_reason}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{log.ip || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {viewMode === "operations" && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b flex items-center justify-between">
              <span className="text-sm text-slate-600">Tutte le operazioni (fascicoli, documenti, export, download, accessi…)</span>
              <Badge variant="outline">{operationsTotal} record</Badge>
            </div>
            {operationsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data/Ora</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Sezione</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Azione</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fascicolo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">IP</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {operationsLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                          Nessun log trovato. Applica altri filtri o attendi che gli utenti eseguano operazioni.
                        </td>
                      </tr>
                    ) : (
                      operationsLogs.map((log) => (
                        <tr key={log._id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString("it-IT") : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900" title={log.userId}>
                            {log.userName ?? log.userId ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{log.section ?? "—"}</td>
                          <td className="px-4 py-3">
                            <Badge variant="secondary">{log.action ?? "—"}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 font-mono" title={log.fascicoloId}>
                            {truncateId(log.fascicoloId)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">{log.ip ?? "N/A"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {operationsTotal > limit && (
              <div className="p-3 border-t flex justify-between items-center">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={operationsSkip === 0 || operationsLoading}
                  onClick={() => fetchOperations(Math.max(0, operationsSkip - limit))}
                >
                  Precedenti
                </Button>
                <span className="text-sm text-slate-500">
                  {operationsSkip + 1}–{Math.min(operationsSkip + limit, operationsTotal)} di {operationsTotal}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={operationsSkip + limit >= operationsTotal || operationsLoading}
                  onClick={() => fetchOperations(operationsSkip + limit)}
                >
                  Successive
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Modulo Configurazione
function ConfigModule({ data, loading }) {
  const configData = useMemo(() => data.config || {}, [data.config]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Inizializza i dati di modifica quando i dati vengono caricati
  useEffect(() => {
    if (configData.config) {
      setEditData(configData.config);
    }
  }, [configData]);
  
  if (loading && !configData.config) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (configData.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-medium">Errore nel caricamento della configurazione</h3>
        <p className="text-red-600 text-sm mt-2">{configData.error}</p>
      </div>
    );
  }

  const { config = {}, sections = [] } = configData;

  // Gestisce la modifica di un valore
  const handleValueChange = (section, key, newValue) => {
    setEditData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: newValue
      }
    }));
  };

  // Salva la configurazione
  const handleSave = async () => {
    // Mostra conferma
    const confirmed = window.confirm(
      "⚠️ ATTENZIONE: Stai per modificare la configurazione del sistema.\n\n" +
      "Verrà creato un backup automatico del file .env.\n\n" +
      "Sei sicuro di voler continuare?"
    );
    
    if (!confirmed) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      
      // Prepara i dati per il backend
      const sectionsData = sections.map(section => ({
        section: section,
        parameters: editData[section] || {}
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sections: sectionsData
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`✅ ${result.message}`);
        toast.info(`📁 Backup creato: ${result.backup_file}`);
        setIsEditing(false);
        
        // Ricarica i dati aggiornati
        window.location.reload();
      } else {
        toast.error(`❌ ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(`Errore nel salvataggio: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Annulla le modifiche
  const handleCancel = () => {
    setEditData(config);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-500" />
              Configurazione Sistema
            </CardTitle>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    size="sm"
                    disabled={saving}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSave}
                    size="sm"
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Salvataggio...
                      </>
                    ) : (
                      "💾 Salva con Backup"
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setIsEditing(true)}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  ✏️ Abilita Modifica
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section} className="border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4 text-slate-800">{section}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(editData[section] || {}).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <label className="text-sm font-medium text-slate-600">{key}</label>
                      {isEditing ? (
                        <Input
                          value={value}
                          onChange={(e) => handleValueChange(section, key, e.target.value)}
                          className="font-mono text-sm"
                          placeholder={key}
                        />
                      ) : (
                        <div className="p-2 bg-slate-50 rounded border border-slate-200">
                          <code className="text-sm text-slate-700 break-all">{value}</code>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Informazioni sulla Sicurezza</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Tutti i parametri sono visibili in chiaro (accesso solo Super Admin)</li>
              <li>• Questa pagina è accessibile solo al Super Admin</li>
              <li>• Ogni modifica crea un backup automatico con timestamp</li>
              <li>• In caso di errore, il backup viene ripristinato automaticamente</li>
              <li>• In futuro verrà implementata l'autenticazione a due fattori</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MODULO COORDINATE BANCARIE ====================

function BankDetailsModule() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    intestatario: "",
    banca: "",
    iban: "",
    bic: "",
    indirizzo_banca: "",
    causale_template: "Abbonamento Affidya {plan_tier} {plan} - {organizationName}",
    note: "",
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  const token = localStorage.getItem("super_admin_token");
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/admin/bank-details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setForm({
              intestatario: data.intestatario || "",
              banca: data.banca || "",
              iban: data.iban || "",
              bic: data.bic || "",
              indirizzo_banca: data.indirizzo_banca || "",
              causale_template: data.causale_template || "Abbonamento Affidya {plan_tier} {plan} - {organizationName}",
              note: data.note || "",
            });
            setLastUpdated(data.updated_at || null);
          }
        }
      } catch (e) {
        toast.error("Errore nel caricamento delle coordinate bancarie");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, token]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.intestatario || !form.banca || !form.iban) {
      toast.error("Intestatario, Banca e IBAN sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/bank-details`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore salvataggio");
      setLastUpdated(data.data?.updated_at || new Date().toISOString());
      toast.success("Coordinate bancarie salvate con successo");
    } catch (e) {
      toast.error(e.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Landmark className="w-5 h-5 text-emerald-600" />
            Coordinate Bancarie per Bonifici
          </CardTitle>
          <p className="text-sm text-slate-500">
            Queste coordinate vengono incluse nell'email di conferma registrazione inviata agli utenti che scelgono il pagamento tramite bonifico.
            {lastUpdated && (
              <span className="ml-2 text-xs text-slate-400">
                Ultimo aggiornamento: {new Date(lastUpdated).toLocaleString("it-IT")}
              </span>
            )}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="intestatario">
                  Intestatario <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="intestatario"
                  value={form.intestatario}
                  onChange={(e) => handleChange("intestatario", e.target.value)}
                  placeholder="Affidya S.r.l."
                  required
                />
              </div>
              <div>
                <Label htmlFor="banca">
                  Banca <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="banca"
                  value={form.banca}
                  onChange={(e) => handleChange("banca", e.target.value)}
                  placeholder="Banca Sella"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="iban">
                  IBAN <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="iban"
                  value={form.iban}
                  onChange={(e) => handleChange("iban", e.target.value.toUpperCase())}
                  placeholder="IT60 X054 2811 1010 0000 0123 456"
                  required
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="bic">BIC / SWIFT</Label>
                <Input
                  id="bic"
                  value={form.bic}
                  onChange={(e) => handleChange("bic", e.target.value.toUpperCase())}
                  placeholder="SELBIT2BXXX"
                  className="font-mono"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="indirizzo_banca">Indirizzo Filiale</Label>
              <Input
                id="indirizzo_banca"
                value={form.indirizzo_banca}
                onChange={(e) => handleChange("indirizzo_banca", e.target.value)}
                placeholder="Via Roma 1, 20100 Milano (MI)"
              />
            </div>

            <div>
              <Label htmlFor="causale_template">
                Template Causale
              </Label>
              <Input
                id="causale_template"
                value={form.causale_template}
                onChange={(e) => handleChange("causale_template", e.target.value)}
                placeholder="Abbonamento Affidya {plan_tier} {plan} - {organizationName}"
              />
              <p className="text-xs text-slate-400 mt-1">
                Variabili disponibili:{" "}
                <code className="bg-slate-100 px-1 rounded">{"{plan_tier}"}</code> → es. "1 Struttura", "3 Strutture"{" "}
                &nbsp;·&nbsp;
                <code className="bg-slate-100 px-1 rounded">{"{plan}"}</code> → "Mensile" o "Annuale"{" "}
                &nbsp;·&nbsp;
                <code className="bg-slate-100 px-1 rounded">{"{organizationName}"}</code> → nome della struttura
              </p>
            </div>

            <div>
              <Label htmlFor="note">Note aggiuntive</Label>
              <Input
                id="note"
                value={form.note}
                onChange={(e) => handleChange("note", e.target.value)}
                placeholder="Specificare sempre la causale. Pagamenti accettati solo da conto intestato alla struttura."
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salva coordinate
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Preview email */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Anteprima sezione email</CardTitle>
          <p className="text-sm text-slate-500">
            Come appariranno le coordinate nell'email inviata all'utente che sceglie il bonifico.
          </p>
        </CardHeader>
        <CardContent>
          <div className="border border-slate-200 rounded-lg p-4 bg-white text-sm space-y-2">
            {form.iban ? (
              <table className="w-full border-collapse text-sm">
                <tbody>
                  <tr>
                    <td className="py-1.5 px-2 bg-emerald-50 font-semibold w-36">Intestatario</td>
                    <td className="py-1.5 px-2 bg-emerald-50">{form.intestatario || "—"}</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 px-2 font-semibold">Banca</td>
                    <td className="py-1.5 px-2">{form.banca || "—"}</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 px-2 bg-emerald-50 font-semibold">IBAN</td>
                    <td className="py-1.5 px-2 bg-emerald-50 font-mono">{form.iban || "—"}</td>
                  </tr>
                  {form.bic && (
                    <tr>
                      <td className="py-1.5 px-2 font-semibold">BIC/SWIFT</td>
                      <td className="py-1.5 px-2 font-mono">{form.bic}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-1.5 px-2 bg-emerald-50 font-semibold">Causale</td>
                    <td className="py-1.5 px-2 bg-emerald-50 italic">
                      {form.causale_template
                        .replace("{plan_tier}", "1 Struttura")
                        .replace("{plan}", "Mensile")
                        .replace("{organizationName}", "Casa di Riposo Esempio")}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-slate-400 italic text-center py-4">
                Compila i campi sopra per vedere l'anteprima
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MODULO ALLOGGIATI WEB ====================

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function AlloggiatiStruttura({ struttura }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("invii");

  const { stats, invii, ospiti, display_name, org_name, struttura_name, user_email } = struttura;

  const hasErrors = invii.some(i => !i.successo || i.num_schedine_errate > 0);

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className="cursor-pointer select-none py-3 px-4"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Hotel className="h-5 w-5 text-violet-500 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 leading-tight truncate">{display_name}</div>
              {(org_name || user_email) && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {org_name && <span>{org_name}</span>}
                  {org_name && user_email && <span className="mx-1">·</span>}
                  {user_email && <span className="font-mono">{user_email}</span>}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {hasErrors && (
              <Badge variant="destructive" className="text-xs gap-1">
                <AlertTriangle className="h-3 w-3" /> Errori
              </Badge>
            )}
            {stats.schedine_scadute > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs gap-1">
                <Clock className="h-3 w-3" /> {stats.schedine_scadute} da inviare
              </Badge>
            )}
            {stats.schedine_in_scadenza_48h > 0 && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs gap-1">
                <CalendarClock className="h-3 w-3" /> {stats.schedine_in_scadenza_48h} entro 48h
              </Badge>
            )}
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Send className="h-3.5 w-3.5" />{stats.total_invii} invii</span>
              <span className="flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" />{stats.ospiti_attivi} ospiti</span>
            </div>
            {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
        </div>

        {stats.last_invio && (
          <div className="text-xs text-slate-400 mt-1">
            Ultimo invio: {fmtDateTime(stats.last_invio)}
          </div>
        )}
      </CardHeader>

      {open && (
        <CardContent className="pt-0 px-0">
          {/* Tab switcher */}
          <div className="flex border-b border-slate-200 px-4">
            <button
              onClick={() => setTab("invii")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "invii" ? "border-violet-500 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              Storico invii ({invii.length})
            </button>
            <button
              onClick={() => setTab("ospiti")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "ospiti" ? "border-violet-500 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              Schedine / Prossimi invii ({ospiti.length})
            </button>
          </div>

          {tab === "invii" && (
            <div className="px-4 pt-3">
              {invii.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4 text-center">Nessun invio registrato</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-100">
                        <th className="text-left py-2 pr-4 font-medium">Data / ora</th>
                        <th className="text-left py-2 pr-4 font-medium">Tipo</th>
                        <th className="text-center py-2 pr-4 font-medium">Schedine</th>
                        <th className="text-center py-2 pr-4 font-medium">Corrette</th>
                        <th className="text-center py-2 pr-4 font-medium">Errate</th>
                        <th className="text-left py-2 font-medium">Stato / Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invii.map(inv => (
                        <tr key={inv.id} className={`border-b border-slate-50 hover:bg-slate-50 ${!inv.successo || inv.num_schedine_errate > 0 ? "bg-red-50/40" : ""}`}>
                          <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs text-slate-600">{fmtDateTime(inv.data)}</td>
                          <td className="py-2 pr-4">
                            {inv.automatico
                              ? <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-xs">Auto</Badge>
                              : <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">Manuale</Badge>
                            }
                          </td>
                          <td className="py-2 pr-4 text-center font-medium">{inv.num_schedine ?? "—"}</td>
                          <td className="py-2 pr-4 text-center">
                            <span className="text-green-700 font-medium">{inv.num_schedine_corrette ?? "—"}</span>
                          </td>
                          <td className="py-2 pr-4 text-center">
                            {inv.num_schedine_errate > 0
                              ? <span className="text-red-600 font-semibold">{inv.num_schedine_errate}</span>
                              : <span className="text-slate-300">0</span>
                            }
                          </td>
                          <td className="py-2">
                            {inv.successo
                              ? inv.num_schedine_errate > 0
                                ? <span className="text-amber-600 flex items-center gap-1 text-xs"><AlertTriangle className="h-3 w-3" /> Parziale</span>
                                : <span className="text-green-600 flex items-center gap-1 text-xs"><CheckCircle className="h-3 w-3" /> OK{inv.ricevuta_scaricata ? " · ricevuta" : ""}</span>
                              : <span className="text-red-600 flex items-center gap-1 text-xs" title={inv.errore}><XCircle className="h-3 w-3" />{inv.errore ? inv.errore.slice(0, 60) : "Errore"}</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "ospiti" && (
            <div className="px-4 pt-3">
              {ospiti.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4 text-center">Nessuna schedina attiva</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-100">
                        <th className="text-left py-2 pr-4 font-medium">Ospite</th>
                        <th className="text-left py-2 pr-4 font-medium">Stato</th>
                        <th className="text-left py-2 pr-4 font-medium">Ultimo invio</th>
                        <th className="text-left py-2 font-medium">Prossimo invio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ospiti.map(osp => {
                        const now = new Date();
                        const prossimo = osp.data_prossimo_invio ? new Date(osp.data_prossimo_invio) : null;
                        const scaduta = prossimo && prossimo <= now;
                        const vicina = prossimo && !scaduta && (prossimo - now) < 48 * 3600 * 1000;
                        return (
                          <tr key={osp.id} className={`border-b border-slate-50 hover:bg-slate-50 ${scaduta ? "bg-amber-50/50" : ""}`}>
                            <td className="py-2 pr-4 font-medium text-slate-800">{osp.nome || "—"}</td>
                            <td className="py-2 pr-4">
                              {osp.stato === "INVIATA"
                                ? <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Inviata</Badge>
                                : osp.stato
                                  ? <Badge className="bg-slate-100 text-slate-600 text-xs">{osp.stato}</Badge>
                                  : <span className="text-slate-400 text-xs">—</span>
                              }
                            </td>
                            <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(osp.data_ultimo_invio)}</td>
                            <td className="py-2">
                              {prossimo ? (
                                <span className={`text-xs font-medium whitespace-nowrap flex items-center gap-1 ${scaduta ? "text-red-600" : vicina ? "text-amber-600" : "text-slate-600"}`}>
                                  {scaduta && <AlertTriangle className="h-3 w-3" />}
                                  {vicina && <Clock className="h-3 w-3" />}
                                  {fmtDateTime(osp.data_prossimo_invio)}
                                </span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function AlloggiatiWebModule({ data, loading, onRefresh }) {
  const strutture = data?.alloggiati?.strutture || [];
  const total = data?.alloggiati?.total ?? 0;

  const totalInvii = strutture.reduce((s, x) => s + (x.stats?.total_invii || 0), 0);
  const totalScadute = strutture.reduce((s, x) => s + (x.stats?.schedine_scadute || 0), 0);
  const totalOspiti = strutture.reduce((s, x) => s + (x.stats?.ospiti_attivi || 0), 0);
  const struttureConErrori = strutture.filter(s => s.invii.some(i => !i.successo || i.num_schedine_errate > 0)).length;

  if (loading && strutture.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Hotel className="h-5 w-5 text-violet-500" />
            Alloggiati Web
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Monitoraggio schedine e invii per tutte le strutture configurate
          </p>
        </div>
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          disabled={loading}
          className="gap-1.5"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {/* Riepilogo KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-2xl font-bold text-violet-600">{total}</div>
          <div className="text-xs text-slate-500 mt-0.5">Strutture configurate</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-slate-700">{totalInvii}</div>
          <div className="text-xs text-slate-500 mt-0.5">Invii totali (storico)</div>
        </Card>
        <Card className="p-4">
          <div className={`text-2xl font-bold ${totalScadute > 0 ? "text-amber-600" : "text-green-600"}`}>{totalScadute}</div>
          <div className="text-xs text-slate-500 mt-0.5">Schedine da inviare ora</div>
        </Card>
        <Card className={`p-4 ${struttureConErrori > 0 ? "border-red-200 bg-red-50/30" : ""}`}>
          <div className={`text-2xl font-bold ${struttureConErrori > 0 ? "text-red-600" : "text-green-600"}`}>{struttureConErrori}</div>
          <div className="text-xs text-slate-500 mt-0.5">Strutture con errori</div>
        </Card>
      </div>

      {strutture.length === 0 ? (
        <Card className="p-8 text-center text-slate-400">
          <Hotel className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="font-medium">Nessuna struttura ha Alloggiati Web configurato</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {strutture.map(s => (
            <AlloggiatiStruttura key={s.config_id} struttura={s} />
          ))}
        </div>
      )}
    </div>
  );
}

const AI_MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "gpt-4o-mini (consigliato)" },
  { value: "gpt-4o", label: "gpt-4o" },
  { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
  { value: "gpt-4.1", label: "gpt-4.1" },
];

const AI_FEATURE_LABELS = {
  therapy_extraction: "Estrazione terapia",
  assistant_chat: "Assistente virtuale (chat)",
  assistant_voice: "Assistente virtuale (chat vocale)",
  generazione_turni: "Generazione turni assistita",
  risparmio_listino: "Listino Risparmio insieme",
  planimetria_stanze: "Planimetria camere (Vision)",
  identita_extraction: "Documento identità (anagrafica)",
  document_scan_cleanup: "Pulizia scansioni (Documenti fascicolo)",
  patologie_extraction: "Estrazione patologie (tab Sanitario)",
  spesa_receipt_extraction: "Scontrino spesa (Prima Nota)",
  ricette_split_pdf: "Suddivisione PDF multi-ospite (Ricette)",
  crm_whatsapp_disponibilita: "Disponibilità strutture da WhatsApp",
  whatsapp_ops_assistant: "WhatsApp operativo (assistente)",
};

const AI_USER_ROLE_LABELS = {
  super_admin: "Responsabile",
  amministrativo: "Amministrativo",
  coordinatore: "Coordinatore",
  operatore: "Operatore",
  infermiere: "Infermiere",
  medico: "Medico",
};

function AIConfigModule() {
  const [activeTab, setActiveTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [byUserLoading, setByUserLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasOpenaiKey, setHasOpenaiKey] = useState(false);
  const [assistantTools, setAssistantTools] = useState([]);
  const [realtimeOptions, setRealtimeOptions] = useState({ voices: [], efforts: [] });
  const [config, setConfig] = useState({
    enabled: false,
    model: "gpt-4o-mini",
    monthly_budget_usd: "",
    features: {
      therapy_extraction: { enabled: false, model: "", prompt: "" },
      assistant_chat: { enabled: false, model: "" },
      assistant_voice: voiceConfigFromApi(null),
      generazione_turni: { enabled: false, model: "" },
      risparmio_listino: { enabled: false, model: "" },
      planimetria_stanze: { enabled: false, model: "" },
      identita_extraction: { enabled: false, model: "" },
      document_scan_cleanup: { enabled: true, model: "" },
      patologie_extraction: { enabled: false, model: "", prompt: "" },
      spesa_receipt_extraction: { enabled: false, model: "" },
      ricette_split_pdf: { enabled: true, model: "" },
      crm_whatsapp_disponibilita: { enabled: true, model: "" },
      whatsapp_ops_assistant: { enabled: true, model: "" },
    },
  });
  const [usage, setUsage] = useState(null);
  const [usageMonth, setUsageMonth] = useState("");
  const [operationsLogs, setOperationsLogs] = useState({ items: [], total: 0, limit: 50, offset: 0 });
  const [operationsMonth, setOperationsMonth] = useState("");
  const [operationsFeature, setOperationsFeature] = useState("");
  const [expandedOpId, setExpandedOpId] = useState(null);
  const [usageByUser, setUsageByUser] = useState({ months: [], users: [], totals: null });
  const [byUserSearch, setByUserSearch] = useState("");
  const [expandedUserKey, setExpandedUserKey] = useState(null);
  const token = localStorage.getItem("super_admin_token");
  const API = process.env.REACT_APP_BACKEND_URL;

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const cfgRes = await fetch(`${API}/api/admin/ai-config`, { headers: authHeaders });
      if (cfgRes.ok) {
        const data = await cfgRes.json();
        const therapy = data.features?.therapy_extraction || {};
        const assistant = data.features?.assistant_chat || {};
        const genTurni = data.features?.generazione_turni || {};
        const risparmioListino = data.features?.risparmio_listino || {};
        const planimetriaStanze = data.features?.planimetria_stanze || {};
        const identitaExtraction = data.features?.identita_extraction || {};
        const documentScanCleanup = data.features?.document_scan_cleanup || {};
        const patologieExtraction = data.features?.patologie_extraction || {};
        const spesaReceiptExtraction = data.features?.spesa_receipt_extraction || {};
        const ricetteSplitPdf = data.features?.ricette_split_pdf || {};
        const crmWhatsappDisponibilita = data.features?.crm_whatsapp_disponibilita || {};
        const whatsappOpsAssistant = data.features?.whatsapp_ops_assistant || {};
        setHasOpenaiKey(!!data.has_openai_key);
        setAssistantTools(data.assistant_tools_catalog || []);
        setRealtimeOptions({
          voices: data.realtime_voices || [],
          efforts: data.realtime_reasoning_efforts || [],
        });
        setConfig({
          enabled: !!data.enabled,
          model: data.model || "gpt-4o-mini",
          monthly_budget_usd: data.monthly_budget_usd ?? "",
          features: {
            therapy_extraction: {
              enabled: !!therapy.enabled,
              model: therapy.model || "",
              prompt: therapy.prompt || data.default_prompt || "",
            },
            assistant_chat: {
              enabled: !!assistant.enabled,
              model: assistant.model || "",
            },
            assistant_voice: voiceConfigFromApi(data.features?.assistant_voice),
            generazione_turni: {
              enabled: !!genTurni.enabled,
              model: genTurni.model || "",
            },
            risparmio_listino: {
              enabled: !!risparmioListino.enabled,
              model: risparmioListino.model || "",
            },
            planimetria_stanze: {
              enabled: !!planimetriaStanze.enabled,
              model: planimetriaStanze.model || "",
            },
            identita_extraction: {
              enabled: !!identitaExtraction.enabled,
              model: identitaExtraction.model || "",
            },
            document_scan_cleanup: {
              enabled: documentScanCleanup.enabled !== false,
              model: documentScanCleanup.model || "",
            },
            patologie_extraction: {
              enabled: !!patologieExtraction.enabled,
              model: patologieExtraction.model || "",
              prompt: patologieExtraction.prompt || data.default_patologie_prompt || "",
            },
            spesa_receipt_extraction: {
              enabled: !!spesaReceiptExtraction.enabled,
              model: spesaReceiptExtraction.model || "",
            },
            ricette_split_pdf: {
              enabled: ricetteSplitPdf.enabled !== false,
              model: ricetteSplitPdf.model || "",
            },
            crm_whatsapp_disponibilita: {
              enabled: crmWhatsappDisponibilita.enabled !== false,
              model: crmWhatsappDisponibilita.model || "",
            },
            whatsapp_ops_assistant: {
              enabled: whatsappOpsAssistant.enabled !== false,
              model: whatsappOpsAssistant.model || "",
            },
          },
        });
      }
    } catch {
      toast.error("Errore caricamento configurazione AI");
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders]);

  const loadUsage = useCallback(async (month = "") => {
    setUsageLoading(true);
    try {
      const qs = month ? `?month=${encodeURIComponent(month)}` : "";
      const usageRes = await fetch(`${API}/api/admin/ai-usage${qs}`, { headers: authHeaders });
      if (usageRes.ok) setUsage(await usageRes.json());
    } catch {
      toast.error("Errore caricamento costi AI");
    } finally {
      setUsageLoading(false);
    }
  }, [API, authHeaders]);

  const loadOperations = useCallback(async (month = "", feature = "", offset = 0) => {
    setOperationsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", offset: String(offset) });
      if (month) params.set("month", month);
      if (feature) params.set("feature", feature);
      const res = await fetch(`${API}/api/admin/ai-operations?${params}`, { headers: authHeaders });
      if (res.ok) setOperationsLogs(await res.json());
    } catch {
      toast.error("Errore caricamento operazioni AI");
    } finally {
      setOperationsLoading(false);
    }
  }, [API, authHeaders]);

  const loadUsageByUser = useCallback(async () => {
    setByUserLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/ai-usage-by-user`, { headers: authHeaders });
      if (res.ok) setUsageByUser(await res.json());
    } catch {
      toast.error("Errore caricamento consumo per utente");
    } finally {
      setByUserLoading(false);
    }
  }, [API, authHeaders]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (activeTab === "costs") loadUsage(usageMonth);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === "usage") loadOperations(operationsMonth, operationsFeature, 0);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === "by-user") loadUsageByUser();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFeature = (featureId, patch) => {
    setConfig((c) => ({
      ...c,
      features: {
        ...c.features,
        [featureId]: { ...c.features[featureId], ...patch },
      },
    }));
  };

  const setToolEnabled = (toolId, enabled) => {
    setAssistantTools((prev) =>
      prev.map((t) => (t.id === toolId ? { ...t, enabled } : t))
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        model: config.model,
        monthly_budget_usd: config.monthly_budget_usd === "" ? null : parseFloat(config.monthly_budget_usd),
        features: {
          therapy_extraction: {
            enabled: config.features.therapy_extraction.enabled,
            model: config.features.therapy_extraction.model || null,
            prompt: config.features.therapy_extraction.prompt,
          },
          assistant_chat: {
            enabled: config.features.assistant_chat.enabled,
            model: config.features.assistant_chat.model || null,
            tools: Object.fromEntries(
              assistantTools.map((t) => [t.id, { enabled: t.enabled }])
            ),
          },
          assistant_voice: voiceConfigToPayload(config.features.assistant_voice),
          generazione_turni: {
            enabled: config.features.generazione_turni.enabled,
            model: config.features.generazione_turni.model || null,
          },
          risparmio_listino: {
            enabled: config.features.risparmio_listino.enabled,
            model: config.features.risparmio_listino.model || null,
          },
          planimetria_stanze: {
            enabled: config.features.planimetria_stanze.enabled,
            model: config.features.planimetria_stanze.model || null,
          },
          identita_extraction: {
            enabled: config.features.identita_extraction.enabled,
            model: config.features.identita_extraction.model || null,
          },
          document_scan_cleanup: {
            enabled: config.features.document_scan_cleanup?.enabled,
            model: config.features.document_scan_cleanup?.model || null,
          },
          patologie_extraction: {
            enabled: config.features.patologie_extraction.enabled,
            model: config.features.patologie_extraction.model || null,
            prompt: config.features.patologie_extraction.prompt || null,
          },
          spesa_receipt_extraction: {
            enabled: config.features.spesa_receipt_extraction.enabled,
            model: config.features.spesa_receipt_extraction.model || null,
          },
          ricette_split_pdf: {
            enabled: config.features.ricette_split_pdf.enabled,
            model: config.features.ricette_split_pdf.model || null,
          },
          crm_whatsapp_disponibilita: {
            enabled: config.features.crm_whatsapp_disponibilita.enabled,
            model: config.features.crm_whatsapp_disponibilita.model || null,
          },
          whatsapp_ops_assistant: {
            enabled: config.features.whatsapp_ops_assistant.enabled,
            model: config.features.whatsapp_ops_assistant.model || null,
          },
        },
      };
      const res = await fetch(`${API}/api/admin/ai-config`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success("Configurazione AI salvata");
      loadConfig();
    } catch (err) {
      toast.error(err.message || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const renderModelSelect = (value, onChange, disabled = false) => (
    <Select value={value || "__default__"} onValueChange={(v) => onChange(v === "__default__" ? "" : v)} disabled={disabled}>
      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__default__">Usa modello predefinito globale</SelectItem>
        {AI_MODEL_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const formatAiWhen = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return iso;
    }
  };

  const truncate = (text, max = 120) => {
    const s = (text || "").trim();
    if (s.length <= max) return s;
    return `${s.slice(0, max)}…`;
  };

  if (loading) {
    return (
      <div className="flex justify-center h-40 items-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const globalOff = !config.enabled;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Brain className="w-5 h-5 text-violet-600" />
          Intelligenza Artificiale
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Configurazione funzioni AI, monitoraggio costi, consumo per utente e storico operazioni.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 max-w-5xl bg-slate-100 h-auto">
          <TabsTrigger value="config" className="gap-1.5">
            <Settings className="w-4 h-4" />
            Configurazione
          </TabsTrigger>
          <TabsTrigger value="costs" className="gap-1.5">
            <DollarSign className="w-4 h-4" />
            Monitoraggio costi
          </TabsTrigger>
          <TabsTrigger value="by-user" className="gap-1.5">
            <Users className="w-4 h-4" />
            Consumo per utente
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-1.5">
            <Activity className="w-4 h-4" />
            Monitoraggio utilizzo
          </TabsTrigger>
          <TabsTrigger value="voice" className="gap-1.5" data-testid="ai-tab-voice">
            <Headphones className="w-4 h-4" />
            Chat vocale
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-3 mt-6">
          <AiConfigCollapsibleCard
            title="Impostazioni globali"
            description="Interruttore principale, modello predefinito e budget condivisi da tutte le funzioni AI."
            statusLabel={config.enabled ? "Master ON" : "Master OFF"}
            statusActive={config.enabled}
            contentClassName="space-y-5"
          >
            <form onSubmit={handleSave} className="space-y-5">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label>Abilita OpenAI (master)</Label>
                  <p className="text-xs text-slate-500">
                    Se disattivato, nessuna funzione AI è operativa.
                    {hasOpenaiKey ? " Chiave API rilevata sul server." : " OPENAI_API_KEY non configurata sul server."}
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(v) => setConfig((c) => ({ ...c, enabled: v }))}
                />
              </div>
              <div>
                <Label>Modello predefinito</Label>
                <Select value={config.model} onValueChange={(v) => setConfig((c) => ({ ...c, model: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AI_MODEL_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Usato dalle feature che non specificano un modello dedicato.</p>
              </div>
              <div>
                <Label>Budget mensile (USD, opzionale)</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1 max-w-xs"
                  value={config.monthly_budget_usd}
                  onChange={(e) => setConfig((c) => ({ ...c, monthly_budget_usd: e.target.value }))}
                />
                <p className="text-xs text-slate-500 mt-1">Limite cumulativo per estrazione terapia, chat e altre feature AI.</p>
              </div>
            </form>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Estrazione terapia (OCR)"
            icon={<Pill className="w-4 h-4 text-cyan-600" />}
            description="Estrazione automatica dei farmaci da documenti terapia (portale pre-ingresso familiare e Deblistering via OCR/email)."
            statusLabel={config.features.therapy_extraction.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.therapy_extraction.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita estrazione terapia</Label>
              <Switch
                checked={config.features.therapy_extraction.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("therapy_extraction", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.therapy_extraction.model,
                (v) => setFeature("therapy_extraction", { model: v }),
                globalOff,
              )}
            </div>
            <div>
              <Label>Prompt estrazione terapia</Label>
              <textarea
                className="mt-1 w-full min-h-[180px] rounded-md border border-slate-200 p-3 text-sm font-mono"
                value={config.features.therapy_extraction.prompt}
                disabled={globalOff}
                onChange={(e) => setFeature("therapy_extraction", { prompt: e.target.value })}
                placeholder="Usa {text} per il testo OCR"
              />
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Assistente virtuale (chat)"
            icon={<MessageCircle className="w-4 h-4 text-emerald-600" />}
            description="Chatbot integrato nell'app per rispondere alle domande degli utenti e, con conferma, eseguire alcune operazioni."
            statusLabel={config.features.assistant_chat.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.assistant_chat.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita assistente chat</Label>
              <Switch
                checked={config.features.assistant_chat.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("assistant_chat", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.assistant_chat.model,
                (v) => setFeature("assistant_chat", { model: v }),
                globalOff,
              )}
            </div>

            <div className="space-y-2">
              <div>
                <Label>Tool disponibili per il chatbot</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ogni tool può essere attivato o disattivato. I tool disabilitati non vengono esposti al modello.
                </p>
              </div>
              {assistantTools.length === 0 ? (
                <p className="text-sm text-slate-500">Nessun tool configurato.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {assistantTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="flex flex-col justify-between gap-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50 min-h-[140px]"
                    >
                      <div className="min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="text-sm font-medium text-slate-800 leading-tight">{tool.label}</span>
                          <Switch
                            checked={!!tool.enabled}
                            disabled={globalOff || !config.features.assistant_chat.enabled}
                            onCheckedChange={(v) => setToolEnabled(tool.id, v)}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                          <Badge
                            variant="outline"
                            className={
                              tool.type === "read"
                                ? "bg-blue-50 text-blue-700 border-blue-200 text-[10px]"
                                : tool.type === "control"
                                  ? "bg-violet-50 text-violet-700 border-violet-200 text-[10px]"
                                  : "bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
                            }
                          >
                            {tool.type === "read" ? "Solo lettura" : tool.type === "control" ? "Gestione conferme" : "Azione"}
                          </Badge>
                          {tool.requires_confirmation && (
                            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                              Conferma
                            </Badge>
                          )}
                          {Array.isArray(tool.channels) && tool.channels.length === 1 && (
                            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-[10px]">
                              {tool.channels[0] === "voice" ? "Solo chat vocale" : "Solo chat testuale"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 leading-snug line-clamp-3">{tool.description}</p>
                        {tool.permission && (
                          <p className="text-[10px] text-slate-400 mt-1.5">
                            Permesso: <code className="bg-slate-100 px-1 rounded">{tool.permission}</code>
                          </p>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 font-mono truncate" title={tool.id}>{tool.id}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AiConfigCollapsibleCard>

          <AssistantVoiceConfigCard
            value={config.features.assistant_voice}
            onChange={(v) => setConfig((c) => ({ ...c, features: { ...c.features, assistant_voice: v } }))}
            disabled={globalOff}
            assistantEnabled={config.features.assistant_chat.enabled}
            voices={realtimeOptions.voices}
            efforts={realtimeOptions.efforts}
          />

          <AiConfigCollapsibleCard
            title="Generazione turni assistita (OSS)"
            icon={<CalendarDays className="w-4 h-4 text-sky-600" />}
            description="Permette di inserire istruzioni in linguaggio naturale nel dialog di generazione automatica turni OSS. L'AI genera direttamente il calendario mensile integrando le regole impostate con il testo libero."
            statusLabel={config.features.generazione_turni.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.generazione_turni.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita generazione turni assistita</Label>
              <Switch
                checked={config.features.generazione_turni.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("generazione_turni", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.generazione_turni.model,
                (v) => setFeature("generazione_turni", { model: v }),
                globalOff,
              )}
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Listino Risparmio insieme"
            icon={<PiggyBank className="w-4 h-4 text-blue-600" />}
            description="Fallback OpenAI quando il parser automatico non riconosce il formato Excel/PDF (es. colonne Categoria / Prodotto / Confezione / Prezzo su secondo foglio). I costi compaiono in Monitoraggio costi con feature «Listino Risparmio insieme»."
            statusLabel={config.features.risparmio_listino.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.risparmio_listino.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita estrazione listino con AI</Label>
              <Switch
                checked={config.features.risparmio_listino.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("risparmio_listino", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.risparmio_listino.model,
                (v) => setFeature("risparmio_listino", { model: v }),
                globalOff,
              )}
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Planimetria camere (Vision)"
            icon={<Map className="w-4 h-4 text-emerald-600" />}
            description="Suggerimento automatico delle zone stanza sulla planimetria in Camere e posti letto (modalità Planimetria). Usa OpenAI Vision; i costi compaiono in Monitoraggio costi/utilizzo."
            statusLabel={config.features.planimetria_stanze.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.planimetria_stanze.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita suggerimento stanze da planimetria</Label>
              <Switch
                checked={config.features.planimetria_stanze.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("planimetria_stanze", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.planimetria_stanze.model,
                (v) => setFeature("planimetria_stanze", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">Consigliato gpt-4o per planimetrie dettagliate.</p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Documento identità (anagrafica)"
            icon={<IdCard className="w-4 h-4 text-violet-600" />}
            description="Estrazione automatica dei dati anagrafici da carta d'identità, CIE, passaporto o patente nel fascicolo assistenziale (upload fronte/retro + Vision/OCR)."
            statusLabel={config.features.identita_extraction.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.identita_extraction.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita compilazione anagrafica da documento identità</Label>
              <Switch
                checked={config.features.identita_extraction.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("identita_extraction", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.identita_extraction.model,
                (v) => setFeature("identita_extraction", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">Consigliato gpt-4o per lettura documenti con foto.</p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Pulizia scansioni (Documenti fascicolo)"
            icon={<Sparkles className="w-4 h-4 text-violet-600" />}
            description="Nel fascicolo, tab Documenti, ritaglia lo sfondo delle foto (tavolo, pavimento), raddrizza il foglio e migliora contrasto. Il ritaglio classico funziona sempre; l'AI Vision interviene solo se i bordi del documento non si distinguono da soli (es. tessera o CI fotografata in diagonale)."
            statusLabel={config.features.document_scan_cleanup?.enabled ? "Attiva" : "Spenta"}
            statusActive={!!config.features.document_scan_cleanup?.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita AI per i bordi delle scansioni difficili</Label>
              <Switch
                checked={!!config.features.document_scan_cleanup?.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("document_scan_cleanup", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.document_scan_cleanup?.model,
                (v) => setFeature("document_scan_cleanup", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">
                Consigliato gpt-4o-mini. La Vision viene chiamata solo se il ritaglio automatico non basta.
              </p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Estrazione patologie (tab Sanitario)"
            icon={<Heart className="w-4 h-4 text-rose-600" />}
            description="Legge i documenti clinici già caricati nel fascicolo (lettere di dimissione, valutazioni geriatriche, piani terapeutici, richieste RSA), analizza fino alle prime 6 pagine di ogni PDF e compila diagnosi, patologie croniche e allergie nel tab Sanitario. Usa i dati già in cartella come contesto."
            statusLabel={config.features.patologie_extraction.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.patologie_extraction.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita estrazione patologie da documenti</Label>
              <Switch
                checked={config.features.patologie_extraction.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("patologie_extraction", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.patologie_extraction.model,
                (v) => setFeature("patologie_extraction", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">
                Consigliato gpt-4.1 per documenti clinici. Con più pagine PDF il costo sale: ~$0.03–0.12 per analisi.
              </p>
            </div>
            <div>
              <Label>Prompt estrazione patologie</Label>
              <textarea
                className="mt-1 w-full min-h-[180px] rounded-md border border-slate-200 p-3 text-sm font-mono"
                value={config.features.patologie_extraction.prompt || ""}
                disabled={globalOff}
                onChange={(e) => setFeature("patologie_extraction", { prompt: e.target.value })}
                placeholder="Usa {contesto_fascicolo} per diagnosi, patologie, allergie e terapie già in cartella"
              />
              <p className="text-xs text-slate-500 mt-1">
                Placeholder: {"{contesto_fascicolo}"} (blocco completo), oppure {"{diagnosi_principale}"}, {"{diagnosi_secondarie}"}, {"{patologie_croniche}"}, {"{allergie}"}, {"{terapie_attive}"}.
              </p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Scontrino spesa (Prima Nota)"
            icon={<Camera className="w-4 h-4 text-rose-600" />}
            description="In Prima Nota (struttura e area OSS) consente di fotografare o caricare uno scontrino/ricevuta e compilare automaticamente tipo, importo, data e descrizione della spesa extra tramite Vision/OCR."
            statusLabel={config.features.spesa_receipt_extraction?.enabled ? "Attiva" : "Spenta"}
            statusActive={!!config.features.spesa_receipt_extraction?.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita riconoscimento spesa da scontrino</Label>
              <Switch
                checked={config.features.spesa_receipt_extraction?.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("spesa_receipt_extraction", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.spesa_receipt_extraction?.model,
                (v) => setFeature("spesa_receipt_extraction", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">
                Consigliato gpt-4o per foto di scontrini. L&apos;operatore deve sempre verificare i dati prima di salvare.
              </p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Suddivisione PDF multi-ospite (Ricette)"
            icon={<FileText className="w-4 h-4 text-violet-600" />}
            description="Quando un PDF allegato a una Richiesta Farmacia contiene pagine per ospiti diversi, questa funzione lo scorpora automaticamente creando richieste separate. Usa Vision AI solo per le pagine ambigue (con nomi di più ospiti); il rilevamento base è gratuito via OCR."
            statusLabel={config.features.ricette_split_pdf.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.ricette_split_pdf.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita suddivisione PDF multi-ospite</Label>
              <Switch
                checked={config.features.ricette_split_pdf.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("ricette_split_pdf", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello per disambiguazione pagine ambigue (opzionale)</Label>
              {renderModelSelect(
                config.features.ricette_split_pdf.model,
                (v) => setFeature("ricette_split_pdf", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">
                La Vision AI è chiamata solo sulle pagine con nomi di più ospiti. Consigliato gpt-4o-mini per il costo ridotto.
              </p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="Disponibilità strutture da WhatsApp"
            icon={<MessageCircle className="w-4 h-4 text-emerald-600" />}
            description="Legge le risposte WhatsApp delle strutture e propone posti disponibili, nessun posto oppure una risposta da chiarire. Il dato non aggiorna mai la struttura senza conferma dell'operatore commerciale."
            statusLabel={config.features.crm_whatsapp_disponibilita.enabled ? "Attiva" : "Spenta"}
            statusActive={config.features.crm_whatsapp_disponibilita.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita lettura automatica delle risposte</Label>
              <Switch
                checked={config.features.crm_whatsapp_disponibilita.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("crm_whatsapp_disponibilita", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello (opzionale)</Label>
              {renderModelSelect(
                config.features.crm_whatsapp_disponibilita.model,
                (v) => setFeature("crm_whatsapp_disponibilita", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">Consigliato gpt-4o-mini: il testo originale resta sempre visibile nella coda WhatsApp.</p>
            </div>
          </AiConfigCollapsibleCard>

          <AiConfigCollapsibleCard
            title="WhatsApp operativo (assistente)"
            icon={<MessageCircle className="w-4 h-4 text-sky-600" />}
            description="Assistente Affidya via WhatsApp per utenti abilitati e operatori CRM: testo, vocali (Whisper) e file. Indipendente dalla chat web; uso e costi compaiono nelle tab Costi e Operazioni."
            statusLabel={config.features.whatsapp_ops_assistant?.enabled ? "Attiva" : "Spenta"}
            statusActive={!!config.features.whatsapp_ops_assistant?.enabled && !globalOff}
            className={globalOff ? "opacity-60" : ""}
            contentClassName="space-y-4"
          >
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label>Abilita assistente WhatsApp operativo</Label>
              <Switch
                checked={!!config.features.whatsapp_ops_assistant?.enabled}
                disabled={globalOff}
                onCheckedChange={(v) => setFeature("whatsapp_ops_assistant", { enabled: v })}
              />
            </div>
            <div>
              <Label>Modello</Label>
              {renderModelSelect(
                config.features.whatsapp_ops_assistant?.model || "",
                (v) => setFeature("whatsapp_ops_assistant", { model: v }),
                globalOff,
              )}
              <p className="text-xs text-slate-500 mt-1">
                Usato per le risposte operative su WhatsApp. I vocali usano Whisper (costo a minuto, separato).
                Consigliato gpt-4o-mini se vuoi contenere i costi; gpt-4o per richieste più complesse.
              </p>
            </div>
          </AiConfigCollapsibleCard>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva configurazione AI
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="voice" className="mt-6">
          <AssistantVoiceAdminTab />
        </TabsContent>

        <TabsContent value="costs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-violet-600" />
                Monitoraggio costi
              </CardTitle>
              <p className="text-sm text-slate-500">
                Consumo token e costo stimato per tutte le funzioni AI (mese corrente se non filtrato).
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-6">
                <div>
                  <Label>Mese (YYYY-MM)</Label>
                  <Input
                    type="month"
                    className="mt-1 w-44"
                    value={usageMonth}
                    onChange={(e) => setUsageMonth(e.target.value)}
                  />
                </div>
                <Button variant="outline" onClick={() => loadUsage(usageMonth)} disabled={usageLoading}>
                  {usageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Aggiorna
                </Button>
              </div>

              {usageLoading && !usage ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : usage ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">{usage.total_calls}</p>
                      <p className="text-xs text-slate-500">Chiamate</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">{usage.total_tokens?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Token</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">${usage.total_cost_usd?.toFixed(4)}</p>
                      <p className="text-xs text-slate-500">Costo stimato</p>
                    </div>
                  </div>
                  {usage.by_feature?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Per funzione</p>
                      <ul className="text-sm space-y-1">
                        {usage.by_feature.map((row) => (
                          <li key={row.feature} className="flex justify-between text-slate-600">
                            <span>{AI_FEATURE_LABELS[row.feature] || row.feature}</span>
                            <span>{row.calls} chiamate — ${row.cost_usd}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {usage.by_model?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Per modello</p>
                      <ul className="text-sm space-y-1">
                        {usage.by_model.map((row) => (
                          <li key={row.model} className="flex justify-between text-slate-600">
                            <span>{row.model}</span>
                            <span>{row.calls} chiamate — ${row.cost_usd}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">Nessun dato di consumo disponibile.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-user" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-600" />
                Consumo OpenAI per utente
              </CardTitle>
              <p className="text-sm text-slate-500">
                Costo stimato e token per ogni utente, con dettaglio mese per mese. Le operazioni senza utente
                (job automatici, email inbound, ecc.) compaiono come «Non attribuito».
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-6">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    className="pl-9"
                    placeholder="Cerca utente, email o struttura…"
                    value={byUserSearch}
                    onChange={(e) => setByUserSearch(e.target.value)}
                  />
                </div>
                <Button variant="outline" onClick={() => loadUsageByUser()} disabled={byUserLoading}>
                  {byUserLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Aggiorna
                </Button>
              </div>

              {byUserLoading && !usageByUser.users?.length ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : !usageByUser.users?.length ? (
                <p className="text-sm text-slate-500 py-8 text-center">Nessun consumo OpenAI registrato.</p>
              ) : (() => {
                const q = byUserSearch.trim().toLowerCase();
                const filtered = q
                  ? usageByUser.users.filter((u) => {
                      const hay = [
                        u.user_name,
                        u.user_email,
                        u.organization_name,
                        u.user_id,
                        u.user_role,
                      ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();
                      return hay.includes(q);
                    })
                  : usageByUser.users;
                const months = usageByUser.months || [];
                const totals = usageByUser.totals || {};
                const formatMonthLabel = (m) => {
                  if (!m || m.length < 7) return m;
                  const [y, mo] = m.split("-");
                  const labels = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
                  const idx = parseInt(mo, 10) - 1;
                  return `${labels[idx] || mo} ${y?.slice(2) || ""}`;
                };
                return (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                      <div className="rounded-lg bg-slate-50 p-4 text-center">
                        <p className="text-2xl font-bold">{filtered.length}</p>
                        <p className="text-xs text-slate-500">Utenti{q ? " (filtrati)" : ""}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-4 text-center">
                        <p className="text-2xl font-bold">{(totals.calls ?? 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Chiamate totali</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-4 text-center">
                        <p className="text-2xl font-bold">{(totals.tokens ?? 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Token totali</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-4 text-center">
                        <p className="text-2xl font-bold">${(totals.cost_usd ?? 0).toFixed(4)}</p>
                        <p className="text-xs text-slate-500">Costo stimato totale</p>
                      </div>
                    </div>

                    {filtered.length === 0 ? (
                      <p className="text-sm text-slate-500 py-6 text-center">Nessun utente corrisponde alla ricerca.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-sm min-w-[720px]">
                          <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase">
                            <tr>
                              <th className="p-3 font-medium sticky left-0 bg-slate-50 z-10">Utente</th>
                              <th className="p-3 font-medium">Struttura</th>
                              <th className="p-3 font-medium text-right">Chiamate</th>
                              <th className="p-3 font-medium text-right">Token</th>
                              <th className="p-3 font-medium text-right">Totale</th>
                              {months.map((m) => (
                                <th key={m} className="p-3 font-medium text-right whitespace-nowrap">
                                  {formatMonthLabel(m)}
                                </th>
                              ))}
                              <th className="p-3 font-medium w-10" />
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((u) => {
                              const isOpen = expandedUserKey === u.user_key;
                              const monthCost = Object.fromEntries(
                                (u.by_month || []).map((m) => [m.month, m])
                              );
                              return (
                                <Fragment key={u.user_key}>
                                  <tr className="border-t border-slate-100 hover:bg-slate-50/50">
                                    <td className="p-3 sticky left-0 bg-white z-10">
                                      <p className="font-medium text-slate-800">
                                        {u.user_name || "Utente sconosciuto"}
                                      </p>
                                      {u.user_email && (
                                        <p className="text-[10px] text-slate-500">{u.user_email}</p>
                                      )}
                                      {u.user_role && (
                                        <p className="text-[10px] text-slate-400">
                                          {AI_USER_ROLE_LABELS[u.user_role] || u.user_role}
                                        </p>
                                      )}
                                    </td>
                                    <td className="p-3 text-xs text-slate-600 max-w-[160px]">
                                      <p className="truncate" title={u.organization_name || ""}>
                                        {u.organization_name || "—"}
                                      </p>
                                    </td>
                                    <td className="p-3 text-right text-xs tabular-nums">
                                      {(u.total_calls ?? 0).toLocaleString()}
                                    </td>
                                    <td className="p-3 text-right text-xs tabular-nums">
                                      {(u.total_tokens ?? 0).toLocaleString()}
                                    </td>
                                    <td className="p-3 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                                      ${(u.total_cost_usd ?? 0).toFixed(4)}
                                    </td>
                                    {months.map((m) => {
                                      const cell = monthCost[m];
                                      const cost = cell?.cost_usd ?? 0;
                                      return (
                                        <td
                                          key={`${u.user_key}-${m}`}
                                          className="p-3 text-right text-xs tabular-nums whitespace-nowrap"
                                          title={
                                            cell
                                              ? `${cell.calls} chiamate · ${cell.tokens?.toLocaleString()} token`
                                              : undefined
                                          }
                                        >
                                          {cost > 0 ? (
                                            <span className="text-slate-800">${cost.toFixed(4)}</span>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="p-3">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setExpandedUserKey(isOpen ? null : u.user_key)}
                                        title="Dettaglio per funzione"
                                      >
                                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                      </Button>
                                    </td>
                                  </tr>
                                  {isOpen && (
                                    <tr className="border-t border-slate-100 bg-slate-50/80">
                                      <td colSpan={6 + months.length} className="p-4">
                                        <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">
                                          Dettaglio per funzione
                                        </p>
                                        {(u.by_feature || []).length === 0 ? (
                                          <p className="text-xs text-slate-500">Nessun dettaglio</p>
                                        ) : (
                                          <ul className="text-xs space-y-1 max-w-xl">
                                            {u.by_feature.map((f) => (
                                              <li key={`${u.user_key}-${f.feature}`} className="flex justify-between gap-4 text-slate-600">
                                                <span>{AI_FEATURE_LABELS[f.feature] || f.feature}</span>
                                                <span className="tabular-nums whitespace-nowrap">
                                                  {f.calls} chiamate · {f.tokens?.toLocaleString()} token · ${f.cost_usd?.toFixed(4)}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        {(u.by_month || []).some((m) => m.calls > 0) && (
                                          <div className="mt-4">
                                            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">
                                              Dettaglio mensile
                                            </p>
                                            <ul className="text-xs space-y-1 max-w-xl">
                                              {(u.by_month || [])
                                                .filter((m) => m.calls > 0)
                                                .map((m) => (
                                                  <li key={`${u.user_key}-md-${m.month}`} className="flex justify-between gap-4 text-slate-600">
                                                    <span>{formatMonthLabel(m.month)}</span>
                                                    <span className="tabular-nums whitespace-nowrap">
                                                      {m.calls} chiamate · {m.tokens?.toLocaleString()} token · ${m.cost_usd?.toFixed(4)}
                                                    </span>
                                                  </li>
                                                ))}
                                            </ul>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400 mt-3">
                      Costi stimati in USD in base al listino modelli configurato (non fattura OpenAI).
                    </p>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-600" />
                Monitoraggio utilizzo — Operazioni AI
              </CardTitle>
              <p className="text-sm text-slate-500">
                Registro di tutte le chiamate OpenAI (chat, estrazione terapia OCR, ecc.) con tipo operazione, token e costo stimato.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <Label>Mese (YYYY-MM)</Label>
                  <Input
                    type="month"
                    className="mt-1 w-44"
                    value={operationsMonth}
                    onChange={(e) => setOperationsMonth(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Tipo operazione</Label>
                  <Select
                    value={operationsFeature || "__all__"}
                    onValueChange={(v) => setOperationsFeature(v === "__all__" ? "" : v)}
                  >
                    <SelectTrigger className="mt-1 w-52">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tutte le operazioni</SelectItem>
                      <SelectItem value="assistant_chat">Assistente virtuale (chat)</SelectItem>
                      <SelectItem value="assistant_voice">Assistente virtuale (chat vocale)</SelectItem>
                      <SelectItem value="therapy_extraction">Estrazione terapia (OCR)</SelectItem>
                      <SelectItem value="generazione_turni">Generazione turni assistita</SelectItem>
                      <SelectItem value="risparmio_listino">Listino Risparmio insieme</SelectItem>
                      <SelectItem value="planimetria_stanze">Planimetria camere (Vision)</SelectItem>
                      <SelectItem value="identita_extraction">Documento identità (anagrafica)</SelectItem>
                      <SelectItem value="document_scan_cleanup">Pulizia scansioni (Documenti fascicolo)</SelectItem>
                      <SelectItem value="patologie_extraction">Estrazione patologie (tab Sanitario)</SelectItem>
                      <SelectItem value="spesa_receipt_extraction">Scontrino spesa (Prima Nota)</SelectItem>
                      <SelectItem value="ricette_split_pdf">Suddivisione PDF multi-ospite (Ricette)</SelectItem>
                      <SelectItem value="crm_whatsapp_disponibilita">Disponibilità strutture da WhatsApp</SelectItem>
                      <SelectItem value="whatsapp_ops_assistant">WhatsApp operativo (assistente)</SelectItem>
                      <SelectItem value="ricette_ia">Ricette IA (analisi farmaci)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={() => loadOperations(operationsMonth, operationsFeature, 0)}
                  disabled={operationsLoading}
                >
                  {operationsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Aggiorna
                </Button>
              </div>

              {operationsLoading && operationsLogs.items.length === 0 ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : operationsLogs.items.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">
                  Nessuna operazione AI registrata
                  {operationsMonth || operationsFeature ? " con i filtri selezionati" : ""}.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-3">
                    {operationsLogs.total} operazioni totali
                    {operationsMonth ? ` (mese: ${operationsMonth})` : ""}
                    {operationsFeature ? ` — ${AI_FEATURE_LABELS[operationsFeature] || operationsFeature}` : ""}
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-3 font-medium">Data</th>
                          <th className="p-3 font-medium">Operazione</th>
                          <th className="p-3 font-medium">Modello</th>
                          <th className="p-3 font-medium">Token</th>
                          <th className="p-3 font-medium">Costo</th>
                          <th className="p-3 font-medium">Utente</th>
                          <th className="p-3 font-medium">Ruolo</th>
                          <th className="p-3 font-medium">Struttura</th>
                          <th className="p-3 font-medium">Domanda</th>
                          <th className="p-3 font-medium">Risposta</th>
                          <th className="p-3 font-medium">Esito</th>
                          <th className="p-3 font-medium w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {operationsLogs.items.map((op) => {
                          const isOpen = expandedOpId === op.id;
                          const chat = op.chat_detail;
                          const isChat =
                            op.feature === "assistant_chat" ||
                            op.feature === "whatsapp_ops_assistant";
                          const hasExpandable = isChat && chat;
                          return (
                            <Fragment key={op.id}>
                              <tr className="border-t border-slate-100 align-top hover:bg-slate-50/50">
                                <td className="p-3 text-slate-600 whitespace-nowrap text-xs">
                                  {formatAiWhen(op.created_at)}
                                </td>
                                <td className="p-3 text-slate-800 text-xs font-medium">
                                  {AI_FEATURE_LABELS[op.feature] || op.feature || "—"}
                                </td>
                                <td className="p-3 text-slate-600 text-xs font-mono">{op.model || "—"}</td>
                                <td className="p-3 text-slate-600 text-xs tabular-nums whitespace-nowrap">
                                  {(op.total_tokens ?? 0).toLocaleString()}
                                  <span className="text-slate-400 block text-[10px]">
                                    in {(op.prompt_tokens ?? 0).toLocaleString()} / out {(op.completion_tokens ?? 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="p-3 text-slate-800 text-xs font-semibold tabular-nums whitespace-nowrap">
                                  ${(op.estimated_cost_usd ?? 0).toFixed(6)}
                                </td>
                                <td className="p-3 text-slate-600 text-xs">
                                  {isChat && (op.user_name || chat?.user_name) ? (
                                    <>
                                      <p className="font-medium text-slate-800">{op.user_name || chat?.user_name}</p>
                                      {(chat?.user_email) && (
                                        <p className="text-[10px] text-slate-500">{chat.user_email}</p>
                                      )}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="p-3 text-slate-600 text-xs">
                                  {isChat && chat?.user_role
                                    ? (AI_USER_ROLE_LABELS[chat.user_role] || chat.user_role)
                                    : "—"}
                                </td>
                                <td className="p-3 text-slate-600 text-xs max-w-[140px]">
                                  <p className="font-medium truncate" title={op.organization_name}>
                                    {op.organization_name || "—"}
                                  </p>
                                  {op.organizationId && (
                                    <p className="text-[10px] text-slate-400 font-mono truncate" title={op.organizationId}>
                                      {op.organizationId}
                                    </p>
                                  )}
                                  {!isChat && op.fascicolo_id && (
                                    <p className="text-[10px] text-slate-400 font-mono truncate mt-1" title={op.fascicolo_id}>
                                      Fascicolo: {op.fascicolo_id}
                                    </p>
                                  )}
                                </td>
                                <td className="p-3 text-slate-700 max-w-xs">
                                  {isChat && chat?.user_message ? (
                                    isOpen ? (
                                      <p className="text-xs whitespace-pre-wrap">{chat.user_message}</p>
                                    ) : (
                                      <p className="text-xs">{truncate(chat.user_message)}</p>
                                    )
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>
                                <td className="p-3 text-slate-700 max-w-xs">
                                  {isChat && chat?.assistant_message ? (
                                    <>
                                      {isOpen ? (
                                        <p className="text-xs whitespace-pre-wrap">{chat.assistant_message}</p>
                                      ) : (
                                        <p className="text-xs">{truncate(chat.assistant_message)}</p>
                                      )}
                                      {chat.tools_used?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {chat.tools_used.map((t, i) => (
                                            <Badge key={`${op.id}-tool-${i}`} variant="outline" className="text-[10px] font-mono">
                                              {t.tool}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  {op.success === false ? (
                                    <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                                      Errore
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                      OK
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {(hasExpandable || op.error) ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => setExpandedOpId(isOpen ? null : op.id)}
                                    >
                                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </Button>
                                  ) : null}
                                </td>
                              </tr>
                              {isOpen && op.error ? (
                                <tr className="border-t border-slate-100 bg-slate-50/80">
                                  <td colSpan={12} className="p-4 text-xs text-slate-700">
                                    <p className="font-semibold text-rose-600 uppercase text-[10px] mb-1">Errore</p>
                                    <p className="whitespace-pre-wrap text-rose-800">{op.error}</p>
                                  </td>
                                </tr>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {operationsLogs.total > operationsLogs.limit && (
                    <div className="flex justify-between items-center mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={operationsLoading || operationsLogs.offset <= 0}
                        onClick={() =>
                          loadOperations(
                            operationsMonth,
                            operationsFeature,
                            Math.max(0, operationsLogs.offset - operationsLogs.limit)
                          )
                        }
                      >
                        Precedenti
                      </Button>
                      <span className="text-xs text-slate-500">
                        {operationsLogs.offset + 1}–
                        {Math.min(operationsLogs.offset + operationsLogs.limit, operationsLogs.total)} di {operationsLogs.total}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={operationsLoading || operationsLogs.offset + operationsLogs.limit >= operationsLogs.total}
                        onClick={() =>
                          loadOperations(
                            operationsMonth,
                            operationsFeature,
                            operationsLogs.offset + operationsLogs.limit
                          )
                        }
                      >
                        Successivi
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
